import type { PDFInstance } from "types/viewer.type"
import { smartNavDebug } from "components/ui/customWebviewer/smartNavDebug"
import type {
	TAnnotationCategory,
	TAnnotationFillState,
	TAnnotationMetadata,
	TAnnotationStatus,
	TAnnotationStyleConfig,
	TAnnotationType,
} from "types/annotations.type"
import type {
	TCoordinates,
	TEcall,
	TEref,
	TErefStatus,
	TFurniture,
	TFurnitureStatus,
	THighlight,
	TNote,
	TPageElevationData,
	TPageInfo,
} from "types/elevationReferences.type"
import type { Core } from "@pdftron/webviewer"
import {
	FURNITURE_LABEL_STYLE,
	computeFurnitureLabelPlacements,
	resolveLabelFontSize,
	type TFurnitureLabelInput,
} from "helpers/furnitureLabelPlacement.helper"
import { buildFurnitureLabelLines, getFurnitureLabelSide } from "helpers/furnitureLabelText.helper"
import {
	BOTTOM_DOCK_ATTRIBUTE,
	FRAME_MAX_ZOOM,
	isAlreadyFramed,
	resolveFitZoom,
	resolveFramePadding,
	resolveFrameScroll,
	unionBoxes,
} from "helpers/annotationFraming.helper"
import type { TFrameBox, TFrameMode, TVisibleRect } from "helpers/annotationFraming.helper"

type TPageRotationTransform = {
	rotation: number
	origWidth: number
	origHeight: number
}

// REQ-400 (#1381 review): backend `line_style` → Apryse's `Style`/`Dashes` border-dash properties.
// Apryse's own draw code checks `this.Style === "dash" && this.Dashes` to render a dashed border —
// verified empirically against a live RectangleAnnotation AND EllipseAnnotation, both honor it
// despite `RectangleAnnotation.setBorderStyle()`'s doc comment claiming only "solid"/"cloudy" are
// valid. `Dashes` wants a COMMA-separated string ("8,4"), unlike the SVG `stroke-dasharray`
// SPACE-separated form (`figureHighlight.constants.ts`'s `LINE_STYLE_OPTIONS.dasharray`, used for the
// picker's own SVG previews) — duplicated here rather than imported because `services/` never
// imports from `components/` in this codebase; keep the two catalogues in sync if a line style is
// ever added/removed. "solid" (and any unrecognised value) intentionally has no entry, leaving
// `Style`/`Dashes` unset — Apryse's default border, matching a highlight built before line styles
// existed.
const HIGHLIGHT_LINE_STYLE_DASHES: Record<string, string> = {
	dashed_long: "8,4",
	dashed_medium: "5,3",
	dashed_short: "3,2",
	dotted_large: "0.5,4",
	dotted_medium: "0.5,3",
	dotted_small: "0.5,2",
}

// REQ-400 (#1386 review, flagged major — "users use it a lot in PDF files they provide"): the one
// line style that is NOT a dash pattern. A cloudy border is a PDF border EFFECT, so it goes through
// Apryse's own `setBorderStyle("cloudy")` + `setCloudIntensity(...)` pair rather than Style/Dashes.
// Kept beside HIGHLIGHT_LINE_STYLE_DASHES for the same reason that map lives here: `services/` never
// imports from `components/`, so this string is duplicated from `figureHighlight.constants.ts`'s
// LINE_STYLE_OPTIONS — keep the two catalogues in sync.
//
// Both methods are FEATURE-DETECTED at the call site and wrapped in a try/catch, and that is not
// defensive boilerplate: on this WebViewer 11.9.1 build several methods Apryse's own .d.ts declares
// are simply absent at runtime (`tool.on` — see useFigureHighlightCreate's comment — and
// `getEditBoxManager().on`, which crashed the whole editor to a white page). Measured live here:
//
//   - RectangleAnnotation HAS both, and the border really draws as a scalloped cloud (pixel-tested:
//     53 direction reversals along the top edge, ~13px period, vs 0 for a solid control).
//   - EllipseAnnotation has NEITHER, despite the typings declaring both — and there is no other way
//     in. Forcing `Style = "cloudy"` / `Intensity = 2` directly, and even copying the rectangle's own
//     `setBorderStyle` / `Intensity` accessor onto `EllipseAnnotation.prototype`, all succeed and
//     read back correctly while changing the render not one pixel (1 reversal = a plain arc, before
//     and after). Apryse's ellipse draw code simply never consults `Style === "cloudy"`; the cloud
//     geometry is rectangle-specific. A cloudy ellipse would mean drawing the scallops ourselves.
//
// That asymmetry is why `supportsCloudyBorder` exists and why the UI hides the option per shape
// rather than offering a style that saves fine and then silently draws solid.
const HIGHLIGHT_CLOUDY_LINE_STYLE = "cloudy"
// Apryse's own default cloud intensity is 1 for a freshly created cloudy annotation; 2 is the
// heavier scallop the reviewer's screenshot shows, and matches what the picker previews.
const HIGHLIGHT_CLOUD_INTENSITY = 2

// One furniture info label (REQ-401), fully resolved and ready to be drawn: text, patch color and
// geometry already in Apryse annotation space. `baseRect` is the owning highlight's rect in the
// same space — the difference between the two is cached so the label can follow a live drag.
type TResolvedFurnitureLabel = {
	lines: string[]
	/** Shrink-to-fit size for THIS label — see `resolveLabelFontSize`. */
	fontSize: number
	fillColor: [number, number, number, number]
	rotation: number
	pageNumber: number
	rect: TCoordinates
	baseRect: TCoordinates
}

const toCssColor = ([r, g, b, a]: [number, number, number, number]): string => `rgba(${r}, ${g}, ${b}, ${a})`

type TFurnitureLabelConstructor = new (init: {
	PageNumber: number
	X: number
	Y: number
	Width: number
	Height: number
}) => Core.Annotations.RectangleAnnotation

const ANNOTATION_STYLES: TAnnotationStyleConfig = {
	connected: {
		fillColor: [255, 234, 0, 0.3],
	},
	unconnected: {
		fillColor: [189, 189, 189, 0.3],
	},
	reviewed: {
		fillColor: [0, 71, 255, 0.3],
	},
	in_quotation: {
		fillColor: [112, 199, 105, 0.4],
	},
}

// Which viewport a geometry lookup / selection refers to when split screen is open (REQ-399).
// "primary" is the only pane in single-viewer mode, so every default stays today's behavior.
export type TViewerPane = "primary" | "split"

// Zoom bounds for the ecall ("callout") framing, which still asks for the highlight to take ~40% of
// the viewport — for a bubble that resolves past the ceiling, so THE CEILING is where it lands. Erefs
// and objects fit instead (REQ-455), with their own bounds in helpers/annotationFraming.helper.ts.
//
// Smart navigation moves a pane the user did not click, so an unreadably deep zoom would arrive
// unbidden — it passes SMART_NAV_MAX_ZOOM as the ceiling instead (decision D9 in the #1311 plan).
const CENTER_MIN_ZOOM = 0.5
const CENTER_MAX_ZOOM = 3
// Ceiling for an AUTOMATIC (smart-navigation) jump — see above.
export const SMART_NAV_MAX_ZOOM = 2

// Custom-data keys carried onto a split-screen mirror. The FULL set is copied — not just
// entityId/entityType — so a mirror is indistinguishable from its source to anything that resolves
// an annotation through the second pane's manager. Copying a subset would leave an unwritten
// invariant ("pane-2 hits may only be read through metadataMap") that the next reader would break
// silently. Empty values are skipped, so keys that don't apply to an entity type stay unset.
const MIRRORED_CUSTOM_DATA_KEYS = [
	"entityId",
	"entityType",
	"status",
	"entityStatus",
	"elevationNumber",
	"pageName",
	"pageNumber",
	"roomName",
	"linkedErefIds",
	"category",
	"erefId",
] as const

// Selected-highlight outline + control points (Figma "Detected / Selected=Yes / Highlighted=Yes"):
// 2px solid --clr-main (#0047FF). Kept as raw channels because Apryse takes an Annotations.Color,
// not CSS — the value must stay in step with --clr-main in root.scss.
const SELECTION_OUTLINE_COLOR: [number, number, number] = [0, 71, 255]
const SELECTION_OUTLINE_THICKNESS = 2

const resolveEntityFillState = (
	entityStatus: TErefStatus | TFurnitureStatus | undefined,
	connectivity: TAnnotationStatus,
): TAnnotationFillState => {
	if (entityStatus === "in_quotation") return "in_quotation"
	if (entityStatus === "reviewed") return "reviewed"
	return connectivity
}

// The subset of Apryse's `FreeTextAnnotation.RichTextEditor` that `applyNoteStyleToOpenEditor`
// touches, declared locally and ENTIRELY OPTIONAL on purpose: the SDK's own typings for this class
// have twice promised methods that were absent at runtime in this build (`tool.on()`,
// `annotationManager.getEditBoxManager().on()` — the latter took the whole editor to a white page),
// so nothing here may be called without a `typeof … === "function"` check first. `format` is
// deliberately widened past the SDK's declared `Format` union to admit the undocumented
// `"applyCustomFontSize"` (see `applyNoteStyleToOpenEditor` for how that name was established), and
// `selectAll` is runtime-only — present on the prototype, absent from the typings.
type TNoteRichTextEditor = {
	format?: (format: string, value: string | number | boolean) => void
	hasFocus?: () => boolean
	getSelection?: () => { index: number; length: number } | null
	setSelection?: (index: number, length: number) => void
	selectAll?: () => void
}

// REQ-400: highlight colors arrive as backend "#RRGGBB" hex strings; Apryse's Color takes raw
// 0-255 channels. First hex parser in this file — every other color here is already channel triples.
// Exported so the create flow (interactions/useFigureHighlightCreate) can preview the same channels
// on the native draft annotation before it is persisted, instead of re-deriving this parse.
export const hexToRgb = (hex: string): [number, number, number] => {
	const match = /^#([0-9a-fA-F]{2})([0-9a-fA-F]{2})([0-9a-fA-F]{2})$/.exec(hex)
	if (!match) return [0, 0, 0]
	return [parseInt(match[1], 16), parseInt(match[2], 16), parseInt(match[3], 16)]
}

/**
 * REQ-400: the identity of a highlight is (type, id) — NEVER the id alone.
 *
 * Figures (rectangle/ellipse) and freehand live in SEPARATE backend tables with independent id
 * sequences, so a figure and a freehand highlight routinely share an id. That is exactly why the
 * DELETE endpoint requires a `highlight_type` query param — the backend documents that
 * "`highlight_id` alone does not uniquely identify a row" — and the same is true of every lookup on
 * this side of the wire.
 *
 * Keying on the id alone silently breaks two ways once the two id ranges overlap: `addHighlight`
 * mistakes a new freehand for an already-drawn figure and never paints it (the native draft is
 * already gone by then, so the stroke just vanishes), and `removeHighlight` finds the wrong record
 * and leaves the real annotation on the canvas until a reload rebuilds it from the server.
 *
 * Rectangle and ellipse deliberately collapse to one key: they share the `figure` table, and so an
 * id space.
 */
export const highlightKey = (highlight: THighlight): string =>
	`${highlight.shape_type === "freehand" ? "freehand" : "figure"}:${highlight.id}`

class AnnotationService {
	private instance: PDFInstance | null = null
	private annotationsByCategory: Map<TAnnotationCategory, Set<Core.Annotations.Annotation>> = new Map()
	// Categories the user has switched off. Apryse owns the actual visibility; this mirrors it so
	// hit-testing can tell "there is a highlight here" from "there is a highlight here but nobody
	// can see it" (see `isPointOverEntityAnnotation`).
	private hiddenCategories: Set<TAnnotationCategory> = new Set()
	private metadataMap: Map<string, TAnnotationMetadata> = new Map()
	// Incremented on every frameAnnotations call so a newer scroll cancels the
	// async/deferred work (zoom settle + verify loop) of any earlier in-flight one.
	// Kept PER PANE (REQ-399): the two split-screen panes scroll independently, so focusing an
	// entity in the right screen must not abort a still-settling scroll in the left one.
	private scrollGeneration: Record<TViewerPane, number> = { primary: 0, split: 0 }
	// frameAnnotations calls still running per pane. A pane being framed owns its own viewport, so
	// holdPaneScroll must not restore a pre-frame zoom over it (REQ-455, dual view + auto follow).
	private framesInFlight: Record<TViewerPane, number> = { primary: 0, split: 0 }
	onAnnotationSelected?: (metadata: TAnnotationMetadata) => void
	// Fired when the user finishes moving/resizing an entity highlight (REQ-349). Carries the
	// entity's metadata (whose `renderedRect` still holds the PREVIOUS, last-saved coords) plus
	// the new backend coordinates recovered from the annotation. The orchestrator persists them.
	onAnnotationCoordinatesChanged?: (metadata: TAnnotationMetadata, coordinates: TCoordinates) => void
	// Stored so setupClickListener can be idempotent: the single shared Quotations instance
	// re-registers on every preview↔editor switch (#1119), so we must remove the prior
	// listener before adding a new one (and on cleanup) to avoid stacking duplicates.
	private annotationSelectedHandler: ((annotations: Core.Annotations.Annotation[], action: string) => void) | null =
		null
	// Apryse's own selection-appearance statics, snapshotted while the connect-objects picking
	// appearance is applied so leaving the mode restores them exactly. Non-null == mode is applied.
	private savedSelectionAppearance: {
		outlineThickness: number
		outlineColor: Core.Annotations.Color
		noPermissionOutlineColor: Core.Annotations.Color
		useDashedLine: boolean
		handleColor: Core.Annotations.Color
		handleOutlineColor: Core.Annotations.Color
	} | null = null
	// Same idempotency concern as annotationSelectedHandler, for the "annotationChanged" listener.
	private annotationChangedHandler:
		| ((annotations: Core.Annotations.Annotation[], action: string, info?: { imported?: boolean }) => void)
		| null = null
	// --- Furniture info labels (REQ-401) -------------------------------------------------------
	// Display-only text patches drawn above/below furniture highlights. They are deliberately kept
	// OUT of `metadataMap` / `annotationsByCategory` so every selection, hit-test, geometry and
	// coordinate-commit path ignores them for free.
	private labelClass: TFurnitureLabelConstructor | null = null
	// Which instance `labelClass` was derived from. The Quotations viewer re-registers the SAME
	// shared instance on every preview↔editor switch (#1119), so the subclass (and its draw-handler
	// registration) is built exactly once per real WebViewer — but a genuinely new instance gets a
	// fresh one built against its own `Annotations` namespace.
	private labelClassInstance: PDFInstance | null = null
	private labelAnnotations: Map<number, Core.Annotations.RectangleAnnotation> = new Map()
	// Signature (text + fill + rect + page) of what each label currently shows, so a sync only
	// touches the labels that actually differ.
	private labelSignatures: Map<number, string> = new Map()
	// Label top-left relative to its base highlight's top-left, so a live drag can move the label
	// with the box before the debounced PATCH / cache write recomputes collisions.
	private labelOffsets: Map<number, { dx: number; dy: number }> = new Map()
	private labelIds: Set<string> = new Set()
	private textMeasureContext: CanvasRenderingContext2D | null = null
	private textMeasureAttempted = false
	// Labels follow the `furniture:connected` category toggle; remembered so labels created after
	// the toggle start out in the right state.
	private labelsVisible = true

	// --- Highlights (REQ-400, load/hydration only) -----------------------------------------------
	// Persisted rectangle/ellipse/freehand highlights drawn by #1323/#1324. Purely decorative: kept
	// OUT of `metadataMap` / `annotationsByCategory` like furniture info labels above, for the same
	// reason (every selection/hit-test/coordinate-commit path ignores them for free). Unlike a
	// label, a highlight is never drawn on top of anything (it's the bottom-most layer, see
	// `renderAnnotations`), so it needs no yield-to-what's-underneath selection model — "never
	// hit-testable" (`applyHighlightPermissions`) is exactly the wanted behaviour, not a workaround.
	private highlightAnnotations: Core.Annotations.Annotation[] = []
	private highlightIds: Set<string> = new Set()
	// Apryse annotation Id -> the domain highlight it renders. REQ-400 create/delete needs the
	// backend id + page/coords back from a hit-test (getHighlightAtMouseEvent), which the load-only
	// fields above don't carry.
	private highlightById: Map<string, THighlight> = new Map()
	// The one highlight currently lifted out of its inert permissions by a click-to-select
	// (REQ-400 delete) — null when nothing is picked. Single-selection only: unlike the entity
	// highlights, only one highlight can be "selected" (showing the floating delete toolbar) at a
	// time.
	private selectedHighlightAnnotId: string | null = null
	private selectedHighlightPane: TViewerPane | null = null
	// Memoized answers from `supportsCloudyBorder` — a property of the loaded Apryse build, so it is
	// resolved once per shape and never invalidated (see that method's comment).
	private cloudySupportByShape: Map<string, boolean> = new Map()

	// --- Notes (REQ-395/396) ----------------------------------------------------------------------
	// Real, NATIVELY INTERACTIVE Apryse `FreeTextAnnotation`s — the first use of that class in this
	// codebase. Unlike every other tracked annotation here (entities driven entirely by our own
	// click/drag handlers, highlights kept fully inert), a note leans on Apryse's own built-in
	// click/drag-to-place, double-click-to-edit rich-text editor, and move/resize handles. Kept OUT
	// of `metadataMap` (no sidebar, no entity-style hit-testing) and OUT of the highlight-style
	// inert-permissions model (`NoMove`/`NoResize` stay false so the native handles work) —
	// see `applyNotePermissions` for exactly what IS still denied (rotation, native delete).
	private noteAnnotations: Map<number, Core.Annotations.Annotation> = new Map()
	// Apryse annotation Id -> the backend note id it renders. Needed to resolve a hit-test
	// (annotationSelected) or a freshly-added draft back to "which note is this" / "is this note id
	// already ours" without scanning `noteAnnotations`'s values.
	private noteIdByAnnotId: Map<string, number> = new Map()
	// Last-known backend record per note, refreshed after every successful create/update. The note
	// interaction hooks diff a live annotation's Contents/FontSize/TextColor/geometry against THIS
	// (not against a previous annotation snapshot) to know which fields actually changed and to read
	// the note's current optimistic-lock `version` for a PATCH/DELETE.
	private noteRecords: Map<number, TNote> = new Map()

	// The split pane's mirror for each PRIMARY highlight annotation, keyed by the PRIMARY
	// annotation's Id (highlights have no entityType/entityId, so this can't key off entityKey like
	// mirroredByEntityKey below). Without this a highlight only ever rendered in the primary manager
	// — drawing one in the split pane deleted the native draft there and re-added it through
	// addHighlight, which only ever wrote to the primary manager, so it silently "jumped" to the
	// left pane. Both the primary and mirror Apryse Ids are also registered in `highlightIds` /
	// `highlightById` so hit-testing and isOwnAnnotation work from either pane's own manager.
	private mirroredHighlightByPrimaryId: Map<string, Core.Annotations.Annotation> = new Map()

	// --- Split-screen / MultiViewerMode support (REQ-399) ---------------------------------------
	// Apryse's side-by-side mode gives the SECOND viewport its own AnnotationManager; annotations
	// are never shared between managers. So the second pane gets a MIRROR of every tracked entity
	// highlight: a separate annotation object with the same geometry, style, contents and custom
	// data, registered in `metadataMap` under its own Id so the shared selection/change handlers
	// resolve it to the same entity. The mirror is a pure PROJECTION — versions and coordinates
	// still come from the TanStack cache at commit time (see the plan's version-mismatch
	// invariant), so an edit made in either pane goes through the same per-entity mutation queue.
	private splitViewer: Core.DocumentViewer | null = null
	private splitManager: Core.AnnotationManager | null = null
	// entityKey (`${entityType}-${entityId}`) → the mirrored annotation in the second pane.
	private mirroredByEntityKey: Map<string, Core.Annotations.RectangleAnnotation> = new Map()
	// Microtask-debounced full re-mirror. Style/status updates touch the primary annotations in
	// place (property mutation + redraw, no annotationChanged event), so instead of fanning every
	// mutating method out by hand we re-project the whole layer once per tick. No-op with no
	// second pane registered, which keeps single-viewer behavior byte-identical.
	private splitSyncScheduled = false
	// The pane the user last interacted with. Selection STATE is always owned by the primary
	// manager (a click in the second pane selects both the mirror and its primary counterpart), so
	// this only decides which viewport the floating overlays MEASURE against — the selection
	// toolbar, the paste point, the context menu.
	private activePane: TViewerPane = "primary"
	// Annotation ids selected by smart navigation as the Secondary pane's counterpart rather than by
	// the user (#1311) — see selectEntityAsCounterpart.
	private counterpartSelectedIds = new Set<string>()
	private splitChangedHandler:
		| ((annotations: Core.Annotations.Annotation[], action: string, info?: { imported?: boolean }) => void)
		| null = null

	setInstance(instance: PDFInstance): void {
		this.instance = instance
		instance.Core.annotationManager.promoteUserToAdmin()
		// Deny every permission on label annotations so Apryse's edit tool won't move, resize or
		// delete them; everything else keeps the admin permissions promoteUserToAdmin just granted.
		instance.Core.annotationManager.setPermissionCheckCallback?.((...args: unknown[]) => {
			const annot = args.find(
				(arg): arg is Core.Annotations.Annotation =>
					typeof arg === "object" && arg !== null && typeof (arg as { Id?: unknown }).Id === "string",
			)
			return !(annot && this.isInertAnnotation(annot.Id))
		})
		this.setupClickListener()
		this.setupChangeListener()
	}

	renderAnnotations(pages: TPageElevationData[], pageInfos: TPageInfo[] = []): void {
		if (!this.instance) return

		// Entity highlights only — furniture info labels are owned entirely by `syncFurnitureLabels`
		// and its own add/update/delete diff, so a re-render must NOT wipe them. Keeping them out of
		// here is what makes the two `documentLoaded` listeners order-independent: whichever of
		// render / label-sync runs first, the result is the same. (`cleanup()` still clears both.)
		this.clearEntityAnnotations()
		this.clearHighlights()

		const { Annotations, annotationManager, documentViewer, Math: CoreMath } = this.instance.Core
		const doc = documentViewer.getDocument()
		const allAnnotations: Core.Annotations.Annotation[] = []

		// Per-page rotation lookup (intrinsic /Rotate degrees) + unrotated page dims for
		// the coordinate transform. Backend coords are in original (unrotated) page space;
		// we rotate them so they line up with what WebViewer renders. Include page_infos[]
		// pages too — some pages have a page-name box but no related_objects entry.
		const pageTransforms = this.buildPageRotationMap(doc, [
			...pages,
			...pageInfos.map((info) => ({ page_number: info.page_number }) as TPageElevationData),
		])

		// Pass -1 — persisted highlights (REQ-400, drawn at the very bottom, under the page-name
		// boxes too). Purely decorative: no metadata, no category, no sidebar — see the "Highlights"
		// field comments above for why they're excluded from trackAnnotation.
		for (const page of pages) {
			const viewerPageNumber = page.page_number + 1
			const transform = pageTransforms.get(viewerPageNumber)

			for (const highlight of page.highlights ?? []) {
				const annot = this.buildHighlightAnnotation(highlight, viewerPageNumber, transform, Annotations, CoreMath.Point)
				if (!annot) continue
				this.applyHighlightPermissions(annot)
				this.highlightAnnotations.push(annot)
				this.highlightIds.add(annot.Id)
				this.highlightById.set(annot.Id, highlight)
				allAnnotations.push(annot)
				this.mirrorHighlightIntoSplitPane(annot.Id, highlight)
			}
		}

		// Pass 0 — page-name detection boxes (drawn at the very bottom, under every entity
		// highlight). One per page_infos[] record. They carry no connectivity and have no
		// sidebar, so they always use the default (connected/yellow) fill — but they ARE
		// movable/resizable so the user can correct the page detection bbox, which the
		// coordinate-commit flow PATCHes to …/pages/{page_info_id}/ (same as eref/ecall/furniture).
		for (const pageInfo of pageInfos) {
			const viewerPageNumber = pageInfo.page_number + 1
			const transform = pageTransforms.get(viewerPageNumber)
			const category: TAnnotationCategory = "page:connected"
			const coords = this.applyRotation(
				{ x0: pageInfo.x0, y0: pageInfo.y0, x1: pageInfo.x1, y1: pageInfo.y1 },
				transform,
			)

			const annot = new Annotations.RectangleAnnotation({
				PageNumber: viewerPageNumber,
				X: coords.x0,
				Y: coords.y0,
				Width: coords.x1 - coords.x0,
				Height: coords.y1 - coords.y0,
			})
			this.applyStyle(annot, "connected", Annotations)
			this.applyEntityPermissions(annot)
			if (pageInfo.page_name) annot.setContents(pageInfo.page_name)

			annot.setCustomData("entityId", String(pageInfo.id))
			annot.setCustomData("entityType", "page")
			annot.setCustomData("pageName", pageInfo.page_name ?? "")
			annot.setCustomData("pageNumber", String(pageInfo.page_number))

			const metadata: TAnnotationMetadata = {
				entityId: pageInfo.id,
				entityType: "page",
				status: "connected",
				pageNumber: pageInfo.page_number,
				pageName: pageInfo.page_name ?? undefined,
				renderedRect: { x0: pageInfo.x0, y0: pageInfo.y0, x1: pageInfo.x1, y1: pageInfo.y1 },
			}
			this.trackAnnotation(annot, category, metadata)
			allAnnotations.push(annot)
		}

		// Build set of eref IDs that have furniture
		const erefsWithFurniture = new Set<number>()
		for (const page of pages) {
			for (const f of page.furniture) {
				if (f.eref_id) erefsWithFurniture.add(f.eref_id)
			}
		}

		// Pass 1 — erefs (drawn at bottom). Seed each eref's connectivity from the furniture
		// set; this is the single place where furniture presence maps to eref connectivity.
		for (const page of pages) {
			// API pages are 0-based, WebViewer is 1-based
			const viewerPageNumber = page.page_number + 1
			const transform = pageTransforms.get(viewerPageNumber)

			for (const eref of page.erefs) {
				const status: TAnnotationStatus = erefsWithFurniture.has(eref.id) ? "connected" : "unconnected"
				const category: TAnnotationCategory = `eref:${status}`
				const fillState = resolveEntityFillState(eref.status, status)
				const coords = this.applyRotation(eref.coordinates, transform)

				const annot = new Annotations.RectangleAnnotation({
					PageNumber: viewerPageNumber,
					X: coords.x0,
					Y: coords.y0,
					Width: coords.x1 - coords.x0,
					Height: coords.y1 - coords.y0,
				})
				this.applyStyle(annot, fillState, Annotations)
				this.applyEntityPermissions(annot)
				annot.setContents(this.buildErefLabel(eref.elevation_number))

				annot.setCustomData("entityId", String(eref.id))
				annot.setCustomData("entityType", "eref")
				annot.setCustomData("status", status)
				annot.setCustomData("entityStatus", eref.status ?? "")
				this.applyErefTextData(annot, eref)
				annot.setCustomData("pageNumber", String(eref.page_number))

				const metadata: TAnnotationMetadata = {
					entityId: eref.id,
					entityType: "eref",
					status,
					pageNumber: eref.page_number,
					...this.erefTextMetadata(eref),
					renderedRect: eref.coordinates,
				}
				this.trackAnnotation(annot, category, metadata)
				allAnnotations.push(annot)
			}
		}

		// Pass 2 — ecalls. Every eref annotation now exists, so connectivity is read through
		// isEcallConnected → isErefConnected — the same predicate the runtime cascade uses,
		// so the render and live paths can't drift apart.
		for (const page of pages) {
			const viewerPageNumber = page.page_number + 1
			const transform = pageTransforms.get(viewerPageNumber)

			for (const ecall of page.ecalls) {
				const linkedErefIds = this.extractLinkedErefIds(ecall)
				const status: TAnnotationStatus = this.isEcallConnected(linkedErefIds) ? "connected" : "unconnected"
				const category: TAnnotationCategory = `ecall:${status}`
				const coords = this.applyRotation(ecall.coordinates, transform)

				const annot = new Annotations.RectangleAnnotation({
					PageNumber: viewerPageNumber,
					X: coords.x0,
					Y: coords.y0,
					Width: coords.x1 - coords.x0,
					Height: coords.y1 - coords.y0,
				})
				// Ecalls don't carry review/in_quotation status — connectivity drives color.
				this.applyStyle(annot, status, Annotations)
				this.applyEntityPermissions(annot)
				annot.setContents(this.buildEcallLabel(ecall))

				annot.setCustomData("entityId", String(ecall.id))
				annot.setCustomData("entityType", "ecall")
				annot.setCustomData("status", status)
				this.applyEcallTextData(annot, ecall)
				annot.setCustomData("pageNumber", String(ecall.page_number))
				annot.setCustomData("linkedErefIds", JSON.stringify(linkedErefIds))

				const metadata: TAnnotationMetadata = {
					entityId: ecall.id,
					entityType: "ecall",
					status,
					pageNumber: ecall.page_number,
					...this.ecallTextMetadata(ecall),
					renderedRect: ecall.coordinates,
				}
				this.trackAnnotation(annot, category, metadata)
				allAnnotations.push(annot)
			}
		}

		// Pass 3 — furniture (drawn on top).
		for (const page of pages) {
			const viewerPageNumber = page.page_number + 1
			const transform = pageTransforms.get(viewerPageNumber)

			for (const furniture of page.furniture) {
				const status: TAnnotationStatus = "connected"
				const category: TAnnotationCategory = `furniture:${status}`
				const fillState = resolveEntityFillState(furniture.status, status)
				const coords = this.applyRotation(furniture.coordinates, transform)

				const annot = new Annotations.RectangleAnnotation({
					PageNumber: viewerPageNumber,
					X: coords.x0,
					Y: coords.y0,
					Width: coords.x1 - coords.x0,
					Height: coords.y1 - coords.y0,
				})
				this.applyStyle(annot, fillState, Annotations)
				this.applyEntityPermissions(annot)
				annot.setContents(this.buildFurnitureLabel(furniture))

				annot.setCustomData("entityId", String(furniture.id))
				annot.setCustomData("entityType", "furniture")
				annot.setCustomData("status", status)
				annot.setCustomData("entityStatus", furniture.status ?? "")
				annot.setCustomData("erefId", String(furniture.eref_id))
				annot.setCustomData("category", furniture.category?.name ?? "")
				annot.setCustomData("pageNumber", String(furniture.page_number))

				const metadata: TAnnotationMetadata = {
					entityId: furniture.id,
					entityType: "furniture",
					status,
					pageNumber: furniture.page_number,
					erefId: furniture.eref_id ?? undefined,
					renderedRect: furniture.coordinates,
				}
				this.trackAnnotation(annot, category, metadata)
				allAnnotations.push(annot)
			}
		}

		annotationManager.addAnnotations(allAnnotations)
		annotationManager.drawAnnotationsFromList(allAnnotations)
	}

	clearAllAnnotations(): void {
		this.clearEntityAnnotations()
		this.clearFurnitureLabels()
		this.clearHighlights()
		this.clearNotes()
	}

	// Entity highlights (eref / ecall / furniture / page) only, leaving furniture info labels in
	// place — see the note in renderAnnotations for why that separation matters.
	private clearEntityAnnotations(): void {
		if (this.instance) {
			const { documentViewer, annotationManager } = this.instance.Core
			const doc = documentViewer.getDocument()

			if (doc && this.annotationsByCategory.size > 0) {
				const allAnnotations: Core.Annotations.Annotation[] = []
				for (const annotations of this.annotationsByCategory.values()) {
					for (const annot of annotations) {
						allAnnotations.push(annot)
					}
				}
				// Clear NoDelete/NoRotate before deleting, same as removeEntityAnnotations /
				// clearHighlights: entity annots are created with NoDelete (applyEntityPermissions), and
				// in this Apryse version `deleteAnnotations({ force: true })` alone does NOT remove a
				// NoDelete annotation — `force` is silently ignored. Without this, EVERY peer-driven
				// redraw (any mutation in the other pop-out window) left the previous full set of
				// entity highlights on the canvas and added a fresh set on top, duplicating every
				// eref/ecall/furniture/page annotation in the whole document on each sync. Invisible
				// for a same-position restyle (the duplicate sits exactly on top of the original), but
				// glaringly visible for a coordinate edit — reported as "duplicated annots" (#1441
				// review) after dragging an eref/ecall/furniture's position.
				for (const annot of allAnnotations) {
					annot.NoDelete = false
					annot.NoRotate = false
				}
				annotationManager.deleteAnnotations(allAnnotations, { force: true })
			}
		}

		this.annotationsByCategory.clear()
		this.metadataMap.clear()
		// Drop the second pane's mirrored copies too, so a re-render can't leave orphans behind.
		this.clearSplitAnnotations()
		// Visibility is a property of the annotations that just went away; a re-render starts with
		// everything shown, so a stale "hidden" entry would wrongly suppress the new highlights.
		this.hiddenCategories.clear()
	}

	setCategoryVisibility(category: TAnnotationCategory, visible: boolean): void {
		if (!this.instance) return

		if (visible) {
			this.hiddenCategories.delete(category)
		} else {
			this.hiddenCategories.add(category)
		}

		// Info labels belong to their furniture highlight — they hide and show with it (REQ-401).
		if (category === "furniture:connected") {
			this.labelsVisible = visible
			const { annotationManager } = this.instance.Core
			for (const label of this.labelAnnotations.values()) {
				if (visible) {
					annotationManager.showAnnotation(label)
				} else {
					annotationManager.hideAnnotation(label)
				}
			}
		}

		const annotations = this.annotationsByCategory.get(category)
		if (!annotations) return

		const { annotationManager } = this.instance.Core
		for (const annot of annotations) {
			if (visible) {
				annotationManager.showAnnotation(annot)
			} else {
				annotationManager.hideAnnotation(annot)
			}
			// Mirror the same visibility into the split-screen pane (no-op when it's closed).
			const metadata = this.metadataMap.get(annot.Id)
			const mirrored = metadata
				? this.mirroredByEntityKey.get(this.entityKey(metadata.entityType, metadata.entityId))
				: undefined
			if (mirrored && this.splitManager) {
				if (visible) this.splitManager.showAnnotation(mirrored)
				else this.splitManager.hideAnnotation(mirrored)
			}
		}
	}

	getMetadata(annotationId: string): TAnnotationMetadata | undefined {
		return this.metadataMap.get(annotationId)
	}

	// Split screen (REQ-399 / review #3+#4 on #1264): every focus call names the pane it should
	// happen in. "primary" is the left screen — the only one in single-viewer mode, and the one the
	// left sidebar and the middle-docked sidebar always drive. The right screen's own sidebar passes
	// "split" so its navigation scrolls the pane it belongs to instead of yanking the left one.
	// REQ-455: an eref is framed together with every object connected to it.
	async scrollToEref(erefId: number, pane: TViewerPane = "primary", maxZoom?: number): Promise<void> {
		if (!this.instance) return
		const group = this.resolveErefGroup(pane, erefId)
		if (group.length === 0) return
		await this.frameAnnotations(group, "group", pane, maxZoom ?? FRAME_MAX_ZOOM)
	}

	// REQ-455: an object on an eref frames that eref's whole group (AC4); a lone one is centered (AC5).
	async scrollToFurniture(furnitureId: number, pane: TViewerPane = "primary"): Promise<void> {
		if (!this.instance) return
		const primaryAnnot = this.findEntityAnnotation("furniture", furnitureId)
		const erefId = primaryAnnot ? this.metadataMap.get(primaryAnnot.Id)?.erefId : undefined
		if (erefId != null) {
			const group = this.resolveErefGroup(pane, erefId)
			if (group.length > 0) {
				await this.frameAnnotations(group, "group", pane, FRAME_MAX_ZOOM)
				return
			}
		}
		const annot = this.findPaneAnnotation(pane, "furniture", furnitureId)
		if (!annot) return
		await this.frameAnnotations([annot], "single", pane, FRAME_MAX_ZOOM)
	}

	async scrollToEcall(ecallId: number, pane: TViewerPane = "primary", maxZoom?: number): Promise<void> {
		if (!this.instance) return
		const annot = this.findPaneAnnotation(pane, "ecall", ecallId)
		if (!annot) return
		await this.frameAnnotations([annot], "callout", pane, maxZoom)
	}

	// Abandon whatever scroll is still settling in ONE pane — always the pane about to receive the
	// counterpart's own fresh scroll (smart navigation's Secondary), never the pane the user just
	// clicked. A `frameAnnotations` pass runs for up to two seconds (it has to — a far page arrives
	// one render at a time), and its own generation guard only cancels it when a NEWER scroll is
	// issued for the SAME pane. Smart navigation moves the OTHER pane on every flip, so without this
	// the previous Secondary pane's scroll kept running after it had become the Primary — arriving
	// seconds later and dragging the pane the user was reading onto the counterpart's page (#1311:
	// click an ecall in the pane that currently hosts the jumping bar).
	//
	// Must NOT touch the pane the caller just clicked in: by the time this runs, that pane's own
	// `scrollToEref`/`scrollToEcall`/`scrollToFurniture` call is already in flight (the click handler
	// issues it before notifying smart navigation), and bumping its generation here invalidates that
	// legitimate, still-settling scroll with nothing left to reissue it — the pane lands wherever
	// `frameAnnotations`'s settle loop happened to be (zoomed but not yet corrected to its anchor)
	// instead of where the click asked it to go. Measured live: an eref click with "Auto open floor
	// plan" on landed away from its bottom-left anchor every time; off, it landed correctly, because
	// this only ever ran while the auto mode's gate passed (PR #1347 review).
	cancelScrolls(pane: TViewerPane): void {
		smartNavDebug("cancelScrolls", { pane, ...this.scrollGeneration })
		this.scrollGeneration[pane] += 1
	}

	selectEref(erefId: number): void {
		const annot = this.findEntityAnnotation("eref", erefId)
		if (!annot) return
		this.selectAnnotation(annot)
	}

	selectFurniture(furnitureId: number): void {
		const annot = this.findEntityAnnotation("furniture", furnitureId)
		if (!annot) return
		this.selectAnnotation(annot)
	}

	selectEcall(ecallId: number): void {
		const annot = this.findEntityAnnotation("ecall", ecallId)
		if (!annot) return
		this.selectAnnotation(annot)
	}

	// Select the entity annotation under a mouse event ourselves, bypassing Apryse's
	// built-in click-to-select. Once a second WebViewer instance has existed in the page
	// (the Documents-style preview viewer on the Quotations tab), Apryse's edit tool silently
	// stops firing `annotationSelected` on click — but hit-testing and programmatic selection
	// both still work. So we hit-test the click and select manually; the existing
	// `annotationSelected` listeners then open the reference sidebar as usual. See #1119.
	// Returns true if one of our entity annotations was hit (and selected).
	// With `{ additive: true }` (Shift/Cmd/Ctrl+click) the hit is TOGGLED into a
	// furniture-only multi-selection instead of replacing the current selection — the
	// batch toolbar operates on furniture only, so additively hitting an eref/ecall is ignored.
	selectAnnotationFromMouseEvent(event: MouseEvent, options?: { additive?: boolean }): boolean {
		if (!this.instance) return false
		const { annotationManager } = this.instance.Core
		// Take ALL annotations under the cursor, not just the topmost one: display-only furniture
		// info labels (REQ-401) are drawn over the drawing and would otherwise swallow the click on
		// whatever highlight sits beneath them (in the reference layout a countertop label lands on
		// top of a neighbouring countertop's box). Pick the hit we actually own, preferring whichever
		// one is already selected (see pickHitAnnotation) over the first hit in raw z-order.
		const hits =
			typeof annotationManager.getAnnotationsByMouseEvent === "function"
				? annotationManager.getAnnotationsByMouseEvent(event)
				: [annotationManager.getAnnotationByMouseEvent(event)]
		const ownedHits = hits.filter((hit): hit is Core.Annotations.Annotation => !!hit && this.metadataMap.has(hit.Id))
		const annot = this.pickHitAnnotation(ownedHits)
		const metadata = annot ? this.metadataMap.get(annot.Id) : undefined
		// Only act on our own highlights (furniture/eref/ecall/page live in metadataMap); ignore
		// clicks on empty space, widgets, or links so the default tool keeps handling them. Selecting
		// a page box just shows its drag/resize handles — it has no sidebar, so the annotationSelected
		// handler simply doesn't open one (no furniture/eref/ecall branch matches).
		if (!annot || !metadata) return false
		if (options?.additive) {
			// Additive (batch) selection is furniture-only; a page/eref/ecall additive hit is ignored.
			if (metadata.entityType !== "furniture") return false
			this.toggleFurnitureInSelection(annot)
			return true
		}
		this.selectAnnotation(annot)
		return true
	}

	// Among several owned hits at the same point, prefer the one the user already has selected over
	// Apryse's raw z-order (#1312). Grabbing a selected highlight's resize handle fires a mousedown
	// at that point, and if the highlight overlaps a neighbour, the neighbour can rank first in the
	// raw hit list even though the selected one is what the user is interacting with — that mousedown
	// must reach Apryse's edit tool as a CONTINUED interaction with the selected highlight, not get
	// reinterpreted as a fresh pick of whatever else sits at the same point. A batch selection (or
	// none) has no single "current" object to prefer, so it falls through to the first hit.
	//
	// Matched on the ENTITY, and read from `getSelectedEntities()` — the primary manager, minus
	// smart navigation's counterparts — rather than on annotation ids from whichever manager is
	// being clicked. Both details are load-bearing, and each was a bug:
	//
	//   - The split pane's hits are MIRROR annotations with their own ids, so an id comparison can
	//     only ever work in the pane it was written for. Worse, Apryse's split-pane edit tool
	//     independently selects the topmost annotation in the mirror manager, so that manager's
	//     selection drifts away from the real one (measured: primary [42725] / mirror [42723]) —
	//     reading it made the preference correct on the first press and wrong on every one after.
	//   - `Auto open floor plan` is ON for every dual-screen entry (#1383), and it adds a
	//     counterpart, so the raw selection is routinely two annotations. A plain length check on
	//     that fell through to `hits[0]` and the preference never applied at all under the shipped
	//     default. `getSelectedEntities()` already skips `counterpartSelectedIds`, for the same
	//     reason the batch pill does.
	private pickHitAnnotation(hits: Core.Annotations.Annotation[]): Core.Annotations.Annotation | undefined {
		const entities = this.getSelectedEntities()
		if (entities.length === 1) {
			const selectedKey = this.entityKey(entities[0].entityType, entities[0].entityId)
			const current = hits.find((hit) => {
				const metadata = this.metadataMap.get(hit.Id)
				return metadata ? this.entityKey(metadata.entityType, metadata.entityId) === selectedKey : false
			})
			if (current) return current
		}
		return hits[0]
	}

	// Toggle a furniture annotation in/out of the live selection without clearing the rest.
	// Any non-furniture annot currently selected (a lone eref/ecall) is dropped first so a
	// batch selection stays furniture-only.
	private toggleFurnitureInSelection(annot: Core.Annotations.Annotation): void {
		if (!this.instance) return
		const { annotationManager } = this.instance.Core
		// A batch selection is furniture-only, so smart navigation's auto-selected counterpart
		// (#1311) has to be RELEASED here, not merely deselected along with the rest below: dropping
		// its annotation while its id stays in `counterpartSelectedIds` would leave it move-locked
		// and invisible to `getSelectedEntities` for the rest of the session. Released before the
		// selection is read, since releasing deselects.
		this.releaseCounterpartSelection()
		const selected = annotationManager.getSelectedAnnotations()
		const nonFurniture = selected.filter((a) => this.metadataMap.get(a.Id)?.entityType !== "furniture")
		if (nonFurniture.length > 0) annotationManager.deselectAnnotations(nonFurniture)
		if (selected.some((a) => a.Id === annot.Id)) {
			annotationManager.deselectAnnotation(annot)
		} else {
			annotationManager.selectAnnotation(annot)
		}
	}

	deselectAll(): void {
		this.instance?.Core.annotationManager.deselectAllAnnotations()
		this.releaseCounterpartSelection()
	}

	// Ids of the currently-selected furniture highlights (skips erefs/ecalls and any non-entity
	// annotation). Copy is furniture-only and single-item, so the caller uses this to reject a
	// multi-selection and to copy exactly the selected furniture.
	getSelectedFurnitureIds(): number[] {
		if (!this.instance) return []
		const { annotationManager } = this.instance.Core
		const ids: number[] = []
		for (const annot of annotationManager.getSelectedAnnotations()) {
			const metadata = this.metadataMap.get(annot.Id)
			if (metadata?.entityType === "furniture") ids.push(metadata.entityId)
		}
		return ids
	}

	// Every selected entity highlight as (type, id) pairs, in selection order. Lets a caller reason
	// about the WHOLE selection — the floating toolbar uses it to show eref actions only for a
	// selection that is exactly one eref and nothing else (REQ-367: no toolbar for mixed-type
	// selections). Non-entity annotations are skipped.
	// The entities the USER's own selection covers. Smart navigation's auto-selected counterpart
	// (#1311, see selectEntityAsCounterpart) is deliberately excluded: it is selected only so the
	// other pane draws its blue handles, and it must never change what the batch pill acts on or
	// what Delete deletes — those follow the Primary pane's entity, i.e. what the user clicked.
	getSelectedEntities(): { entityType: TAnnotationType; entityId: number }[] {
		if (!this.instance) return []
		const { annotationManager } = this.instance.Core
		const entities: { entityType: TAnnotationType; entityId: number }[] = []
		for (const annot of annotationManager.getSelectedAnnotations()) {
			if (this.counterpartSelectedIds.has(annot.Id)) continue
			const metadata = this.metadataMap.get(annot.Id)
			if (metadata) entities.push({ entityType: metadata.entityType, entityId: metadata.entityId })
		}
		return entities
	}

	private selectAnnotation(annot: Core.Annotations.Annotation): void {
		if (!this.instance) return
		const { annotationManager } = this.instance.Core
		const selected = annotationManager.getSelectedAnnotations()
		if (selected.length === 1 && selected[0].Id === annot.Id) return
		annotationManager.deselectAllAnnotations()
		this.releaseCounterpartSelection()
		annotationManager.selectAnnotation(annot)
	}

	// ADDITIVE selection of smart navigation's counterpart entity (#1311). The design shows BOTH the
	// Primary pane's entity and the Secondary pane's counterpart carrying selection handles, and
	// keeping the user's own selection is also what keeps the eref batch pill and Delete/Backspace
	// working — both resolve exactly one eref, which a plain replacing select would have taken away.
	//
	// So this adds to the selection instead of replacing it, and remembers what it added:
	//   • the PREVIOUS counterpart is dropped first, so stepping the jumping navigation swaps the
	//     highlighted counterpart instead of accumulating one per step;
	//   • the added annotation is move-locked, so a drag on the user's own highlight cannot drag the
	//     auto-selected one along with it (Apryse's edit tool moves the whole selection);
	//   • `getSelectedEntities` skips it, so every consumer of "what is selected" still sees only the
	//     user's own entity.
	//
	// Both annotations live in the PRIMARY manager and are projected onto the split pane's mirror by
	// syncSelectionToSplitPane — that projection is the only thing that draws handles in the second
	// pane, so a split-manager-only selection would be wiped on its next pass. In practice each pane
	// still shows one highlight: an eref and its callouts sit on different pages.
	// `keepPaneStill` names the pane that must NOT move as a result of this select — the one the
	// counterpart is NOT for. Belt to the `autoFocusNoteOnAnnotationSelection: false` constructor
	// option (see core/webviewerInit.ts): the selection lives in the primary manager whichever pane
	// the counterpart is displayed in, so anything that scrolls "the selected annotation" into view
	// moves the wrong pane. Restoring the offsets is provably correct here — a counterpart select is
	// never a reason for the other pane to move (#1311 D12) — and cheap, so it stays as a guard even
	// though the option should make it moot.
	selectEntityAsCounterpart(entityType: "eref" | "ecall", entityId: number, keepPaneStill?: TViewerPane): void {
		if (!this.instance) return
		const annot = this.findEntityAnnotation(entityType, entityId)
		if (!annot) return
		const { annotationManager } = this.instance.Core
		if (this.counterpartSelectedIds.has(annot.Id)) return
		const holdStill = keepPaneStill ? this.holdPaneScroll(keepPaneStill) : null
		try {
			this.selectEntityAsCounterpartInner(annot, annotationManager)
		} finally {
			holdStill?.()
		}
	}

	// Snapshot a pane's whole viewport — zoom AND scroll offsets — and return a restore that runs now
	// and again next frame. The stray scroll this defends against is a single synchronous write, but
	// the frame-later pass costs nothing and covers a deferred one.
	//
	// Zoom is part of the snapshot, not just the offsets: the panes sit in a FIT zoom mode, so a
	// scroll offset only means a position at a given zoom. Restoring the offset while the zoom has
	// moved puts the pane on a different page entirely — and the narrower the pane, the bigger that
	// error, which is why it can show up in one window size and not another.
	private holdPaneScroll(pane: TViewerPane): () => void {
		// Its own in-flight frame keeps it where it belongs; a snapshot here would be the PRE-frame zoom.
		if (this.framesInFlight[pane] > 0) return () => {}
		const documentViewer = this.paneViewer(pane)
		const scrollEl = documentViewer?.getScrollViewElement?.() as HTMLElement | null
		if (!documentViewer || !scrollEl) return () => {}
		const { scrollLeft, scrollTop } = scrollEl
		const zoom = documentViewer.getZoomLevel?.()

		const restore = () => {
			if (!scrollEl.isConnected) return
			// Zoom first: changing it re-scales the scroll content, so the offsets have to be applied
			// after it or they would be interpreted against the wrong scale.
			if (typeof zoom === "number" && zoom > 0 && documentViewer.getZoomLevel?.() !== zoom) {
				documentViewer.zoomTo(zoom)
			}
			if (scrollEl.scrollLeft !== scrollLeft) scrollEl.scrollLeft = scrollLeft
			if (scrollEl.scrollTop !== scrollTop) scrollEl.scrollTop = scrollTop
		}

		return () => {
			restore()
			if (typeof requestAnimationFrame === "function") requestAnimationFrame(restore)
		}
	}

	private selectEntityAsCounterpartInner(
		annot: Core.Annotations.Annotation,
		annotationManager: Core.AnnotationManager,
	): void {
		this.releaseCounterpartSelection()
		// Never re-add what the user already has selected: it would become a counterpart and then be
		// dropped (and move-locked) on the next step.
		if (annotationManager.getSelectedAnnotations().some((selected) => selected.Id === annot.Id)) return
		this.counterpartSelectedIds.add(annot.Id)
		;(annot as Core.Annotations.RectangleAnnotation).NoMove = true
		// The manager's own annotationSelected handler projects the new selection onto the split
		// pane's mirror, so no explicit sync is needed here.
		annotationManager.selectAnnotation(annot)
	}

	// Deselect + unlock whatever selectEntityAsCounterpart last added. Called before adding the next
	// one, whenever the user makes a fresh (replacing) selection, and when the mode ends.
	releaseCounterpartSelection(): void {
		if (this.counterpartSelectedIds.size === 0) return
		const ids = [...this.counterpartSelectedIds]
		this.counterpartSelectedIds.clear()
		const manager = this.instance?.Core.annotationManager
		if (!manager) return
		const annots = manager.getAnnotationsList().filter((annot) => ids.includes(annot.Id))
		for (const annot of annots) (annot as Core.Annotations.RectangleAnnotation).NoMove = false
		const stillSelected = annots.filter((annot) =>
			manager.getSelectedAnnotations().some((selected) => selected.Id === annot.Id),
		)
		if (stillSelected.length > 0) manager.deselectAnnotations(stillSelected)
	}

	updateErefAnnotationStatus(erefId: number, status: TAnnotationStatus): void {
		if (!this.instance) return

		const annot = this.findEntityAnnotation("eref", erefId)
		if (!annot) return

		const { Annotations, annotationManager } = this.instance.Core
		const entityStatus = (annot.getCustomData("entityStatus") || undefined) as TErefStatus | undefined
		const fillState = resolveEntityFillState(entityStatus, status)

		this.applyStyle(annot as Core.Annotations.RectangleAnnotation, fillState, Annotations)
		annot.setCustomData("status", status)

		const existingMetadata = this.metadataMap.get(annot.Id)
		if (existingMetadata) {
			this.metadataMap.set(annot.Id, { ...existingMetadata, status })
		}

		const oppositeStatus: TAnnotationStatus = status === "connected" ? "unconnected" : "connected"
		const oldCategory: TAnnotationCategory = `eref:${oppositeStatus}`
		const newCategory: TAnnotationCategory = `eref:${status}`
		this.annotationsByCategory.get(oldCategory)?.delete(annot)
		if (!this.annotationsByCategory.has(newCategory)) {
			this.annotationsByCategory.set(newCategory, new Set())
		}
		this.annotationsByCategory.get(newCategory)!.add(annot)

		annotationManager.redrawAnnotation(annot)

		// An eref's connectivity just flipped — cascade to any ecalls that link to it so
		// the ecall turns yellow once one of its erefs gains furniture, and back to gray
		// once all of its erefs lose theirs.
		this.refreshEcallsForEref(erefId)
	}

	// Recompute the connectivity color of every ecall that links to the given eref.
	private refreshEcallsForEref(erefId: number): void {
		const categories: TAnnotationCategory[] = ["ecall:connected", "ecall:unconnected"]
		for (const category of categories) {
			const set = this.annotationsByCategory.get(category)
			if (!set) continue
			// Copy the set — recomputeEcallConnectivity may move annots between categories.
			for (const annot of [...set]) {
				if (this.getLinkedErefIds(annot).includes(erefId)) {
					this.recomputeEcallConnectivity(annot)
				}
			}
		}
	}

	private getLinkedErefIds(annot: Core.Annotations.Annotation): number[] {
		const raw = annot.getCustomData("linkedErefIds")
		if (!raw) return []
		try {
			const parsed = JSON.parse(raw) as unknown
			return Array.isArray(parsed) ? parsed.filter((id): id is number => typeof id === "number") : []
		} catch {
			return []
		}
	}

	private extractLinkedErefIds(ecall: TEcall): number[] {
		return ecall.elevation_numbers.map((en) => en.eref_id).filter((id): id is number => id !== null)
	}

	// Single source of truth for eref connectivity: an eref is "connected" (has furniture)
	// iff its annotation says so. The status is seeded from the furniture set during
	// renderAnnotations and kept current at runtime by updateErefAnnotationStatus.
	private isErefConnected(erefId: number): boolean {
		return this.findEntityAnnotation("eref", erefId)?.getCustomData("status") === "connected"
	}

	// An ecall is "connected" (yellow) iff at least one of its linked erefs is connected.
	private isEcallConnected(linkedErefIds: number[]): boolean {
		return linkedErefIds.some((id) => this.isErefConnected(id))
	}

	// Re-derive an ecall's connectivity from its linked erefs' current statuses: yellow
	// when at least one linked eref is connected, gray otherwise. No-op when unchanged.
	private recomputeEcallConnectivity(annot: Core.Annotations.Annotation): void {
		if (!this.instance) return

		const { Annotations, annotationManager } = this.instance.Core
		const currentStatus = (annot.getCustomData("status") || "unconnected") as TAnnotationStatus
		const status: TAnnotationStatus = this.isEcallConnected(this.getLinkedErefIds(annot)) ? "connected" : "unconnected"
		if (status === currentStatus) return

		this.applyStyle(annot as Core.Annotations.RectangleAnnotation, status, Annotations)
		annot.setCustomData("status", status)

		const existingMetadata = this.metadataMap.get(annot.Id)
		if (existingMetadata) {
			this.metadataMap.set(annot.Id, { ...existingMetadata, status })
		}

		const oldCategory: TAnnotationCategory = `ecall:${currentStatus}`
		const newCategory: TAnnotationCategory = `ecall:${status}`
		this.annotationsByCategory.get(oldCategory)?.delete(annot)
		if (!this.annotationsByCategory.has(newCategory)) {
			this.annotationsByCategory.set(newCategory, new Set())
		}
		this.annotationsByCategory.get(newCategory)!.add(annot)

		annotationManager.redrawAnnotation(annot)
	}

	// Resync an ecall annotation's linked erefs from the source data and recompute its
	// connectivity color. Call after any flow that adds or removes an ecall↔eref link
	// (auto-connect / detach / remove), since the annotation layer is only fully
	// re-rendered on document load — cache changes alone don't repaint it.
	updateEcallLinks(ecallId: number, erefIds: number[]): void {
		if (!this.instance) return

		const annot = this.findEntityAnnotation("ecall", ecallId)
		if (!annot) return

		annot.setCustomData("linkedErefIds", JSON.stringify(erefIds))
		this.recomputeEcallConnectivity(annot)
	}

	// Convenience over updateEcallLinks: derives the linked eref ids straight from an
	// ecall's elevation_numbers and resyncs its highlight. Use this when a flow already
	// holds the updated ecall (e.g. after writing it to the cache).
	syncEcallConnectivity(ecall: TEcall): void {
		this.updateEcallLinks(ecall.id, this.extractLinkedErefIds(ecall))
	}

	updateErefEntityStatus(erefId: number, entityStatus: TErefStatus): void {
		if (!this.instance) return

		const annot = this.findEntityAnnotation("eref", erefId)
		if (!annot) return

		const { Annotations, annotationManager } = this.instance.Core
		const connectivity = (annot.getCustomData("status") || "unconnected") as TAnnotationStatus
		const fillState = resolveEntityFillState(entityStatus, connectivity)

		this.applyStyle(annot as Core.Annotations.RectangleAnnotation, fillState, Annotations)
		annot.setCustomData("entityStatus", entityStatus)
		annotationManager.redrawAnnotation(annot)
	}

	updateFurnitureEntityStatus(furnitureId: number, entityStatus: TFurnitureStatus): void {
		if (!this.instance) return

		const annot = this.findEntityAnnotation("furniture", furnitureId)
		if (!annot) return

		const { Annotations, annotationManager } = this.instance.Core
		const connectivity = (annot.getCustomData("status") || "connected") as TAnnotationStatus
		const fillState = resolveEntityFillState(entityStatus, connectivity)

		this.applyStyle(annot as Core.Annotations.RectangleAnnotation, fillState, Annotations)
		annot.setCustomData("entityStatus", entityStatus)
		annotationManager.redrawAnnotation(annot)
	}

	// Bring every furniture annotation's eref link in line with the quotation data. Connect / move /
	// detach / eref delete splice the cache without rebuilding annotations, and the REQ-455 framing
	// reads the group from these links — a stale one framed a detached object as part of its old eref.
	syncFurnitureErefLinks(pages: TPageElevationData[]): void {
		const annotByFurnitureId = new Map<number, Core.Annotations.Annotation>()
		for (const category of ["furniture:connected", "furniture:unconnected"] as TAnnotationCategory[]) {
			this.annotationsByCategory.get(category)?.forEach((annot) => {
				annotByFurnitureId.set(Number(annot.getCustomData("entityId")), annot)
			})
		}
		let changed = false
		for (const page of pages) {
			for (const furniture of page.furniture) {
				const annot = annotByFurnitureId.get(furniture.id)
				const metadata = annot ? this.metadataMap.get(annot.Id) : undefined
				if (!annot || !metadata) continue
				const erefId = furniture.eref_id ?? undefined
				if (metadata.erefId === erefId) continue
				annot.setCustomData("erefId", String(furniture.eref_id))
				this.metadataMap.set(annot.Id, { ...metadata, erefId })
				changed = true
			}
		}
		// The split pane's mirrors carry the same custom data.
		if (changed) this.scheduleSplitSync()
	}

	updateFurnitureAnnotation(furniture: TFurniture): void {
		if (!this.instance) return

		const annot = this.findEntityAnnotation("furniture", furniture.id)
		if (!annot) return

		const { annotationManager } = this.instance.Core
		const newLabel = this.buildFurnitureLabel(furniture)

		annot.setCustomData("category", furniture.category?.name ?? "")
		annot.setCustomData("erefId", String(furniture.eref_id))

		const existingMetadata = this.metadataMap.get(annot.Id)
		if (existingMetadata) {
			this.metadataMap.set(annot.Id, {
				...existingMetadata,
				erefId: furniture.eref_id ?? undefined,
			})
		}

		try {
			// Apryse-recommended way to update annotation contents — also refreshes the
			// associated popup and fires "annotationChanged"/"modify" so the UI re-renders.
			annotationManager.setNoteContents(annot, newLabel)
		} catch (error) {
			console.warn("Failed to update furniture annotation:", error)
		}
		// Label/eref changes don't pass through applyStyle — re-project the split pane's copy.
		this.scheduleSplitSync()
	}

	hasEntityAnnotation(entityType: "eref" | "ecall" | "furniture", entityId: number): boolean {
		return !!this.findEntityAnnotation(entityType, entityId)
	}

	// Refresh an eref's label + text metadata in place after a field edit that left its geometry and
	// links alone (the pop-out's peer sync, #1430). In place, so the selection outline survives.
	updateErefAnnotationFields(eref: TEref): void {
		if (!this.instance) return
		const annot = this.findEntityAnnotation("eref", eref.id)
		if (!annot) return

		this.applyErefTextData(annot, eref)
		const existingMetadata = this.metadataMap.get(annot.Id)
		if (existingMetadata) {
			this.metadataMap.set(annot.Id, {
				...existingMetadata,
				...this.erefTextMetadata(eref),
			})
		}
		try {
			this.instance.Core.annotationManager.setNoteContents(annot, this.buildErefLabel(eref.elevation_number))
		} catch (error) {
			console.warn("Failed to update eref annotation:", error)
		}
		this.scheduleSplitSync()
	}

	// The ecall counterpart of `updateErefAnnotationFields`; also resyncs its links/colour.
	updateEcallAnnotationFields(ecall: TEcall): void {
		if (!this.instance) return
		const annot = this.findEntityAnnotation("ecall", ecall.id)
		if (!annot) return

		this.applyEcallTextData(annot, ecall)
		const existingMetadata = this.metadataMap.get(annot.Id)
		if (existingMetadata) {
			this.metadataMap.set(annot.Id, {
				...existingMetadata,
				...this.ecallTextMetadata(ecall),
			})
		}
		try {
			this.instance.Core.annotationManager.setNoteContents(annot, this.buildEcallLabel(ecall))
		} catch (error) {
			console.warn("Failed to update ecall annotation:", error)
		}
		this.syncEcallConnectivity(ecall)
		this.scheduleSplitSync()
	}

	private findEntityAnnotation(
		entityType: "eref" | "ecall" | "furniture" | "page",
		entityId: number,
	): Core.Annotations.Annotation | undefined {
		const categories: TAnnotationCategory[] = [`${entityType}:connected`, `${entityType}:unconnected`]
		for (const category of categories) {
			const set = this.annotationsByCategory.get(category)
			if (!set) continue
			for (const annot of set) {
				if (annot.getCustomData("entityId") === String(entityId)) {
					return annot
				}
			}
		}
		return undefined
	}

	// Resolve an entity's annotation IN a given pane. The second pane holds MIRRORS of the primary
	// layer (own manager, own Ids), so anything that measures or scrolls that pane must work with
	// its copy — the primary annotation belongs to a different viewer and would be projected
	// through the wrong layout. REQ-399.
	private findPaneAnnotation(
		pane: TViewerPane,
		entityType: "eref" | "ecall" | "furniture" | "page",
		entityId: number,
	): Core.Annotations.Annotation | undefined {
		if (pane === "split") return this.mirroredByEntityKey.get(this.entityKey(entityType, entityId))
		return this.findEntityAnnotation(entityType, entityId)
	}

	// The eref's annotation in `pane` plus every furniture annotation connected to it (REQ-455). An
	// object can only be connected to an eref on the same page, so the group never spans pages.
	private resolveErefGroup(pane: TViewerPane, erefId: number): Core.Annotations.Annotation[] {
		const eref = this.findPaneAnnotation(pane, "eref", erefId)
		if (!eref) return []
		const furnitureIds = new Set<number>()
		this.metadataMap.forEach((metadata) => {
			if (metadata.entityType === "furniture" && metadata.erefId === erefId) furnitureIds.add(metadata.entityId)
		})
		const furniture: Core.Annotations.Annotation[] = []
		furnitureIds.forEach((id) => {
			const annot = this.findPaneAnnotation(pane, "furniture", id)
			if (annot) furniture.push(annot)
		})
		return [eref, ...furniture]
	}

	// Bring `annots` into view in `pane`. The first annotation is the target; the rest share its page.
	//   "group"   — fit the union in the visible area, anchored bottom-left (REQ-455 AC1-AC4, AC6)
	//   "single"  — fit one object centered with wide free space (REQ-455 AC5)
	//   "callout" — the pre-REQ-455 ecall behaviour: the target fills ~40% of the viewport
	// "group" / "single" leave zoom and scroll alone when the target is already framed (AC9).
	private async frameAnnotations(
		annots: Core.Annotations.Annotation[],
		mode: TFrameMode | "callout",
		pane: TViewerPane = "primary",
		// Caller-supplied ceiling. Only smart navigation passes one; see CENTER_MAX_ZOOM.
		maxZoom: number = CENTER_MAX_ZOOM,
	): Promise<void> {
		const annot = annots[0]
		if (!this.instance || !annot) return
		const group = annots.filter((a) => a.PageNumber === annot.PageNumber)

		// Claim the latest scroll. Any earlier call's pending awaits and deferred
		// verify timers bail when they see a newer generation, so rapidly selecting
		// another annotation (or stepping a jump session) can't get yanked back.
		const myGeneration = ++this.scrollGeneration[pane]
		this.framesInFlight[pane] += 1
		smartNavDebug("center:start", {
			pane,
			generation: myGeneration,
			annotId: annot.Id,
			page: annot.PageNumber,
			mode,
			groupSize: group.length,
			maxZoom,
		})

		const waitFrame = () =>
			new Promise<void>((resolve) => {
				let settled = false
				const finish = () => {
					if (settled) return
					settled = true
					resolve()
				}
				if (typeof requestAnimationFrame === "function") requestAnimationFrame(finish)
				setTimeout(finish, 32)
			})

		try {
			const documentViewer = this.paneViewer(pane)
			if (!documentViewer) return
			const scrollEl = documentViewer.getScrollViewElement() as HTMLElement | null
			if (!scrollEl) return

			// Content size in page units, from the rendered (backend) rects so it is right regardless of
			// page rotation. Only when EVERY member has one — mixing the two spaces would be wrong.
			const renderedBoxes = group.map((a) => {
				const r = this.metadataMap.get(a.Id)?.renderedRect
				return r ? { left: r.x0, top: r.y0, right: r.x1, bottom: r.y1 } : null
			})
			const contentBox = renderedBoxes.every((b) => b !== null)
				? unionBoxes(renderedBoxes as TFrameBox[])
				: unionBoxes(group.map((a) => ({ left: a.X, top: a.Y, right: a.X + a.Width, bottom: a.Y + a.Height })))
			const contentWidth = contentBox ? contentBox.right - contentBox.left : 0
			const contentHeight = contentBox ? contentBox.bottom - contentBox.top : 0

			const root = scrollEl.getRootNode?.() as ShadowRoot | Document | undefined
			// Both Apryse sidebars and the keyboard-shortcuts panel overlay the scroll view, so the
			// document area the user can actually see is the viewport minus whatever each covers (AC7).
			// Rect-based, so it stays correct per pane in split screen (AC8): a panel that doesn't
			// overlap THIS pane's scroll element yields a negative overlap and clamps to 0. The
			// middle-docked sidebar needs no entry — it is a flex SIBLING of the two panes
			// (core/middlePanel.ts), so it shrinks them instead of overlaying them.
			const measureInset = (selector: string, side: "left" | "right" | "bottom"): number => {
				const scRect = scrollEl.getBoundingClientRect?.()
				if (!scRect || !root?.querySelector) return 0
				const el = root.querySelector(selector) as HTMLElement | null
				const rect = el?.getBoundingClientRect()
				if (!rect || rect.width <= 0 || rect.height <= 0) return 0
				if (side === "bottom") return Math.max(0, Math.min(scrollEl.clientHeight, scRect.bottom - rect.top))
				const overlap = side === "left" ? rect.right - scRect.left : scRect.right - rect.left
				return Math.max(0, Math.min(scrollEl.clientWidth, overlap))
			}
			const readVisibleRect = (): TVisibleRect => {
				const left = measureInset('[data-element="elevation-references-panel"]', "left")
				const right = measureInset('[data-element="reference-panel"]', "right")
				const bottom = measureInset(`[${BOTTOM_DOCK_ATTRIBUTE}]`, "bottom")
				return {
					left,
					top: 0,
					width: Math.max(1, scrollEl.clientWidth - left - right),
					height: Math.max(1, scrollEl.clientHeight - bottom),
				}
			}
			const visibleKey = (v: TVisibleRect) => `${Math.round(v.left)}:${Math.round(v.width)}x${Math.round(v.height)}`

			const resolveTargetZoom = (visible: TVisibleRect): number => {
				if (mode === "callout") {
					// Measured against the whole viewport, as it always was — ecalls are out of REQ-455's scope.
					const targetFillRatio = 0.4
					const zoomX = contentWidth > 0 ? (scrollEl.clientWidth * targetFillRatio) / contentWidth : 1
					const zoomY = contentHeight > 0 ? (scrollEl.clientHeight * targetFillRatio) / contentHeight : 1
					return Math.max(CENTER_MIN_ZOOM, Math.min(maxZoom, Math.min(zoomX, zoomY)))
				}
				return resolveFitZoom({
					contentWidth,
					contentHeight,
					visible,
					padding: resolveFramePadding(mode, visible),
					maxZoom,
				})
			}

			// pageToWindow applies the same page transform as the rendering path, so it
			// works correctly on /Rotate'd pages — unlike displayPageLocation, which
			// interprets coords in a different space and lands at the wrong spot.
			type DisplayMode = {
				pageToWindow: (pt: { x: number; y: number }, pageNumber: number) => { x: number; y: number }
			}
			const dvAny = documentViewer as unknown as {
				getDisplayModeManager?: () => { getDisplayMode?: () => DisplayMode | undefined } | undefined
				displayPageLocation: typeof documentViewer.displayPageLocation
			}
			const displayMode = dvAny.getDisplayModeManager?.()?.getDisplayMode?.()

			if (!displayMode?.pageToWindow) {
				// Test-mock fallback: real Apryse always exposes pageToWindow.
				await (documentViewer.zoomTo(resolveTargetZoom(readVisibleRect())) as unknown as Promise<void>)
				if (this.scrollGeneration[pane] !== myGeneration) return
				const box = unionBoxes(
					group.map((a) => ({ left: a.X, top: a.Y, right: a.X + a.Width, bottom: a.Y + a.Height })),
				) as TFrameBox
				dvAny.displayPageLocation(annot.PageNumber, (box.left + box.right) / 2, (box.top + box.bottom) / 2, false)
				return
			}

			// Clamp scroll targets to the valid range so an annot near a document edge is
			// not scrolled past the content (which would clip it). scrollWidth/Height may
			// be absent in test mocks — fall back to no upper bound there.
			const clampScroll = (value: number, max: number): number => {
				const lower = Math.max(0, value)
				return Number.isFinite(max) ? Math.min(lower, max) : lower
			}

			// Project a bounding box through pageToWindow. Take the min/max of all four projected
			// corners for a correct axis-aligned screen box at any rotation — do NOT replace it with a
			// flat `containerLeft + X*zoom` mapping, which lands wrong on rotated pages.
			const projectCorners = (corners: Array<{ x: number; y: number }>): TFrameBox => {
				const pts = corners.map((pt) => displayMode.pageToWindow(pt, annot.PageNumber))
				return {
					left: Math.min(...pts.map((c) => c.x)),
					top: Math.min(...pts.map((c) => c.y)),
					right: Math.max(...pts.map((c) => c.x)),
					bottom: Math.max(...pts.map((c) => c.y)),
				}
			}
			// Project each annotation's OWN coords and take the union. applyRotation already baked the
			// page /Rotate into annot.X/Y (they're in the viewer's rendered space), and pageToWindow
			// maps that same space to the screen. Do NOT feed the un-rotated backend rect here:
			// pageToWindow does not re-apply /Rotate, so backend coords land at the wrong spot on
			// rotated pages (verified on a live 90° page — they pointed at the title block).
			const projectBoxViaApi = (): TFrameBox =>
				unionBoxes(
					group.map((a) =>
						projectCorners([
							{ x: a.X, y: a.Y },
							{ x: a.X + a.Width, y: a.Y },
							{ x: a.X, y: a.Y + a.Height },
							{ x: a.X + a.Width, y: a.Y + a.Height },
						]),
					),
				) as TFrameBox

			// In v11.9 the display-mode's layout model can be off from where the page is
			// ACTUALLY rendered by a constant translation (its page x-offset was ~264px
			// wrong here); pageToWindow + windowToPage stay self-consistent yet both point
			// at the wrong screen position. The error is a pure offset (zoom/rotation are
			// applied correctly), so measure the page's true origin from its container
			// element and return the delta to add on top of pageToWindow output. Works for
			// any rotation; null when the element can't be found (e.g. test mocks).
			const computeModelCorrection = (): { x: number; y: number } | null => {
				const doc = documentViewer.getDocument?.()
				const viewerEl = (
					documentViewer as unknown as { getViewerElement?: () => HTMLElement | null }
				).getViewerElement?.()
				// `#pageContainer{n}` ids are NOT unique across panes — each pane nests its own set
				// under its own `#DocumentContainer{key}` (see getPaneSearchRoot). So the pane's own
				// viewer element is the only safe place to look, and falling back to the shared
				// shadow root is only safe while there is ONE pane: `querySelector` returns the
				// FIRST match, which in split screen is the LEFT pane's container whichever pane we
				// are centring. Measuring the wrong pane's page yields a correction that is wrong
				// yet reports `corrected: true`, which is what made a long backward jump in the
				// split pane drift a page per pass until it clamped at the end of the document
				// (#1311). Without the pane's element and with two panes live, stay uncorrected —
				// the settle loop is built to keep going until the page is measurable.
				const paneScopedRoot = viewerEl ?? (this.splitViewer ? null : root)
				const pageEl = paneScopedRoot?.querySelector?.(`#pageContainer${annot.PageNumber}`) as HTMLElement | null
				const pageRect = pageEl?.getBoundingClientRect()
				const scRect = scrollEl.getBoundingClientRect?.()
				const info = doc?.getPageInfo?.(annot.PageNumber)
				if (!pageRect || !scRect || pageRect.width <= 0 || !info) return null
				// The page box must be in the SAME space as the annot.X/Y coords we project —
				// the viewer's rendered space. On a 90°/270° page that space measures
				// info.height wide × info.width tall, but getPageInfo reports the dims the
				// other way round, so swap them. Using the unswapped getPageInfo dims projects
				// the wrong page corner → a wrong, over-large correction that throws the scroll
				// far off horizontally (seen live: correction.x was -2856 instead of -264).
				const rotation =
					(doc as unknown as { getPageRotation?: (p: number) => number })?.getPageRotation?.(annot.PageNumber) ?? 0
				const swapDims = rotation === 90 || rotation === 270
				const pageW = swapDims ? info.height : info.width
				const pageH = swapDims ? info.width : info.height
				// The page's rendered (rotation-aware) bounding box, both as the model sees
				// it and as the DOM actually places it. Both are axis-aligned screen boxes
				// of the same rendered page, so their min-corners correspond.
				const modelPageBox = projectCorners([
					{ x: 0, y: 0 },
					{ x: pageW, y: 0 },
					{ x: 0, y: pageH },
					{ x: pageW, y: pageH },
				])
				const domLeft = pageRect.left - scRect.left + scrollEl.scrollLeft
				const domTop = pageRect.top - scRect.top + scrollEl.scrollTop
				return { x: domLeft - modelPageBox.left, y: domTop - modelPageBox.top }
			}

			// The target's box in scroll-CONTENT coordinates, DOM-corrected when its page is laid out.
			const measureBox = (): { box: TFrameBox; corrected: boolean } => {
				const box = projectBoxViaApi()
				const correction = computeModelCorrection()
				if (correction) {
					box.left += correction.x
					box.right += correction.x
					box.top += correction.y
					box.bottom += correction.y
				}
				return { box, corrected: correction !== null }
			}

			if (mode !== "callout") {
				// The click that navigates here usually also opens a sidebar, which lands a frame or more
				// later. Fitting before it does is what made the elevation come out too big, so wait
				// (bounded) until the visible area stops changing.
				let prevKey = visibleKey(readVisibleRect())
				for (let frame = 0; frame < 8; frame++) {
					await waitFrame()
					if (this.scrollGeneration[pane] !== myGeneration) return
					const key = visibleKey(readVisibleRect())
					if (key === prevKey) break
					prevKey = key
				}

				// AC9: already framed on screen → leave zoom and scroll as they are.
				if (documentViewer.getCurrentPage?.() === annot.PageNumber) {
					const visible = readVisibleRect()
					const { box, corrected } = measureBox()
					const viewportBox = {
						left: box.left - scrollEl.scrollLeft,
						right: box.right - scrollEl.scrollLeft,
						top: box.top - scrollEl.scrollTop,
						bottom: box.bottom - scrollEl.scrollTop,
					}
					const zoom = documentViewer.getZoomLevel?.() ?? 0
					if (corrected && isAlreadyFramed({ box: viewportBox, visible, zoom, fitZoom: resolveTargetZoom(visible) })) {
						smartNavDebug("center:alreadyFramed", { pane, generation: myGeneration })
						return
					}
				}
			}

			let appliedZoom = resolveTargetZoom(readVisibleRect())
			// zoomTo is typed `void` but returns a Promise in Apryse v11. Cast so the await
			// isn't flagged as a no-op; awaiting it lets pageToWindow read post-zoom layout.
			await (documentViewer.zoomTo(appliedZoom) as unknown as Promise<void>)
			// Get the target PAGE on screen by number before fine-tuning by geometry. Apryse owns
			// page virtualization, so this is the only reliable way to cross a long distance: the
			// geometry below can only measure a page that is actually laid out, and until it is, the
			// model's uncorrected projection is not even reliably signed — measured on a 29-page
			// BACKWARD jump, it pointed forward and the settle loop walked to the end of the document
			// (#1311). Skipped when the page is already current, so a short hop keeps its single jump.
			const pageBefore = documentViewer.getCurrentPage?.()
			if (pageBefore !== annot.PageNumber) {
				documentViewer.setCurrentPage?.(annot.PageNumber, true)
				// setCurrentPage returns before its scroll lands — measured, the page state read back as
				// the target while scrollTop was still deep in the OLD page's region, so the first geometry
				// pass anchored on the wrong page. Give it a frame.
				await waitFrame()
			}
			smartNavDebug("center:page", {
				pane,
				generation: myGeneration,
				targetZoom: appliedZoom,
				pageBefore,
				pageAfter: documentViewer.getCurrentPage?.(),
				scrollTop: scrollEl.scrollTop,
			})
			// zoomTo resolves before the viewer finishes re-laying-out the scroll content,
			// so pageToWindow / scrollWidth can still report pre-zoom values. Wait one frame
			// for the layout to settle; the timeout guarantees resolution in environments
			// where requestAnimationFrame never fires (e.g. jsdom in tests).
			await waitFrame()
			// A newer scroll superseded this one while we awaited the zoom/settle.
			if (this.scrollGeneration[pane] !== myGeneration) return

			// `corrected` reports whether the DOM-measured page correction was applied this
			// pass — i.e. the target page container exists. The settle loop only treats a
			// target as final once it's corrected, so it can't lock onto an uncorrected
			// position while the page is still rendering in.
			const computeTarget = (): { left: number; top: number; corrected: boolean } => {
				// Recompute everything from the LIVE layout each pass — panel insets,
				// viewport size and box position all keep changing while the viewer
				// finishes its post-zoom relayout, so values captured once go stale.
				const visible = readVisibleRect()
				const { box, corrected } = measureBox()
				const liveMaxLeft = Number.isFinite(scrollEl.scrollWidth)
					? Math.max(0, scrollEl.scrollWidth - scrollEl.clientWidth)
					: Number.POSITIVE_INFINITY
				const liveMaxTop = Number.isFinite(scrollEl.scrollHeight)
					? Math.max(0, scrollEl.scrollHeight - scrollEl.clientHeight)
					: Number.POSITIVE_INFINITY
				const scrollMode = mode === "group" ? "group" : "single"
				const target = resolveFrameScroll({
					box,
					visible,
					mode: scrollMode,
					padding: resolveFramePadding(scrollMode, visible),
				})
				return {
					left: clampScroll(target.left, liveMaxLeft),
					top: clampScroll(target.top, liveMaxTop),
					corrected,
				}
			}
			// Apply, then re-measure and re-apply until the scroll actually LANDS on a target
			// that has stopped moving. On the FIRST selection the target page is usually not
			// rendered yet (Apryse virtualizes far pages, and the click also opens a panel /
			// resizes the viewport), so the page container is absent and the model-offset
			// correction can't run — the scroll lands ~one offset short ("half hidden"). The
			// first pass brings the page into view; a later pass measures it and corrects, so a
			// single click lands right instead of needing a second. Assign scrollLeft/scrollTop
			// directly — scrollTo({}) is not reliably honored by the viewer's scroll element.
			//
			// Two things make this a loop with a TIME budget rather than a small fixed pass
			// count, both measured on #1311's smart navigation:
			//
			//   • ASSIGNING A FAR scrollTop IS CLAMPED BY THE BROWSER, because the content that
			//     far away is not laid out yet — Apryse renders one more page per frame, so a
			//     long jump advances exactly ONE PAGE PER PASS however far the target is. A cap
			//     of 6 passes silently stopped ~24 pages short (page 38 instead of page 14),
			//     leaving the pane parked on the wrong page with no error. `landed` is what
			//     tells progress apart from arrival: a clamped assignment is progress.
			//   • A PANE BEING RESIZED MID-FLIGHT (the Secondary pane's sidebar opening) changes
			//     the visible area, and every part of the target is derived from it. A target
			//     that "stopped moving" across a resize has not converged, so a resize restarts
			//     the comparison — and re-fits the zoom when the fit moved (REQ-455).
			//
			// The budget is what guarantees termination when the target can never be reached
			// (bad coordinates, a page that never renders) instead of spinning.
			const SETTLE_BUDGET_MS = 2000
			const MAX_SETTLE_PASSES = 60
			const MAX_REFITS = 3
			// Without the DOM correction there is no way to know the target is final, so an
			// uncorrected target has to prove itself by holding still while landed instead.
			const STABLE_PASSES_WITHOUT_CORRECTION = 3
			const settleDeadline = Date.now() + SETTLE_BUDGET_MS
			const readViewportKey = () => `${scrollEl.clientWidth}x${scrollEl.clientHeight}|${visibleKey(readVisibleRect())}`

			let target = { left: 0, top: 0, corrected: false }
			let prevTarget: { left: number; top: number } | null = null
			let stablePasses = 0
			let refits = 0
			let viewportKey = readViewportKey()
			for (let pass = 0; pass < MAX_SETTLE_PASSES; pass++) {
				// A newer selection superseded this one — stop so we don't fight it.
				if (this.scrollGeneration[pane] !== myGeneration) {
					smartNavDebug("center:superseded", { pane, generation: myGeneration, pass })
					return
				}
				target = computeTarget()
				scrollEl.scrollLeft = target.left
				scrollEl.scrollTop = target.top
				smartNavDebug("center:pass", {
					pane,
					generation: myGeneration,
					pass,
					wantTop: Math.round(target.top),
					gotTop: Math.round(scrollEl.scrollTop),
					corrected: target.corrected,
					width: scrollEl.clientWidth,
					page: documentViewer.getCurrentPage?.(),
				})
				// Did the assignment stick? If the browser clamped it, the content is still
				// paging in and this pass was progress, not arrival.
				const landed =
					Math.abs(scrollEl.scrollTop - target.top) <= 1 && Math.abs(scrollEl.scrollLeft - target.left) <= 1
				const stable =
					prevTarget !== null &&
					Math.abs(target.left - prevTarget.left) <= 1 &&
					Math.abs(target.top - prevTarget.top) <= 1
				const nextViewportKey = readViewportKey()
				const resized = nextViewportKey !== viewportKey
				viewportKey = nextViewportKey

				if (resized) {
					// The viewport the target was computed against is gone; nothing before this
					// pass is comparable.
					prevTarget = null
					stablePasses = 0
					const refit = resolveTargetZoom(readVisibleRect())
					if (mode !== "callout" && refits < MAX_REFITS && Math.abs(refit - appliedZoom) / appliedZoom > 0.03) {
						refits += 1
						appliedZoom = refit
						await (documentViewer.zoomTo(refit) as unknown as Promise<void>)
					}
				} else {
					stablePasses = landed && stable ? stablePasses + 1 : 0
					// Done once the correction has been applied (page container exists), the
					// assignment stuck, and two passes in a row agree.
					if (target.corrected && stablePasses >= 1) break
					if (stablePasses >= STABLE_PASSES_WITHOUT_CORRECTION) break
					prevTarget = target
				}

				if (Date.now() >= settleDeadline) break
				await waitFrame()
			}
		} catch (error) {
			console.warn("Failed to center annotation:", error)
		} finally {
			this.framesInFlight[pane] -= 1
		}
	}

	// Map a screen point to a point in the backend's furniture-coordinate space — the same
	// space `TFurniture.coordinates` and the duplicate endpoint use. Returns the 0-based
	// `pageNumber` plus `x`/`y`, or null when the point isn't over a rendered page. Accepts
	// client coords so it serves both right-click events and the keyboard-paste path.
	//
	// Crucially, backend furniture coords are in the page's RENDERED (rotation-applied)
	// orientation — the same orientation as the `#pageContainer{n}` DOM box and `getPageInfo`
	// (`applyRotation` converts these into Apryse's un-rotated annotation space only for
	// drawing). So this mapping is a direct fractional scale of the DOM rect into page units;
	// it must NOT re-apply / invert the page /Rotate (verified on a live 90° page: doing so
	// landed the paste far from the cursor; the direct scale matches exactly).
	//
	// We measure the real DOM rect (not windowToPage / getZoomLevel) to sidestep the known
	// layout-model offset bug.
	//
	// Split screen (REQ-399): `pageContainer{n}` ids are NOT unique across panes, so the search is
	// scoped to the requested pane's own viewer element. Defaults to the pane the user last
	// interacted with; in single-viewer mode that is always "primary" (unchanged behavior).
	getPagePointFromClient(
		clientX: number,
		clientY: number,
		pane: TViewerPane = this.getActivePane(),
	): { pageNumber: number; x: number; y: number } | null {
		if (!this.instance) return null
		const documentViewer = this.paneViewer(pane)
		if (!documentViewer) return null
		const searchRoot = this.getPaneSearchRoot(documentViewer)
		if (!searchRoot?.querySelectorAll) return null

		// Find the page container the cursor is inside. Ids are `pageContainer{viewerPageNumber}`.
		let hitPage: number | null = null
		let hitRect: DOMRect | null = null
		const containers = searchRoot.querySelectorAll<HTMLElement>('[id^="pageContainer"]')
		for (const el of Array.from(containers)) {
			const match = /^pageContainer(\d+)$/.exec(el.id)
			if (!match) continue
			const rect = el.getBoundingClientRect()
			if (rect.width <= 0 || rect.height <= 0) continue
			if (clientX >= rect.left && clientX <= rect.right && clientY >= rect.top && clientY <= rect.bottom) {
				hitPage = Number(match[1])
				hitRect = rect
				break
			}
		}
		if (hitPage == null || !hitRect) return null

		const doc = documentViewer.getDocument?.()
		if (!doc) return null
		const info = doc.getPageInfo?.(hitPage)
		if (!info || info.width <= 0 || info.height <= 0) return null

		// getPageInfo is rotation-aware (rendered dims) and matches the DOM rect orientation, so
		// the cursor fraction maps straight into backend page units — no rotation/swap.
		const x = ((clientX - hitRect.left) / hitRect.width) * info.width
		const y = ((clientY - hitRect.top) / hitRect.height) * info.height

		return { pageNumber: hitPage - 1, x, y }
	}

	// Map a viewport-client rectangle (the area the user dragged out) onto the page it covers and
	// into that page's backend coordinate space — the input side of `addErefAnnotation` / the create
	// endpoint (REQ-292). The page is resolved from the rect's CENTER, and the corners are clamped to
	// that page's box, so a drag that overshoots the sheet still yields a rect fully inside it.
	//
	// The mapping is the same direct fractional scale `getPagePointFromClient` uses (and
	// `getSelectionScreenBounds` inverts): backend coords live in the page's RENDERED orientation,
	// which is what the `#pageContainer{n}` DOM box measures. No /Rotate inversion here — that
	// belongs on the annotation side, where `applyRotation` converts backend coords into Apryse's
	// un-rotated annotation space for drawing.
	//
	// Returns null when the rect's center isn't over a measurable page.
	getBackendRectFromClientRect(
		rect: { left: number; top: number; right: number; bottom: number },
		pane: TViewerPane = this.getActivePane(),
	): { pageNumber: number; coordinates: TCoordinates } | null {
		if (!this.instance) return null
		const documentViewer = this.paneViewer(pane)
		if (!documentViewer) return null
		const searchRoot = this.getPaneSearchRoot(documentViewer)
		if (!searchRoot?.querySelectorAll) return null

		const centerX = (rect.left + rect.right) / 2
		const centerY = (rect.top + rect.bottom) / 2

		let hitPage: number | null = null
		let hitRect: DOMRect | null = null
		const containers = searchRoot.querySelectorAll<HTMLElement>('[id^="pageContainer"]')
		for (const el of Array.from(containers)) {
			const match = /^pageContainer(\d+)$/.exec(el.id)
			if (!match) continue
			const box = el.getBoundingClientRect()
			if (box.width <= 0 || box.height <= 0) continue
			if (centerX >= box.left && centerX <= box.right && centerY >= box.top && centerY <= box.bottom) {
				hitPage = Number(match[1])
				hitRect = box
				break
			}
		}
		if (hitPage == null || !hitRect) return null

		const doc = documentViewer.getDocument?.()
		const info = doc?.getPageInfo?.(hitPage)
		if (!info || info.width <= 0 || info.height <= 0) return null

		const pageBox = hitRect
		const toPageX = (clientX: number) =>
			Math.min(info.width, Math.max(0, ((clientX - pageBox.left) / pageBox.width) * info.width))
		const toPageY = (clientY: number) =>
			Math.min(info.height, Math.max(0, ((clientY - pageBox.top) / pageBox.height) * info.height))

		return {
			pageNumber: hitPage - 1,
			coordinates: this.normalizeCoords({
				x0: toPageX(rect.left),
				y0: toPageY(rect.top),
				x1: toPageX(rect.right),
				y1: toPageY(rect.bottom),
			}),
		}
	}

	// Inverse of `getBackendRectFromClientRect`: where a backend rect on `pageNumber` currently sits
	// on screen. The elevation-area draft box (REQ-292) is anchored to the page, not the viewport, so
	// it re-derives its client box from this whenever the pane scrolls, zooms or re-lays-out.
	// Returns null while the page isn't rendered/measurable (e.g. scrolled far out of view).
	getClientRectFromBackendRect(
		pageNumber: number,
		coordinates: TCoordinates,
		pane: TViewerPane = this.getActivePane(),
	): { left: number; top: number; right: number; bottom: number } | null {
		if (!this.instance) return null
		const documentViewer = this.paneViewer(pane)
		if (!documentViewer) return null
		const searchRoot = this.getPaneSearchRoot(documentViewer)
		const container = (searchRoot as ParentNode | null)?.querySelector?.<HTMLElement>(`#pageContainer${pageNumber + 1}`)
		if (!container) return null
		const box = container.getBoundingClientRect()
		if (box.width <= 0 || box.height <= 0) return null

		const info = documentViewer.getDocument?.()?.getPageInfo?.(pageNumber + 1)
		if (!info || info.width <= 0 || info.height <= 0) return null

		return {
			left: box.left + (coordinates.x0 / info.width) * box.width,
			top: box.top + (coordinates.y0 / info.height) * box.height,
			right: box.left + (coordinates.x1 / info.width) * box.width,
			bottom: box.top + (coordinates.y1 / info.height) * box.height,
		}
	}

	// Rendered (rotation-aware) dimensions of a page, in the backend furniture-coordinate
	// space — the same space `TFurniture.coordinates` and the duplicate endpoint use. Accepts
	// the backend's 0-based `pageNumber`; returns null when the page can't be measured. Used by
	// the Duplicate flow to know the right page edge for collision avoidance.
	getPageDimensions(pageNumber: number): { width: number; height: number } | null {
		if (!this.instance) return null
		const doc = this.instance.Core.documentViewer.getDocument?.()
		if (!doc) return null
		const info = doc.getPageInfo?.(pageNumber + 1)
		if (!info || info.width <= 0 || info.height <= 0) return null
		return { width: info.width, height: info.height }
	}

	// Screen (viewport-client) bounding box that unions the currently-selected highlights of the
	// given entity types (furniture by default; the eref toolbar passes ["eref"]) — used to anchor
	// the floating selection toolbar above the selection. Maps each entity's rendered backend rect
	// into the page's live DOM rect the same way `getPagePointFromClient` does in reverse (a direct
	// fractional scale that sidesteps the Apryse v11.9 layout-model offset bug). Returns null when
	// nothing is measurable yet.
	//
	// Split screen (REQ-399): measured in the requested pane (default: the last-interacted one), so
	// the toolbar floats over the highlight the user actually clicked. Both panes hold a selected
	// copy of the same entity (see syncSelectionToSplitPane), so either measures correctly.
	getSelectionScreenBounds(
		entityTypes: TAnnotationType[] = ["furniture"],
		pane: TViewerPane = this.getActivePane(),
	): {
		left: number
		top: number
		right: number
		bottom: number
		width: number
		height: number
	} | null {
		if (!this.instance) return null
		const annotationManager = this.paneManager(pane)
		if (!annotationManager) return null
		const context = this.getMeasureContext(pane)
		if (!context) return null

		let left = Number.POSITIVE_INFINITY
		let top = Number.POSITIVE_INFINITY
		let right = Number.NEGATIVE_INFINITY
		let bottom = Number.NEGATIVE_INFINITY
		let found = false
		const pageCache = new Map<number, { left: number; top: number; sx: number; sy: number } | null>()

		for (const annot of annotationManager.getSelectedAnnotations()) {
			const meta = this.metadataMap.get(annot.Id)
			if (!meta || !entityTypes.includes(meta.entityType)) continue
			const rect = this.measureEntityClientRect(annot, meta, context, pageCache)
			if (!rect) continue
			left = Math.min(left, rect.left)
			right = Math.max(right, rect.right)
			top = Math.min(top, rect.top)
			bottom = Math.max(bottom, rect.bottom)
			found = true
		}

		if (!found) return null
		return { left, top, right, bottom, width: right - left, height: bottom - top }
	}

	// The document + DOM search root every screen measurement needs. Extracted so the selection
	// bounds and the marquee hit-test resolve them identically. `pane` defaults to "primary" — the
	// marquee is a left-screen affordance; only the selection bounds ask for another pane (REQ-399).
	private getMeasureContext(pane: TViewerPane = "primary"): { searchRoot: ParentNode; doc: Core.Document } | null {
		if (!this.instance) return null
		const documentViewer = this.paneViewer(pane)
		if (!documentViewer) return null
		const doc = documentViewer.getDocument?.()
		const searchRoot = this.getPaneSearchRoot(documentViewer)
		if (!doc || !searchRoot?.querySelector) return null
		return { searchRoot, doc }
	}

	// Where a page is on screen and how backend page units scale into client px. `viewerPageNumber`
	// is 1-based: DOM containers are `pageContainer{n}` and `getPageInfo` is 1-based and
	// rotation-aware — matching the rendered rect's orientation — so the mapping is a direct
	// fractional scale (the same math `getPagePointFromClient` does in reverse, sidestepping the
	// Apryse layout-model offset bug).
	//
	// Results are memoized in the caller-supplied `cache` for the duration of one measurement pass:
	// `getBoundingClientRect` forces a layout, so resolving this per annotation made the
	// connect-objects marquee do hundreds of layout reads per frame.
	private getPageGeometry(
		viewerPageNumber: number,
		context: { searchRoot: ParentNode; doc: Core.Document },
		cache: Map<number, { left: number; top: number; sx: number; sy: number } | null>,
	): { left: number; top: number; sx: number; sy: number } | null {
		const cached = cache.get(viewerPageNumber)
		if (cached !== undefined) return cached
		const pageEl = context.searchRoot.querySelector(`#pageContainer${viewerPageNumber}`) as HTMLElement | null
		const pageRect = pageEl?.getBoundingClientRect()
		const info = context.doc.getPageInfo?.(viewerPageNumber)
		const geometry =
			pageRect && pageRect.width > 0 && pageRect.height > 0 && info && info.width > 0 && info.height > 0
				? { left: pageRect.left, top: pageRect.top, sx: pageRect.width / info.width, sy: pageRect.height / info.height }
				: null
		cache.set(viewerPageNumber, geometry)
		return geometry
	}

	// Client-space rect of one entity highlight. Null when its page or its rect isn't measurable yet.
	private measureEntityClientRect(
		annot: Core.Annotations.Annotation,
		meta: TAnnotationMetadata,
		context: { searchRoot: ParentNode; doc: Core.Document },
		cache: Map<number, { left: number; top: number; sx: number; sy: number } | null>,
	): { left: number; top: number; right: number; bottom: number } | null {
		if (!meta.renderedRect) return null
		const page = this.getPageGeometry(annot.PageNumber, context, cache)
		if (!page) return null
		const rect = meta.renderedRect
		return {
			left: page.left + rect.x0 * page.sx,
			top: page.top + rect.y0 * page.sy,
			right: page.left + rect.x1 * page.sx,
			bottom: page.top + rect.y1 * page.sy,
		}
	}

	// Furniture ids whose highlight INTERSECTS a client-space rect on `pageNumber` (the backend's
	// 0-based page number) — the drag-to-select marquee's hit-test (REQ-372). Intersection, not
	// containment, so partially-covered objects are caught the way the design shows.
	getFurnitureIdsInClientRect(
		rect: { left: number; top: number; right: number; bottom: number },
		pageNumber: number,
	): number[] {
		const context = this.getMeasureContext()
		if (!context) return []
		const ids: number[] = []
		// One geometry lookup for the whole pass (every candidate is on the same page).
		const pageCache = new Map<number, { left: number; top: number; sx: number; sy: number } | null>()
		for (const category of ["furniture:connected", "furniture:unconnected"] as TAnnotationCategory[]) {
			const set = this.annotationsByCategory.get(category)
			if (!set) continue
			for (const annot of set) {
				const meta = this.metadataMap.get(annot.Id)
				if (!meta || meta.pageNumber !== pageNumber) continue
				const box = this.measureEntityClientRect(annot, meta, context, pageCache)
				if (!box) continue
				const intersects =
					box.left <= rect.right && box.right >= rect.left && box.top <= rect.bottom && box.bottom >= rect.top
				if (intersects) ids.push(meta.entityId)
			}
		}
		return ids
	}

	// Metadata of the entity highlight under a mouse event, WITHOUT selecting it. Lets an
	// interaction hook (e.g. connect-objects click-toggle) decide what a click landed on.
	getEntityAtMouseEvent(event: MouseEvent): TAnnotationMetadata | undefined {
		if (!this.instance) return undefined
		const annot = this.instance.Core.annotationManager.getAnnotationByMouseEvent(event)
		return annot ? this.metadataMap.get(annot.Id) : undefined
	}

	// Bring the selection to exactly these furniture highlights (ids with no annotation are skipped),
	// touching ONLY what changed.
	//
	// The diff is the point. Apryse's `selectAnnotations` is additive, so the obvious implementation
	// is `deselectAllAnnotations()` then re-select the whole set — but that makes every already-picked
	// object drop and re-acquire its selection model on each marquee step, so the cost grows with the
	// selection and the drag visibly stutters each time a new object is covered. Selecting just the
	// arrivals and deselecting just the departures keeps each step O(changed).
	updateFurnitureSelection(furnitureIds: number[]): void {
		if (!this.instance) return
		const { annotationManager } = this.instance.Core
		const wanted = new Set(furnitureIds)

		// Anything selected that shouldn't be — including a stray eref/ecall — leaves the selection.
		const toDeselect: Core.Annotations.Annotation[] = []
		const alreadySelected = new Set<number>()
		for (const annot of annotationManager.getSelectedAnnotations()) {
			const meta = this.metadataMap.get(annot.Id)
			if (meta?.entityType === "furniture" && wanted.has(meta.entityId)) {
				alreadySelected.add(meta.entityId)
				continue
			}
			toDeselect.push(annot)
		}

		const toSelect: Core.Annotations.Annotation[] = []
		if (alreadySelected.size < wanted.size) {
			for (const category of ["furniture:connected", "furniture:unconnected"] as TAnnotationCategory[]) {
				const set = this.annotationsByCategory.get(category)
				if (!set) continue
				for (const annot of set) {
					const meta = this.metadataMap.get(annot.Id)
					if (!meta || !wanted.has(meta.entityId) || alreadySelected.has(meta.entityId)) continue
					toSelect.push(annot)
				}
			}
		}

		if (toDeselect.length > 0) annotationManager.deselectAnnotations(toDeselect)
		if (toSelect.length > 0) annotationManager.selectAnnotations(toSelect)
	}

	// Enter / leave the connect-objects picking presentation (REQ-372). Two things travel together,
	// so they are one call: highlights stop being draggable out from under the marquee, and the
	// selection takes the design's blue outline + control points.
	//
	// `endObjectPicking` must undo BOTH — the appearance lives on Apryse statics, so leaving it
	// applied would restyle every ordinary selection in the viewer, not just this mode's.
	beginObjectPicking(): void {
		this.setEntityMoveLocked(true)
		this.setPickingSelectionAppearance(true)
	}

	endObjectPicking(): void {
		this.setEntityMoveLocked(false)
		this.setPickingSelectionAppearance(false)
	}

	// Block drag-to-move on every entity highlight (REQ-350 coordinate editing) so a drag that starts
	// over an object draws the marquee instead. `NoResize` is deliberately NOT touched: Apryse only
	// builds a selection's control handles when `!annot.NoResize`, so setting it would silently strip
	// the corner circles off every picked object. The capture-phase mousedown swallow in
	// useErefConnectObjects is what actually keeps the edit tool out of the gesture (a resize
	// included); this is the belt to that braces.
	private setEntityMoveLocked(locked: boolean): void {
		for (const set of this.annotationsByCategory.values()) {
			for (const annot of set) {
				const rect = annot as Core.Annotations.RectangleAnnotation
				rect.NoMove = locked
			}
		}
	}

	// Selected-highlight appearance while picking, from Figma ("Detected / Selected=Yes /
	// Highlighted=Yes"): a 2px --clr-main outline with filled --clr-main control points. Apryse
	// already draws those points as circles — only its default medium-blue rgb(49,131,200) differs.
	//
	// Enabling snapshots Apryse's own values first so leaving restores them exactly. The
	// no-permission outline is included because Apryse swaps to it (bright RED by default) for any
	// annotation its selection model reports as unmodifiable; both are set to the same blue so the
	// outline can never change color mid-mode.
	//
	// This is Apryse configuration, not app state, so a missing/renamed static must not break the
	// mode — the viewer just keeps Apryse's own selection look.
	private setPickingSelectionAppearance(enabled: boolean): void {
		if (!this.instance) return
		try {
			const { SelectionModel, ControlHandle, Color } = this.instance.Core.Annotations
			if (!SelectionModel || !ControlHandle || !Color) return
			if (enabled) {
				// Already applied — don't overwrite the snapshot with our own values.
				if (this.savedSelectionAppearance) return
				this.savedSelectionAppearance = {
					outlineThickness: SelectionModel.selectionOutlineThickness,
					outlineColor: SelectionModel.defaultSelectionOutlineColor,
					noPermissionOutlineColor: SelectionModel.defaultNoPermissionSelectionOutlineColor,
					useDashedLine: SelectionModel.useDashedLine,
					handleColor: ControlHandle.color,
					handleOutlineColor: ControlHandle.outlineColor,
				}
				const selectionBlue = new Color(...SELECTION_OUTLINE_COLOR)
				SelectionModel.selectionOutlineThickness = SELECTION_OUTLINE_THICKNESS
				SelectionModel.defaultSelectionOutlineColor = selectionBlue
				SelectionModel.defaultNoPermissionSelectionOutlineColor = selectionBlue
				SelectionModel.useDashedLine = false
				ControlHandle.color = selectionBlue
				ControlHandle.outlineColor = selectionBlue
				return
			}
			const saved = this.savedSelectionAppearance
			if (!saved) return
			this.savedSelectionAppearance = null
			SelectionModel.selectionOutlineThickness = saved.outlineThickness
			SelectionModel.defaultSelectionOutlineColor = saved.outlineColor
			SelectionModel.defaultNoPermissionSelectionOutlineColor = saved.noPermissionOutlineColor
			SelectionModel.useDashedLine = saved.useDashedLine
			ControlHandle.color = saved.handleColor
			ControlHandle.outlineColor = saved.handleOutlineColor
		} catch (error) {
			console.warn("Could not apply the picking selection appearance:", error)
		}
	}

	// Delete furniture highlights by entity id without a full re-render (mirror of the
	// batch-delete flow). Also clears them from the category sets and metadata map so the
	// selection/geometry bookkeeping stays in sync.
	removeFurnitureAnnotations(ids: number[]): void {
		this.removeEntityAnnotations("furniture", ids)
	}

	// Delete a deleted eref's highlight (REQ-376). The furniture it held is only detached by the
	// backend, not deleted, so their highlights stay on the canvas untouched.
	removeErefAnnotation(erefId: number): void {
		this.removeEntityAnnotations("eref", [erefId])
	}

	// Delete a deleted ecall's highlight (REQ-297). The erefs its elevations pointed at are only
	// detached by the backend, so their highlights stay — and their color is driven by their own
	// furniture connectivity, not by ecall links, so nothing needs repainting either.
	removeEcallAnnotation(ecallId: number): void {
		this.removeEntityAnnotations("ecall", [ecallId])
	}

	private removeEntityAnnotations(entityType: TAnnotationType, ids: number[]): void {
		if (!this.instance || ids.length === 0) return
		const { annotationManager } = this.instance.Core
		// A deleted object takes its info label with it (REQ-401). Erefs carry no labels, and the
		// furniture an eref held is only detached — never deleted — so its labels stay.
		if (entityType === "furniture") this.deleteFurnitureLabels(ids)
		const idSet = new Set(ids.map(String))
		const toDelete: Core.Annotations.Annotation[] = []
		const categories: TAnnotationCategory[] = [`${entityType}:connected`, `${entityType}:unconnected`]
		for (const category of categories) {
			const set = this.annotationsByCategory.get(category)
			if (!set) continue
			for (const annot of [...set]) {
				if (idSet.has(annot.getCustomData("entityId"))) {
					set.delete(annot)
					this.metadataMap.delete(annot.Id)
					toDelete.push(annot)
				}
			}
		}
		if (toDelete.length === 0) return
		// Clear NoDelete/NoMove/NoRotate before deleting: entity annots are created read-only
		// (setReadOnly), and in this Apryse version `deleteAnnotations({ force: true })` alone does
		// NOT remove a NoDelete annotation — the flag has to be cleared on the annot first, otherwise
		// the highlight lingers on the canvas after a successful server delete.
		for (const annot of toDelete) {
			annot.NoDelete = false
			annot.NoMove = false
			annot.NoRotate = false
		}
		annotationManager.deleteAnnotations(toDelete, { force: true })
		// Deletions don't pass through applyStyle — re-project so the split pane drops them too.
		this.scheduleSplitSync()
	}

	// Draw a single eref highlight without re-rendering the whole annotation layer. Mirrors Pass 1 of
	// renderAnnotations for one item — used right after the create endpoint returns (REQ-292) so the
	// new elevation appears and can be selected without a refetch. A brand-new eref has no furniture
	// on it yet, so its connectivity is always "unconnected".
	addErefAnnotation(eref: TEref): void {
		if (!this.instance) return
		if (this.findEntityAnnotation("eref", eref.id)) return

		const { Annotations, annotationManager, documentViewer } = this.instance.Core
		const doc = documentViewer.getDocument()
		const viewerPageNumber = eref.page_number + 1
		const transform = this.buildPageRotationMap(doc, [{ page_number: eref.page_number } as TPageElevationData]).get(
			viewerPageNumber,
		)

		const status: TAnnotationStatus = "unconnected"
		const category: TAnnotationCategory = `eref:${status}`
		const fillState = resolveEntityFillState(eref.status, status)
		const coords = this.applyRotation(eref.coordinates, transform)

		const annot = new Annotations.RectangleAnnotation({
			PageNumber: viewerPageNumber,
			X: coords.x0,
			Y: coords.y0,
			Width: coords.x1 - coords.x0,
			Height: coords.y1 - coords.y0,
		})
		this.applyStyle(annot, fillState, Annotations)
		this.applyEntityPermissions(annot)
		annot.setContents(this.buildErefLabel(eref.elevation_number))

		annot.setCustomData("entityId", String(eref.id))
		annot.setCustomData("entityType", "eref")
		annot.setCustomData("status", status)
		annot.setCustomData("entityStatus", eref.status ?? "")
		this.applyErefTextData(annot, eref)
		annot.setCustomData("pageNumber", String(eref.page_number))

		const metadata: TAnnotationMetadata = {
			entityId: eref.id,
			entityType: "eref",
			status,
			pageNumber: eref.page_number,
			...this.erefTextMetadata(eref),
			renderedRect: eref.coordinates,
		}
		this.trackAnnotation(annot, category, metadata)

		annotationManager.addAnnotations([annot])
		annotationManager.drawAnnotationsFromList([annot])
	}

	/** Draw a newly created ecall without rebuilding the annotation layer. */
	addEcallAnnotation(ecall: TEcall): void {
		if (!this.instance) return
		if (this.findEntityAnnotation("ecall", ecall.id)) return

		const { Annotations, annotationManager, documentViewer } = this.instance.Core
		const doc = documentViewer.getDocument()
		const viewerPageNumber = ecall.page_number + 1
		const transform = this.buildPageRotationMap(doc, [{ page_number: ecall.page_number } as TPageElevationData]).get(
			viewerPageNumber,
		)
		const linkedErefIds = this.extractLinkedErefIds(ecall)
		const status: TAnnotationStatus = this.isEcallConnected(linkedErefIds) ? "connected" : "unconnected"
		const category: TAnnotationCategory = `ecall:${status}`
		const coords = this.applyRotation(ecall.coordinates, transform)

		const annot = new Annotations.RectangleAnnotation({
			PageNumber: viewerPageNumber,
			X: coords.x0,
			Y: coords.y0,
			Width: coords.x1 - coords.x0,
			Height: coords.y1 - coords.y0,
		})
		this.applyStyle(annot, status, Annotations)
		this.applyEntityPermissions(annot)
		annot.setContents(this.buildEcallLabel(ecall))
		annot.setCustomData("entityId", String(ecall.id))
		annot.setCustomData("entityType", "ecall")
		annot.setCustomData("status", status)
		this.applyEcallTextData(annot, ecall)
		annot.setCustomData("pageNumber", String(ecall.page_number))
		annot.setCustomData("linkedErefIds", JSON.stringify(linkedErefIds))

		this.trackAnnotation(annot, category, {
			entityId: ecall.id,
			entityType: "ecall",
			status,
			pageNumber: ecall.page_number,
			...this.ecallTextMetadata(ecall),
			renderedRect: ecall.coordinates,
		})
		annotationManager.addAnnotations([annot])
		annotationManager.drawAnnotationsFromList([annot])
	}

	// Enter / leave the "draw the elevation area" gesture (REQ-292). Only the move lock of
	// `beginObjectPicking` applies here — nothing gets selected while drawing, so the picking
	// selection appearance would have nothing to restyle. Freezing the highlights is what stops a
	// drag that starts over an existing object from moving it (REQ-350) instead of drawing.
	beginAreaDrawing(): void {
		this.setEntityMoveLocked(true)
	}

	endAreaDrawing(): void {
		this.setEntityMoveLocked(false)
	}

	// Draw a single furniture highlight without re-rendering the whole annotation layer
	// (renderAnnotations clears+redraws everything). Mirrors Pass 3 of renderAnnotations for
	// one item — used after a paste/duplicate so the new object appears and can be selected.
	addFurnitureAnnotation(furniture: TFurniture): void {
		if (!this.instance) return
		// Don't double-add if it's already tracked.
		if (this.findEntityAnnotation("furniture", furniture.id)) return

		const { Annotations, annotationManager, documentViewer } = this.instance.Core
		const doc = documentViewer.getDocument()
		const viewerPageNumber = furniture.page_number + 1
		const transform = this.buildPageRotationMap(doc, [
			{ page_number: furniture.page_number } as TPageElevationData,
		]).get(viewerPageNumber)

		const status: TAnnotationStatus = "connected"
		const category: TAnnotationCategory = `furniture:${status}`
		const fillState = resolveEntityFillState(furniture.status, status)
		const coords = this.applyRotation(furniture.coordinates, transform)

		const annot = new Annotations.RectangleAnnotation({
			PageNumber: viewerPageNumber,
			X: coords.x0,
			Y: coords.y0,
			Width: coords.x1 - coords.x0,
			Height: coords.y1 - coords.y0,
		})
		this.applyStyle(annot, fillState, Annotations)
		this.applyEntityPermissions(annot)
		annot.setContents(this.buildFurnitureLabel(furniture))

		annot.setCustomData("entityId", String(furniture.id))
		annot.setCustomData("entityType", "furniture")
		annot.setCustomData("status", status)
		annot.setCustomData("entityStatus", furniture.status ?? "")
		annot.setCustomData("erefId", String(furniture.eref_id))
		annot.setCustomData("category", furniture.category?.name ?? "")
		annot.setCustomData("pageNumber", String(furniture.page_number))

		const metadata: TAnnotationMetadata = {
			entityId: furniture.id,
			entityType: "furniture",
			status,
			pageNumber: furniture.page_number,
			erefId: furniture.eref_id ?? undefined,
			renderedRect: furniture.coordinates,
		}
		this.trackAnnotation(annot, category, metadata)

		annotationManager.addAnnotations([annot])
		annotationManager.drawAnnotationsFromList([annot])
	}

	// --- Furniture info labels (REQ-401) -------------------------------------------------------

	/**
	 * Reconcile the on-drawing info labels with the current quotation data.
	 *
	 * Driven by the `["quotation", quotationId]` cache rather than by each individual edit
	 * call-site: every furniture mutation already writes that cache (`spliceFurnitureIntoCache` /
	 * `reorderFurnitureInCache` / invalidate), so model number, dimensions, category, name, price,
	 * status color, paste/duplicate and batch delete all keep the labels current with no extra
	 * wiring.
	 *
	 * Placement is recomputed for a WHOLE page at a time (one object's text or position changes its
	 * neighbours' collisions), then diffed against what is already on the canvas so only genuinely
	 * changed labels are touched.
	 */
	syncFurnitureLabels(pages: TPageElevationData[]): void {
		if (!this.instance) return
		const { annotationManager, documentViewer } = this.instance.Core
		const doc = documentViewer.getDocument()
		if (!doc) return

		const LabelAnnotation = this.ensureLabelClass()
		if (!LabelAnnotation) return

		const desired = new Map<number, TResolvedFurnitureLabel>()
		// Pages we couldn't measure this pass. Their labels are left exactly as they are rather
		// than swept away as "stale": a transient getPageInfo miss (page not measured yet after a
		// reload or a rotation change) must not wipe labels that are still perfectly valid.
		const unmeasuredPages = new Set<number>()
		// Built once for the whole sync (this runs on every cache write, and the per-page lookup
		// hits the document and logs for rotated pages).
		const pageTransforms = this.buildPageRotationMap(doc, pages)

		for (const page of pages) {
			const viewerPageNumber = page.page_number + 1
			const dims = this.getPageDimensions(page.page_number)
			if (!dims) {
				unmeasuredPages.add(viewerPageNumber)
				continue
			}
			const transform = pageTransforms.get(viewerPageNumber)

			const inputs: TFurnitureLabelInput[] = []
			const linesById = new Map<number, string[]>()
			const fontSizeById = new Map<number, number>()
			const furnitureById = new Map<number, TFurniture>()
			for (const furniture of page.furniture) {
				const lines = buildFurnitureLabelLines(furniture)
				// No text to show → no label at all (no placeholder on the drawing).
				if (lines.length === 0) continue
				// Shrink the text until it fits the object's own highlight. Measured once at the
				// max size; canvas text width is linear in font size, so the fitted width is just
				// scaled rather than re-measured.
				const widthAtMax = this.measureLabelTextWidth(lines, FURNITURE_LABEL_STYLE.maxFontSize)
				const boxWidth = Math.abs(furniture.coordinates.x1 - furniture.coordinates.x0)
				const fontSize = resolveLabelFontSize(boxWidth, widthAtMax)
				inputs.push({
					id: furniture.id,
					rect: furniture.coordinates,
					lineCount: lines.length,
					preferredSide: getFurnitureLabelSide(furniture),
					fontSize,
					textWidth: widthAtMax * (fontSize / FURNITURE_LABEL_STYLE.maxFontSize),
				})
				linesById.set(furniture.id, lines)
				fontSizeById.set(furniture.id, fontSize)
				furnitureById.set(furniture.id, furniture)
			}
			if (inputs.length === 0) continue

			// Every object on the page is something a label should try to sit clear of — including
			// the ones that carry no label themselves, so the text lands on empty drawing.
			const obstacles = page.furniture.map(({ id, coordinates }) => ({ id, rect: coordinates }))
			const placements = computeFurnitureLabelPlacements(inputs, dims.width, dims.height, obstacles)
			for (const [furnitureId, rect] of placements) {
				const furniture = furnitureById.get(furnitureId)
				const lines = linesById.get(furnitureId)
				if (!furniture || !lines) continue
				// Furniture highlights are always "connected"; review / in-quotation status is what
				// swaps the color, and the label tracks it through the same resolver as the box.
				const fillState = resolveEntityFillState(furniture.status, "connected")
				desired.set(furnitureId, {
					lines,
					fontSize: fontSizeById.get(furnitureId) ?? FURNITURE_LABEL_STYLE.maxFontSize,
					fillColor: ANNOTATION_STYLES[fillState].fillColor,
					rotation: transform?.rotation ?? 0,
					pageNumber: viewerPageNumber,
					rect: this.applyRotation(rect, transform),
					baseRect: this.applyRotation(furniture.coordinates, transform),
				})
			}
		}

		// 1. Drop labels whose furniture no longer has one (deleted, or its text went empty),
		// except on pages we couldn't measure — those contributed nothing to `desired`, so their
		// labels would otherwise all look stale.
		const stale: number[] = []
		for (const [furnitureId, annot] of this.labelAnnotations) {
			if (desired.has(furnitureId) || unmeasuredPages.has(annot.PageNumber)) continue
			stale.push(furnitureId)
		}
		this.deleteFurnitureLabels(stale)

		// 2. Add / update the rest, skipping anything that is already showing exactly this.
		const created: Core.Annotations.Annotation[] = []
		for (const [furnitureId, label] of desired) {
			const signature = JSON.stringify([
				label.lines,
				label.fontSize,
				label.fillColor,
				label.rect,
				label.pageNumber,
				label.rotation,
			])
			this.labelOffsets.set(furnitureId, {
				dx: label.rect.x0 - label.baseRect.x0,
				dy: label.rect.y0 - label.baseRect.y0,
			})

			const existing = this.labelAnnotations.get(furnitureId)
			if (existing) {
				if (this.labelSignatures.get(furnitureId) === signature) continue
				this.applyLabelToAnnotation(existing, label)
				this.labelSignatures.set(furnitureId, signature)
				annotationManager.redrawAnnotation(existing)
				continue
			}

			const annot = new LabelAnnotation({
				PageNumber: label.pageNumber,
				X: label.rect.x0,
				Y: label.rect.y0,
				Width: label.rect.x1 - label.rect.x0,
				Height: label.rect.y1 - label.rect.y0,
			})
			annot.setCustomData("entityId", String(furnitureId))
			annot.setCustomData("entityType", "furnitureLabel")
			this.applyLabelToAnnotation(annot, label)
			this.applyLabelPermissions(annot)
			this.labelAnnotations.set(furnitureId, annot)
			this.labelSignatures.set(furnitureId, signature)
			this.labelIds.add(annot.Id)
			created.push(annot)
		}

		if (created.length > 0) {
			annotationManager.addAnnotations(created)
			annotationManager.drawAnnotationsFromList(created)
			if (!this.labelsVisible) {
				for (const annot of created) annotationManager.hideAnnotation(annot)
			}
		}
	}

	// Width of a label's widest line at `fontSize`, in page units, measured with the exact font the
	// draw handler uses. Drives both the shrink-to-fit size and the collision footprint. Falls back
	// to a 0.5em-per-character estimate where no canvas is available (jsdom in tests).
	private measureLabelTextWidth(lines: string[], fontSize: number): number {
		const { fontFamily } = FURNITURE_LABEL_STYLE
		const ctx = this.getTextMeasureContext()
		if (!ctx) return Math.max(...lines.map((line) => line.length)) * fontSize * 0.5
		ctx.font = `${fontSize}px ${fontFamily}`
		return Math.max(...lines.map((line) => ctx.measureText(line).width))
	}

	// One reusable offscreen 2D context for text measurement (creating a canvas per label would be
	// wasteful — this runs for every furniture on every cache write). Null in jsdom.
	private getTextMeasureContext(): CanvasRenderingContext2D | null {
		// A separate "tried" flag, not an `undefined` context: jsdom's getContext("2d") returns
		// `undefined` WITHOUT throwing, so using undefined as the not-yet-attempted sentinel would
		// never memoize and we'd build a fresh canvas (and log a jsdom error) per label per sync.
		if (this.textMeasureAttempted) return this.textMeasureContext
		this.textMeasureAttempted = true
		try {
			this.textMeasureContext = document.createElement("canvas").getContext("2d") ?? null
		} catch {
			this.textMeasureContext = null
		}
		return this.textMeasureContext
	}

	// Geometry + payload of a label onto its annotation. Split out so create and update stay in
	// sync; the draw handler reads everything it needs back off the custom data.
	private applyLabelToAnnotation(annot: Core.Annotations.RectangleAnnotation, label: TResolvedFurnitureLabel): void {
		annot.PageNumber = label.pageNumber
		annot.X = label.rect.x0
		annot.Y = label.rect.y0
		annot.Width = label.rect.x1 - label.rect.x0
		annot.Height = label.rect.y1 - label.rect.y0
		// Same `Contents` metadata every entity highlight carries — and for a label it is also the
		// hover readout, since `LabelSelectionModel` lets a label hit-test wherever it is NOT over an
		// entity highlight (which is where a label normally sits). Over a highlight the label yields
		// and the box's own contents win instead; see `buildFurnitureLabel`, which is kept in sync
		// with this text so the two agree whichever one the cursor lands on.
		annot.setContents(label.lines.join(" · "))
		annot.setCustomData("labelLines", JSON.stringify(label.lines))
		annot.setCustomData("labelFontSize", String(label.fontSize))
		annot.setCustomData("labelFill", toCssColor(label.fillColor))
		annot.setCustomData("labelRotation", String(label.rotation))
		// Also mirrored onto the annotation itself so the patch still shows in the base color if a
		// future Apryse version ever falls back to the stock rectangle rendering.
		annot.StrokeThickness = 0
		if (this.instance) {
			const { Annotations } = this.instance.Core
			const [r, g, b, a] = label.fillColor
			annot.FillColor = new Annotations.Color(r, g, b, a)
		}
	}

	// Labels are display-only: no move, no resize, no delete, no rotate, and invisible to the
	// annotation list. Together with their absence from `metadataMap` and the permission-check
	// callback registered in `setInstance`, that makes them completely inert.
	private applyLabelPermissions(annot: Core.Annotations.RectangleAnnotation): void {
		annot.NoMove = true
		annot.NoResize = true
		annot.NoDelete = true
		annot.NoRotate = true
		annot.ReadOnly = true
		annot.Listable = false
		annot.disableRotationControl?.()
	}

	/**
	 * Is `(x, y)` — a point in page coordinates, on `labelAnnot`'s page — a hit on one of our entity
	 * highlights (furniture / eref / ecall / page)?
	 *
	 * Used by the label selection model to decide whether to yield: a label must be transparent over
	 * a highlight so clicks, drags and that highlight's own hover text all reach it, but should
	 * hit-test over bare drawing so its own hover text can show.
	 *
	 * The rect is grown by Apryse's own `canvasVisibilityPadding` (5 screen px, so `/ zoom` in page
	 * units) — the same slack its hit-testing applies. Matching it matters: a strict containment test
	 * disagrees exactly in that margin, and there the padded hit-test reports the highlight while we
	 * say "bare drawing", so the label wins the singular hit-test and the click selects nothing.
	 * (`SelectionAlgorithm.boundingRectTest` is NOT the padded one — verified live, it returned false
	 * 0.75 units outside a box that Apryse itself was reporting as hit.)
	 */
	isPointOverEntityAnnotation(labelAnnot: Core.Annotations.Annotation, x: number, y: number, zoom = 1): boolean {
		// Declared as an instance field in Apryse's types but static at runtime, hence the cast.
		const algorithm = this.instance?.Core.Annotations.SelectionAlgorithm as unknown as
			| { canvasVisibilityPadding?: number }
			| undefined
		const screenPadding = algorithm?.canvasVisibilityPadding ?? 5
		const padding = screenPadding / Math.max(zoom, 0.01)
		for (const [category, annots] of this.annotationsByCategory) {
			// A hidden highlight is not on screen, so there is nothing there to yield to — without
			// this, a label over a switched-off category would silently refuse its own hover while
			// the user sees only empty drawing.
			if (this.hiddenCategories.has(category)) continue
			for (const annot of annots) {
				if (annot.PageNumber !== labelAnnot.PageNumber) continue
				if (x < annot.X - padding || x > annot.X + annot.Width + padding) continue
				if (y < annot.Y - padding || y > annot.Y + annot.Height + padding) continue
				return true
			}
		}
		return false
	}

	// Create the label annotation subclass once per WebViewer instance. A dedicated subclass (not
	// RectangleAnnotation itself) keeps the custom draw handler off every other rectangle on the
	// page. Both hooks are installed: `setCustomDrawHandler` is the documented API, the `draw`
	// override is the guaranteed fallback if the handler is not applied to a runtime subclass.
	private ensureLabelClass(): TFurnitureLabelConstructor | null {
		if (!this.instance) return null
		if (this.labelClass && this.labelClassInstance === this.instance) return this.labelClass
		const { Annotations } = this.instance.Core
		// eslint-disable-next-line @typescript-eslint/no-this-alias
		const service = this

		class FurnitureLabelAnnotation extends Annotations.RectangleAnnotation {
			draw(ctx: CanvasRenderingContext2D): void {
				service.paintFurnitureLabel(ctx, this)
			}
		}

		// A label YIELDS to any entity highlight underneath it, and is hit-testable everywhere else.
		//
		// Fully inert was the original fix: clicking a highlight that sits under a label used to
		// select it (our plural hit-test finds it) and then the edit tool ran its own SINGULAR
		// hit-test, landed on the unselectable label, and cleared the selection again —
		// box→selected→deselected in one click. We can't just stopPropagation on that mousedown
		// either, because the edit tool needs it to start a drag (REQ-350).
		//
		// But inert everywhere also hides labels from `getAnnotationByMouseEvent`, and that is what
		// drives WebViewer's built-in AnnotationContentOverlay — so hovering a label showed nothing,
		// which is exactly the text a reviewer wants when the patch has shrunk to `minFontSize`.
		//
		// Yielding only over a highlight satisfies both: over a box the label is transparent (clicks
		// and drags reach the box, and its own hover text wins), and over bare drawing — where a
		// label normally sits — it hit-tests, so the overlay pops its text.
		type TSelectionModelCtor = new (
			...args: ConstructorParameters<typeof Core.Annotations.SelectionModel>
		) => Core.Annotations.SelectionModel
		const withSelectionModel = FurnitureLabelAnnotation.prototype as unknown as {
			selectionModel: TSelectionModelCtor
		}
		const BaseSelectionModel: TSelectionModelCtor | undefined =
			withSelectionModel.selectionModel ?? (Annotations.BoxSelectionModel as unknown as TSelectionModelCtor)
		if (typeof BaseSelectionModel === "function") {
			class LabelSelectionModel extends BaseSelectionModel {
				testSelection(
					annotation: Core.Annotations.Annotation,
					x: number,
					y: number,
					pageMatrix: unknown,
					zoom: number,
					rotation: Core.PageRotation,
				): boolean {
					if (service.isPointOverEntityAnnotation(annotation, x, y, zoom)) return false
					return (
						super.testSelection as unknown as (
							...args: [Core.Annotations.Annotation, number, number, unknown, number, Core.PageRotation]
						) => boolean
					).call(this, annotation, x, y, pageMatrix, zoom, rotation)
				}
			}
			withSelectionModel.selectionModel = LabelSelectionModel
		}

		try {
			// generateAppearance: false — the frontend never exports the PDF, and generating an
			// appearance per label would cost an offscreen canvas each.
			Annotations.setCustomDrawHandler(
				FurnitureLabelAnnotation as unknown as Core.Annotations.Annotation,
				(ctx, _pageMatrix, _rotation, options) => {
					service.paintFurnitureLabel(ctx, options.annotation as Core.Annotations.RectangleAnnotation)
				},
				{ generateAppearance: false },
			)
		} catch (error) {
			console.warn("[Annotations] Custom label draw handler unavailable, using draw() override:", error)
		}

		this.labelClass = FurnitureLabelAnnotation as unknown as TFurnitureLabelConstructor
		this.labelClassInstance = this.instance
		return this.labelClass
	}

	// Paint one label: a solid patch in the owning highlight's color with black, centered text.
	// The canvas is in PAGE space (the page matrix is already applied), so the font size is in page
	// units and the label scales with zoom exactly like the highlight. Text is deliberately neither
	// clipped nor wrapped — it may overflow the patch, matching the backend result file.
	private paintFurnitureLabel(ctx: CanvasRenderingContext2D, annot: Core.Annotations.RectangleAnnotation): void {
		let lines: string[] = []
		try {
			const raw = annot.getCustomData("labelLines")
			const parsed = raw ? (JSON.parse(raw) as unknown) : []
			if (Array.isArray(parsed)) lines = parsed.filter((line): line is string => typeof line === "string")
		} catch {
			return
		}
		if (lines.length === 0) return

		const { maxFontSize, lineHeightRatio, textColor, fontFamily } = FURNITURE_LABEL_STYLE
		// Per-label, shrunk to fit its own highlight (see resolveLabelFontSize).
		const fontSize = Number(annot.getCustomData("labelFontSize")) || maxFontSize
		const lineHeight = fontSize * lineHeightRatio
		const centerX = annot.X + annot.Width / 2
		const centerY = annot.Y + annot.Height / 2
		const rotation = Number(annot.getCustomData("labelRotation")) || 0

		ctx.save()
		// The patch is drawn in plain annotation space: `applyRotation` already put the rect where
		// the page matrix will land it, and an axis-aligned rect stays axis-aligned under a 90°
		// multiple. Only the TEXT needs the counter-rotation below.
		ctx.fillStyle = annot.getCustomData("labelFill") || "rgba(255, 234, 0, 0.3)"
		ctx.fillRect(annot.X, annot.Y, annot.Width, annot.Height)
		// On a /Rotate'd page the page matrix turns the whole canvas, which would leave the text
		// reading sideways. Counter-rotate around the patch centre so it stays upright on screen —
		// inside that frame the axes are screen-aligned again. A no-op on a rotation === 0 page.
		if (rotation !== 0) {
			ctx.translate(centerX, centerY)
			ctx.rotate((-rotation * Math.PI) / 180)
			ctx.translate(-centerX, -centerY)
		}
		ctx.fillStyle = textColor
		ctx.font = `${fontSize}px ${fontFamily}`
		ctx.textAlign = "center"
		ctx.textBaseline = "middle"
		// Lay the block out from the CENTRE, not from the top edge: on a rotated page annot.Y is not
		// the screen-top of the patch, but its centre maps correctly at any rotation. With
		// `height = lines × lineHeight + 2 × paddingY` this is the same result as top + padding.
		const firstBaseline = centerY - (lines.length * lineHeight) / 2 + lineHeight / 2
		lines.forEach((line, index) => {
			ctx.fillText(line, centerX, firstBaseline + index * lineHeight)
		})
		ctx.restore()
	}

	// Move a label with its base highlight using the cached offset. Used during a live drag (before
	// the debounced PATCH lands) and whenever a highlight is snapped to authoritative coordinates.
	private repositionFurnitureLabel(furnitureId: number, baseAnnot: Core.Annotations.Annotation): void {
		if (!this.instance) return
		const label = this.labelAnnotations.get(furnitureId)
		const offset = this.labelOffsets.get(furnitureId)
		if (!label || !offset) return
		label.X = baseAnnot.X + offset.dx
		label.Y = baseAnnot.Y + offset.dy
		// The stored signature still describes the old rect, so the next cache-driven sync will
		// re-place this label (and recompute its neighbours' collisions) rather than skip it.
		this.instance.Core.annotationManager.redrawAnnotation(label)
	}

	private deleteFurnitureLabels(furnitureIds: number[]): void {
		if (!this.instance || furnitureIds.length === 0) return
		const toDelete: Core.Annotations.Annotation[] = []
		for (const furnitureId of furnitureIds) {
			const annot = this.labelAnnotations.get(furnitureId)
			if (!annot) continue
			this.labelAnnotations.delete(furnitureId)
			this.labelSignatures.delete(furnitureId)
			this.labelOffsets.delete(furnitureId)
			this.labelIds.delete(annot.Id)
			toDelete.push(annot)
		}
		if (toDelete.length === 0) return
		// Same as removeEntityAnnotations: NoDelete has to be cleared on the annot itself —
		// `deleteAnnotations({ force: true })` alone does not remove a NoDelete annotation.
		for (const annot of toDelete) {
			annot.NoDelete = false
			annot.NoMove = false
			annot.NoRotate = false
			;(annot as Core.Annotations.RectangleAnnotation).ReadOnly = false
		}
		this.instance.Core.annotationManager.deleteAnnotations(toDelete, { force: true })
	}

	private clearFurnitureLabels(): void {
		this.deleteFurnitureLabels([...this.labelAnnotations.keys()])
		this.labelAnnotations.clear()
		this.labelSignatures.clear()
		this.labelOffsets.clear()
		this.labelIds.clear()
	}

	cleanup(): void {
		// Restore Apryse's selection statics before dropping the instance: a teardown mid-picking
		// (preview switch, modal close) would otherwise leave the blue outline applied to whatever
		// uses this instance next.
		this.endObjectPicking()
		// Drops the move lock smart navigation puts on an auto-selected counterpart (#1311).
		this.releaseCounterpartSelection()
		if (this.instance) {
			const { annotationManager } = this.instance.Core
			if (this.annotationSelectedHandler) {
				annotationManager.removeEventListener("annotationSelected", this.annotationSelectedHandler)
			}
			if (this.annotationChangedHandler) {
				annotationManager.removeEventListener("annotationChanged", this.annotationChangedHandler)
			}
		}
		this.annotationSelectedHandler = null
		this.annotationChangedHandler = null
		// Detach the split-screen pane before clearing — it removes its own listeners and mirrored
		// annotations. (clearAllAnnotations would otherwise leave the listeners attached.)
		this.unregisterSplitViewer()
		this.clearAllAnnotations()
		this.instance = null
		this.onAnnotationSelected = undefined
		this.onAnnotationCoordinatesChanged = undefined
	}

	// The text an eref/ecall annotation carries besides its label — ONE definition shared by the
	// render pass, the add* paths and the in-place peer-sync updaters, so they cannot drift apart.
	private applyErefTextData(annot: Core.Annotations.Annotation, eref: TEref): void {
		annot.setCustomData("elevationNumber", eref.elevation_number ?? "")
		annot.setCustomData("pageName", eref.page_name)
	}

	private erefTextMetadata(eref: TEref): Pick<TAnnotationMetadata, "elevationNumber" | "pageName"> {
		return { elevationNumber: eref.elevation_number ?? undefined, pageName: eref.page_name }
	}

	private applyEcallTextData(annot: Core.Annotations.Annotation, ecall: TEcall): void {
		annot.setCustomData("roomName", ecall.room_name ?? "")
		annot.setCustomData("pageName", ecall.page_name ?? "")
	}

	private ecallTextMetadata(ecall: TEcall): Pick<TAnnotationMetadata, "roomName" | "pageName"> {
		return { roomName: ecall.room_name ?? undefined, pageName: ecall.page_name ?? undefined }
	}

	private buildErefLabel(elevationNumber: string | null | undefined): string {
		return elevationNumber ? `Elevation ${elevationNumber}` : "Elevation"
	}

	private buildEcallLabel(ecall: TEcall): string {
		const linked = ecall.elevation_numbers.map((en) => en.elevation_number).filter(Boolean)
		if (linked.length > 0) return `→ ${linked.join(", ")}`
		return ecall.room_name ? `Callout · ${ecall.room_name}` : "Callout"
	}

	/**
	 * `Contents` for a furniture highlight — what WebViewer's built-in AnnotationContentOverlay pops
	 * up when the cursor rests on the box.
	 *
	 * This is the hover readout for the on-drawing info label (REQ-401), so it shows the SAME text
	 * the label paints. The label patch is shrunk to fit its object's highlight and bottoms out at
	 * `minFontSize`, where reading it means zooming in.
	 *
	 * The label carries the same string itself, but a label YIELDS wherever it overlaps a highlight
	 * (so clicks and drags reach the box beneath) — so over the box it is this text the overlay
	 * shows, and only over bare drawing does the label answer for itself. Both paths must read the
	 * same, hence the shared `buildFurnitureLabelLines`.
	 *
	 * Falls back to the internal `model_label · model_number` / category name for objects that have
	 * no label at all (nothing confirmed yet), so hovering never comes up empty.
	 */
	private buildFurnitureLabel(furniture: TFurniture): string {
		const labelLines = buildFurnitureLabelLines(furniture)
		if (labelLines.length > 0) return labelLines.join(" · ")
		const modelLabel = furniture.category?.model_label
		const modelNumber = furniture.selected_model_number?.model_number
		const parts = [modelLabel, modelNumber].filter(Boolean)
		if (parts.length > 0) return parts.join(" · ")
		return furniture.category?.name ?? "Furniture"
	}

	// Build a per-viewer-page-number map describing how to rotate backend coords so
	// they line up with the rendered page. Backend coords come in original (un-rotated)
	// PDF page space; WebViewer renders /Rotate'd pages so we need to pre-rotate the
	// rect coordinates by the same angle to make them align.
	private buildPageRotationMap(
		doc: ReturnType<Core.DocumentViewer["getDocument"]> | null,
		pages: TPageElevationData[],
	): Map<number, TPageRotationTransform> {
		const result = new Map<number, TPageRotationTransform>()
		if (!doc || typeof doc.getPageRotation !== "function" || typeof doc.getPageInfo !== "function") {
			return result
		}

		const seen = new Set<number>()
		for (const page of pages) {
			const viewerPageNumber = page.page_number + 1
			if (seen.has(viewerPageNumber)) continue
			seen.add(viewerPageNumber)

			let rotation = 0
			let info: { width: number; height: number } | undefined
			try {
				rotation = doc.getPageRotation(viewerPageNumber) ?? 0
				info = doc.getPageInfo(viewerPageNumber)
			} catch (error) {
				console.warn(`[Annotations] Could not read rotation for page ${viewerPageNumber}:`, error)
				continue
			}

			// getPageInfo returns the rendered (rotation-aware) page dims, and the
			// backend supplies coordinates in those same dims, so we use them directly
			// — no swap.
			const origWidth = info?.width ?? 0
			const origHeight = info?.height ?? 0

			if (rotation !== 0) {
				console.log(`[Annotations] Page ${viewerPageNumber} is rotated ${rotation}°`)
			}

			result.set(viewerPageNumber, { rotation, origWidth, origHeight })
		}
		return result
	}

	private applyRotation(
		coords: { x0: number; y0: number; x1: number; y1: number },
		transform: TPageRotationTransform | undefined,
	): { x0: number; y0: number; x1: number; y1: number } {
		if (!transform || transform.rotation === 0) return coords
		// Backend supplies coords in the un-rotated PDF page space. To align with the
		// rendered (rotated) viewer space we apply the CCW inverse of the page's
		// /Rotate. w/h are the un-rotated PDF page dims.
		const { rotation, origWidth: w, origHeight: h } = transform
		const { x0, y0, x1, y1 } = coords
		switch (rotation) {
			case 90:
				return { x0: y0, y0: w - x1, x1: y1, y1: w - x0 }
			case 180:
				return { x0: w - x1, y0: h - y1, x1: w - x0, y1: h - y0 }
			case 270:
				return { x0: h - y1, y0: x0, x1: h - y0, y1: x1 }
			default:
				return coords
		}
	}

	// Inverse of applyRotation: map a rect from the rendered (rotated) viewer/annotation space
	// back into backend coordinate space, so a moved/resized highlight can be persisted. Uses the
	// SAME dims (origWidth/origHeight) applyRotation used, so applyRotation(invertRotation(r)) === r.
	private invertRotation(coords: TCoordinates, transform: TPageRotationTransform | undefined): TCoordinates {
		if (!transform || transform.rotation === 0) return coords
		const { rotation, origWidth: w, origHeight: h } = transform
		const { x0, y0, x1, y1 } = coords
		switch (rotation) {
			case 90:
				return { x0: w - y1, y0: x0, x1: w - y0, y1: x1 }
			case 180:
				return { x0: w - x1, y0: h - y1, x1: w - x0, y1: h - y0 }
			case 270:
				return { x0: y0, y0: h - x1, x1: y1, y1: h - x0 }
			default:
				return coords
		}
	}

	// Rotation transform for a single 1-based viewer page (mirrors the per-page lookup
	// renderAnnotations builds for all pages at once).
	private getPageTransform(viewerPageNumber: number): TPageRotationTransform | undefined {
		if (!this.instance) return undefined
		const doc = this.instance.Core.documentViewer.getDocument()
		return this.buildPageRotationMap(doc, [{ page_number: viewerPageNumber - 1 } as TPageElevationData]).get(
			viewerPageNumber,
		)
	}

	// Recover the entity's backend coordinates from an annotation's current geometry (after a
	// drag/resize): read its rotated rect, invert the page /Rotate, then normalize so x0<=x1 /
	// y0<=y1, clamp to non-negative, and round to 2dp (backend stores decimals).
	private getBackendCoordsFromAnnotation(annot: Core.Annotations.Annotation): TCoordinates {
		const transform = this.getPageTransform(annot.PageNumber)
		const rotated: TCoordinates = {
			x0: annot.X,
			y0: annot.Y,
			x1: annot.X + annot.Width,
			y1: annot.Y + annot.Height,
		}
		return this.normalizeCoords(this.invertRotation(rotated, transform))
	}

	private normalizeCoords(c: TCoordinates): TCoordinates {
		const round = (n: number) => Math.round(n * 100) / 100
		return {
			x0: round(Math.max(0, Math.min(c.x0, c.x1))),
			y0: round(Math.max(0, Math.min(c.y0, c.y1))),
			x1: round(Math.max(0, Math.max(c.x0, c.x1))),
			y1: round(Math.max(0, Math.max(c.y0, c.y1))),
		}
	}

	// Two rects are "the same" within half a page unit — filters out float jitter and no-op
	// annotationChanged events (e.g. label/style updates) that don't actually move the box.
	private coordsEqual(a: TCoordinates, b: TCoordinates): boolean {
		const EPS = 0.5
		return (
			Math.abs(a.x0 - b.x0) <= EPS &&
			Math.abs(a.y0 - b.y0) <= EPS &&
			Math.abs(a.x1 - b.x1) <= EPS &&
			Math.abs(a.y1 - b.y1) <= EPS
		)
	}

	// Re-place an entity highlight at authoritative backend coords and refresh its tracked
	// renderedRect. Used to (a) lock a highlight onto the coords the backend returned after a
	// successful move and (b) revert it to the last-saved coords when a move is rejected.
	applyEntityCoordinates(
		entityType: "eref" | "ecall" | "furniture" | "page",
		entityId: number,
		coords: TCoordinates,
	): void {
		if (!this.instance) return
		const annot = this.findEntityAnnotation(entityType, entityId)
		if (!annot) return
		const { annotationManager } = this.instance.Core
		const rotated = this.applyRotation(coords, this.getPageTransform(annot.PageNumber))
		annot.X = rotated.x0
		annot.Y = rotated.y0
		annot.Width = rotated.x1 - rotated.x0
		annot.Height = rotated.y1 - rotated.y0
		const existing = this.metadataMap.get(annot.Id)
		if (existing) this.metadataMap.set(annot.Id, { ...existing, renderedRect: coords })
		annotationManager.redrawAnnotation(annot)

		// Snap the info label onto the authoritative position too (this path also runs when a
		// rejected move is reverted, so the label must not stay behind at the dragged-to spot).
		if (entityType === "furniture") this.repositionFurnitureLabel(entityId, annot)

		// Re-lock the split-screen pane's copy onto the same authoritative coords, and refresh its
		// metadata's renderedRect — otherwise the next drag in that pane would diff against a stale
		// rect and re-PATCH a no-op move (REQ-399).
		const mirrored = this.mirroredByEntityKey.get(this.entityKey(entityType, entityId))
		if (mirrored && this.splitManager) {
			mirrored.X = rotated.x0
			mirrored.Y = rotated.y0
			mirrored.Width = rotated.x1 - rotated.x0
			mirrored.Height = rotated.y1 - rotated.y0
			const mirroredMetadata = this.metadataMap.get(mirrored.Id)
			if (mirroredMetadata) this.metadataMap.set(mirrored.Id, { ...mirroredMetadata, renderedRect: coords })
			this.splitManager.redrawAnnotation(mirrored)
		}
	}

	private applyStyle(
		annot: Core.Annotations.RectangleAnnotation,
		fillState: TAnnotationFillState,
		Annotations: typeof Core.Annotations,
	): void {
		const style = ANNOTATION_STYLES[fillState]
		const [fr, fg, fb, fa] = style.fillColor

		annot.StrokeThickness = 0
		annot.FillColor = new Annotations.Color(fr, fg, fb, fa)

		// Split-screen (REQ-399): applyStyle is the choke point for EVERY fill/status change —
		// the initial render pass plus each connectivity / reviewed / in-quotation update — so
		// re-projecting the mirror here keeps the second pane in colour-sync without fanning the
		// call out across all the update* methods. Debounced to one rebuild per tick; no-op when
		// split screen isn't open.
		this.scheduleSplitSync()
	}

	// Permissions for entity highlights. Move + resize are ALLOWED so the user can correct
	// detection coordinates by dragging the box or its handles (REQ-349); the new geometry is
	// picked up by the annotationChanged listener and persisted. Rotation stays off (bounding
	// boxes are axis-aligned and the backend PATCH bodies have no angle field) and
	// delete-via-edit-tool stays off (deletion has its own confirm flow); clearAllAnnotations /
	// removeFurnitureAnnotations force-delete past NoDelete.
	private applyEntityPermissions(annot: Core.Annotations.RectangleAnnotation): void {
		annot.NoMove = false
		annot.NoResize = false
		annot.NoDelete = true
		annot.NoRotate = true
		// RectangleAnnotations show a freeform-rotation grip when selected; NoRotate does not
		// hide it. Remove it explicitly so users aren't offered a rotation they can't persist.
		annot.disableRotationControl()
	}

	// True for any annotation that must be invisible to selection/hit-testing: furniture info
	// labels (REQ-401) and persisted highlights (REQ-400). Both are permission-denied, but the
	// permission callback and the selected-handler need a single check across both id sets.
	private isInertAnnotation(id: string): boolean {
		return this.labelIds.has(id) || this.highlightIds.has(id)
	}

	// REQ-400 create flow: true when an annotation id is already tracked by this service — an
	// entity highlight (metadataMap) or an inert one (isInertAnnotation: labels/highlights). Used
	// to tell a just-drawn NATIVE draft annotation (never tracked, since Apryse creates it before
	// our own code ever sees it) apart from an addition WE ourselves triggered — the
	// annotationManager fires "annotationChanged"/"add" for EVERY addAnnotations call, including
	// addHighlight's own (which tracks the id in highlightIds before calling addAnnotations, so by
	// the time that event reaches a listener this already returns true for it).
	isOwnAnnotation(id: string): boolean {
		return this.metadataMap.has(id) || this.isInertAnnotation(id)
	}

	// REQ-400: fully inert everywhere, unlike the furniture-label selection model — a highlight is
	// already the bottom-most layer (see the Pass -1 comment in renderAnnotations), so there is
	// nothing underneath it to yield hit-testing back to.
	private applyHighlightPermissions(annot: Core.Annotations.Annotation): void {
		annot.NoMove = true
		annot.NoResize = true
		annot.NoDelete = true
		annot.NoRotate = true
		;(annot as Core.Annotations.RectangleAnnotation).ReadOnly = true
		annot.Listable = false
		annot.disableRotationControl?.()
	}

	// Rotate a single backend-space point the same way applyRotation rotates a rect — by treating
	// the point as a degenerate {x0,y0,x1,y1} rect and reading back its (now-rotated) corner. Keeps
	// the rotation math single-sourced instead of re-deriving it per-axis for freehand paths.
	private rotatePoint(x: number, y: number, transform: TPageRotationTransform | undefined): { x: number; y: number } {
		const rotated = this.applyRotation({ x0: x, y0: y, x1: x, y1: y }, transform)
		return { x: rotated.x0, y: rotated.y0 }
	}

	// REQ-400: construct the Apryse annotation for one persisted highlight. Purely additive
	// rendering — no metadata, no category, no permissions beyond applyHighlightPermissions (the
	// caller applies those once, after this returns, alongside pushing into `highlightAnnotations`).
	private buildHighlightAnnotation(
		highlight: THighlight,
		viewerPageNumber: number,
		transform: TPageRotationTransform | undefined,
		Annotations: typeof Core.Annotations,
		Point: typeof Core.Math.Point,
	): Core.Annotations.Annotation | null {
		const [strokeR, strokeG, strokeB] = hexToRgb(highlight.color)
		const strokeAlpha = highlight.opacity / 100

		if (highlight.shape_type === "freehand") {
			const annot = new Annotations.FreeHandAnnotation({ PageNumber: viewerPageNumber })
			highlight.path.forEach((stroke, pathIndex) => {
				const points = stroke.map(([x, y]) => {
					const rotated = this.rotatePoint(x, y, transform)
					return new Point(rotated.x, rotated.y)
				})
				annot.setPath(points, pathIndex)
			})
			annot.StrokeColor = new Annotations.Color(strokeR, strokeG, strokeB, strokeAlpha)
			annot.StrokeThickness = highlight.stroke_width
			return annot
		}

		// rectangle | ellipse (#1323 figure highlights) — translucent fill, per the requirement.
		const Ctor = highlight.shape_type === "rectangle" ? Annotations.RectangleAnnotation : Annotations.EllipseAnnotation
		const coords = this.applyRotation(highlight.coordinates, transform)
		const annot = new Ctor({
			PageNumber: viewerPageNumber,
			X: coords.x0,
			Y: coords.y0,
			Width: coords.x1 - coords.x0,
			Height: coords.y1 - coords.y0,
		})
		annot.StrokeColor = new Annotations.Color(strokeR, strokeG, strokeB, strokeAlpha)
		annot.StrokeThickness = highlight.stroke_width
		annot.FillColor = highlight.fill_color
			? new Annotations.Color(...hexToRgb(highlight.fill_color), strokeAlpha)
			: new Annotations.Color(0, 0, 0, 0)
		if (highlight.line_style === HIGHLIGHT_CLOUDY_LINE_STYLE) {
			this.applyCloudyBorder(annot)
			return annot
		}
		const dashes = HIGHLIGHT_LINE_STYLE_DASHES[highlight.line_style]
		if (dashes) {
			// Style/Dashes are real, verified-at-runtime properties Apryse's own draw code reads
			// (`this.Style === "dash" && this.Dashes`) — just missing from RectangleAnnotation /
			// EllipseAnnotation's .d.ts (a known gap, see AGENTS.md on the Apryse mock, issue #1325).
			const dashable = annot as unknown as { Style: string; Dashes: string }
			dashable.Style = "dash"
			dashable.Dashes = dashes
		}
		return annot
	}

	// See HIGHLIGHT_CLOUDY_LINE_STYLE for why every call here is feature-detected rather than typed
	// through. `setBorderStyle` is the one that actually makes the border a cloud; `setCloudIntensity`
	// only tunes the scallop depth, so a build missing just IT would still render a cloud.
	private applyCloudyBorder(annot: Core.Annotations.Annotation): void {
		const cloudy = annot as unknown as {
			setBorderStyle?: (value: string) => void
			setCloudIntensity?: (value: number) => void
		}
		try {
			if (typeof cloudy.setCloudIntensity === "function") cloudy.setCloudIntensity(HIGHLIGHT_CLOUD_INTENSITY)
			if (typeof cloudy.setBorderStyle === "function") cloudy.setBorderStyle(HIGHLIGHT_CLOUDY_LINE_STYLE)
		} catch (error) {
			console.warn("Could not apply the cloudy border style to a highlight:", error)
		}
	}

	/**
	 * Whether a cloudy border can actually be RENDERED for this shape on the loaded Apryse build —
	 * asked by the UI so the line-style dropdown never offers a style that would persist and then
	 * draw as a plain solid border. That is not hypothetical: for an ELLIPSE on this build
	 * `line_style: "cloudy"` saves fine and then draws a smooth curve, with no signal to the user.
	 *
	 * It probes a THROWAWAY annotation rather than trusting the typings, because the typings are
	 * wrong here: they declare `setBorderStyle` / `setCloudIntensity` on `EllipseAnnotation`, and
	 * this build's `EllipseAnnotation` has NEITHER (browser-measured), while `RectangleAnnotation`
	 * has both and renders a real cloud. The probe is never added to any annotation manager, so it
	 * has no side effects. Cached because the answer is a property of the SDK build, which cannot
	 * change within a page load.
	 *
	 * Probing the METHOD is the right proxy for "can it render", verified the hard way: making the
	 * method exist on an ellipse (by copying the rectangle's) does not make the cloud draw, so
	 * presence and renderability move together on this SDK. If a future build adds a real ellipse
	 * implementation, this starts returning true on its own and the UI offers the row with no change
	 * here — which is the point of probing rather than hardcoding "rectangle only".
	 */
	supportsCloudyBorder(shapeType: "rectangle" | "ellipse"): boolean {
		const cached = this.cloudySupportByShape.get(shapeType)
		if (cached !== undefined) return cached
		if (!this.instance) return false
		let supported = false
		try {
			const { Annotations } = this.instance.Core
			const Ctor = shapeType === "rectangle" ? Annotations.RectangleAnnotation : Annotations.EllipseAnnotation
			const probe = new Ctor({ PageNumber: 1, X: 0, Y: 0, Width: 1, Height: 1 })
			supported = typeof (probe as unknown as { setBorderStyle?: unknown }).setBorderStyle === "function"
		} catch (error) {
			console.warn("Could not probe cloudy-border support:", error)
			supported = false
		}
		this.cloudySupportByShape.set(shapeType, supported)
		return supported
	}

	// Force-delete every tracked highlight. Mirrors deleteFurnitureLabels: NoDelete + ReadOnly have
	// to be cleared on the annotation itself first — deleteAnnotations({ force: true }) alone does
	// not remove a ReadOnly annotation.
	private clearHighlights(): void {
		if (this.instance && this.highlightAnnotations.length > 0) {
			for (const annot of this.highlightAnnotations) {
				annot.NoDelete = false
				annot.NoMove = false
				annot.NoRotate = false
				;(annot as Core.Annotations.RectangleAnnotation).ReadOnly = false
			}
			this.instance.Core.annotationManager.deleteAnnotations(this.highlightAnnotations, { force: true })
		}
		if (this.splitManager && this.mirroredHighlightByPrimaryId.size > 0) {
			const mirrors = [...this.mirroredHighlightByPrimaryId.values()]
			for (const annot of mirrors) {
				annot.NoDelete = false
				annot.NoMove = false
				annot.NoRotate = false
				;(annot as Core.Annotations.RectangleAnnotation).ReadOnly = false
			}
			this.splitManager.deleteAnnotations(mirrors, { force: true })
		}
		this.mirroredHighlightByPrimaryId.clear()
		this.highlightAnnotations = []
		this.highlightIds.clear()
		this.highlightById.clear()
		this.selectedHighlightAnnotId = null
		this.selectedHighlightPane = null
	}

	// REQ-400: draw ONE just-created highlight without a full renderAnnotations re-run, integrating
	// it into the SAME inert pipeline #1378 built (buildHighlightAnnotation + applyHighlightPermissions
	// + tracking) so it behaves identically to one loaded from persistence — same z-order, same
	// "never hit-testable except through select-highlights mode" permissions.
	addHighlight(highlight: THighlight): void {
		if (!this.instance) return
		// Composite key: an id alone collides across the figure/freehand tables — see highlightKey.
		const key = highlightKey(highlight)
		if ([...this.highlightById.values()].some((h) => highlightKey(h) === key)) return

		const annot = this.buildHighlightMirrorAnnotation(highlight)
		if (!annot) return
		this.applyHighlightPermissions(annot)
		this.highlightAnnotations.push(annot)
		this.highlightIds.add(annot.Id)
		this.highlightById.set(annot.Id, highlight)

		const { annotationManager } = this.instance.Core
		annotationManager.addAnnotations([annot])
		annotationManager.drawAnnotationsFromList([annot])
		// `addAnnotations` draws the new annotation on top of everything already on the page — Apryse
		// has no "send to back" API, so instead bring every entity/label annotation already on this
		// page back above it, in the same bottom-to-top order renderAnnotations uses (page box < eref
		// < ecall < furniture < labels). That reproduces the "Pass -1" bottom-most z-order without a
		// full-page rebuild.
		this.raiseExistingAnnotationsAbove(annot.PageNumber, annotationManager, "primary")

		// REQ-400 split-screen fix: project the same highlight into the split pane too (no-op until
		// split screen is actually open) — see mirroredHighlightByPrimaryId's field comment.
		this.mirrorHighlightIntoSplitPane(annot.Id, highlight)
	}

	// Shared by addHighlight and mirrorHighlightIntoSplitPane: builds an independent Apryse
	// annotation instance from the domain highlight, deterministically from its own page rotation —
	// rather than cloning one pane's runtime annotation properties into the other, which would also
	// have to special-case FreeHandAnnotation's Path separately from Rectangle/EllipseAnnotation's
	// X/Y/Width/Height.
	private buildHighlightMirrorAnnotation(highlight: THighlight): Core.Annotations.Annotation | null {
		if (!this.instance) return null
		const { Annotations, documentViewer, Math: CoreMath } = this.instance.Core
		const doc = documentViewer.getDocument()
		const viewerPageNumber = highlight.page_number + 1
		const transform = this.buildPageRotationMap(doc, [
			{ page_number: highlight.page_number } as TPageElevationData,
		]).get(viewerPageNumber)
		return this.buildHighlightAnnotation(highlight, viewerPageNumber, transform, Annotations, CoreMath.Point)
	}

	// REQ-400 split-screen fix: project ONE highlight into the split pane's own manager, the same way
	// mirroredByEntityKey projects eref/ecall/furniture — a second, independent annotation object
	// with the same geometry/style, registered under its own Id. Idempotent per primary Id. A no-op
	// until split screen actually has a second, document-loaded viewer.
	private mirrorHighlightIntoSplitPane(primaryAnnotId: string, highlight: THighlight): void {
		const manager = this.splitManager
		if (!manager || !this.splitViewer?.getDocument()) return
		if (this.mirroredHighlightByPrimaryId.has(primaryAnnotId)) return

		const mirror = this.buildHighlightMirrorAnnotation(highlight)
		if (!mirror) return
		this.applyHighlightPermissions(mirror)
		// Tracked BEFORE addAnnotations — isOwnAnnotation must already recognize this Id once Apryse
		// fires "annotationChanged"/"add" for it, the same ordering addHighlight relies on for the
		// primary (see the isOwnAnnotation comment above).
		this.mirroredHighlightByPrimaryId.set(primaryAnnotId, mirror)
		this.highlightIds.add(mirror.Id)
		this.highlightById.set(mirror.Id, highlight)

		manager.addAnnotations([mirror])
		manager.drawAnnotationsFromList([mirror])
		this.raiseExistingAnnotationsAbove(mirror.PageNumber, manager, "split")
	}

	// Mirror every highlight already tracked in the primary manager into the split pane once it's
	// registered (registerSplitViewer) — a highlight created or loaded before split screen was ever
	// opened never went through mirrorHighlightIntoSplitPane above.
	private syncSplitHighlights(): void {
		if (!this.splitManager) return
		for (const annot of this.highlightAnnotations) {
			const highlight = this.highlightById.get(annot.Id)
			if (highlight) this.mirrorHighlightIntoSplitPane(annot.Id, highlight)
		}
	}

	// Tear down every split-pane highlight mirror (split screen closing) without touching the
	// primary layer — the counterpart of clearSplitAnnotations, for highlights.
	private clearSplitHighlights(): void {
		const manager = this.splitManager
		if (!manager || this.mirroredHighlightByPrimaryId.size === 0) return
		const mirrors = [...this.mirroredHighlightByPrimaryId.values()]
		for (const annot of mirrors) {
			annot.NoDelete = false
			annot.NoMove = false
			annot.NoRotate = false
			;(annot as Core.Annotations.RectangleAnnotation).ReadOnly = false
			this.highlightIds.delete(annot.Id)
			this.highlightById.delete(annot.Id)
		}
		manager.deleteAnnotations(mirrors, { force: true })
		this.mirroredHighlightByPrimaryId.clear()
		if (this.selectedHighlightPane === "split") {
			this.selectedHighlightAnnotId = null
			this.selectedHighlightPane = null
		}
	}

	// pane === "primary": bring every entity/label annotation on this page back above a highlight
	// that was just added underneath them (Apryse has no "send to back" API). pane === "split": the
	// split pane has no per-category sets of its own — every entity there is a mirror in
	// mirroredByEntityKey, and furniture labels (REQ-401) are primary-pane only, never mirrored.
	private raiseExistingAnnotationsAbove(
		viewerPageNumber: number,
		annotationManager: Core.AnnotationManager,
		pane: TViewerPane,
	): void {
		if (!this.instance) return
		if (pane === "split") {
			for (const annot of this.mirroredByEntityKey.values()) {
				if (annot.PageNumber === viewerPageNumber) annotationManager.bringToFront(annot)
			}
			return
		}
		const orderedCategories: TAnnotationCategory[] = [
			"page:connected",
			"eref:connected",
			"eref:unconnected",
			"ecall:connected",
			"ecall:unconnected",
			"furniture:connected",
			"furniture:unconnected",
		]
		for (const category of orderedCategories) {
			const set = this.annotationsByCategory.get(category)
			if (!set) continue
			for (const annot of set) {
				if (annot.PageNumber === viewerPageNumber) annotationManager.bringToFront(annot)
			}
		}
		for (const label of this.labelAnnotations.values()) {
			if (label.PageNumber === viewerPageNumber) annotationManager.bringToFront(label)
		}
	}

	// REQ-400 delete: force-remove one highlight (by its BACKEND id, not the Apryse annotation id) —
	// used once the DELETE request has succeeded. Clears the select-highlights state too if this was
	// the selected one, so the floating toolbar can't be left pointing at a deleted annotation.
	// Takes the whole record, not an id: the id alone does not identify a highlight across the two
	// backend tables (see highlightKey).
	removeHighlight(highlight: THighlight): void {
		if (!this.instance) return
		const key = highlightKey(highlight)
		const entry = [...this.highlightById.entries()].find(([, h]) => highlightKey(h) === key)
		if (!entry) return
		const [annotId] = entry
		const annot = this.highlightAnnotations.find((a) => a.Id === annotId)
		if (!annot) return

		if (this.selectedHighlightAnnotId === annotId) {
			this.selectedHighlightAnnotId = null
			this.selectedHighlightPane = null
		}

		annot.NoDelete = false
		annot.NoMove = false
		annot.NoRotate = false
		;(annot as Core.Annotations.RectangleAnnotation).ReadOnly = false
		this.instance.Core.annotationManager.deleteAnnotations([annot], { force: true })

		this.highlightAnnotations = this.highlightAnnotations.filter((a) => a.Id !== annotId)
		this.highlightIds.delete(annotId)
		this.highlightById.delete(annotId)

		// REQ-400 split-screen fix: remove the split pane's mirror too, if one exists.
		const mirror = this.mirroredHighlightByPrimaryId.get(annotId)
		if (mirror) {
			if (this.selectedHighlightAnnotId === mirror.Id) {
				this.selectedHighlightAnnotId = null
				this.selectedHighlightPane = null
			}
			mirror.NoDelete = false
			mirror.NoMove = false
			mirror.NoRotate = false
			;(mirror as Core.Annotations.RectangleAnnotation).ReadOnly = false
			this.splitManager?.deleteAnnotations([mirror], { force: true })
			this.highlightIds.delete(mirror.Id)
			this.highlightById.delete(mirror.Id)
			this.mirroredHighlightByPrimaryId.delete(annotId)
		}
	}

	// Public wrapper of getBackendCoordsFromAnnotation: recovers the backend page/coords for any
	// annotation — a native Rectangle/EllipseAnnotation the user just drew with Apryse's own create
	// tool (REQ-400), a native FreeText note just drawn or being dragged/resized (REQ-395/396) —
	// reusing the SAME rotation-inversion this file already uses to persist a drag/resize on an
	// entity highlight (getBackendCoordsFromAnnotation), rather than re-deriving the rotation math
	// per flow.
	getBackendRectFromAnnotation(annot: Core.Annotations.Annotation): { pageNumber: number; coordinates: TCoordinates } {
		return {
			pageNumber: annot.PageNumber - 1,
			coordinates: this.getBackendCoordsFromAnnotation(annot),
		}
	}

	// REQ-400 freehand create (#1324): the inverse of buildHighlightAnnotation's freehand branch.
	// That branch maps each backend [x, y] through rotatePoint into Apryse page space; this maps a
	// just-drawn FreeHandAnnotation's points back the other way, so what we persist round-trips to
	// exactly what was drawn.
	//
	// Returns null when the annotation carries no usable points at all (Apryse can hand back an
	// empty path array) — the caller drops the draft instead of POSTing a degenerate highlight.
	//
	// The bbox is the raw min/max of the returned points, NOT inflated by half the stroke width. The
	// endpoint documents x0/y0/x1/y1 as "the derived bbox of `path`" and requires them, so deriving
	// them the same way the backend would is what keeps the two from ever disagreeing about where
	// the highlight lives (issue #1324's stated concern).
	getBackendPathFromAnnotation(
		annot: Core.Annotations.Annotation,
	): { pageNumber: number; path: number[][][]; coordinates: TCoordinates } | null {
		const transform = this.getPageTransform(annot.PageNumber)
		const round = (n: number) => Math.round(n * 100) / 100

		// getPaths() is typed `any[]` by Apryse; it is an array of strokes, each an array of Points.
		const rawPaths = (annot as Core.Annotations.FreeHandAnnotation).getPaths?.() ?? []
		const path: number[][][] = []
		let minX = Infinity
		let minY = Infinity
		let maxX = -Infinity
		let maxY = -Infinity

		for (const stroke of rawPaths as { x: number; y: number }[][]) {
			if (!Array.isArray(stroke) || stroke.length === 0) continue
			const points: number[][] = []
			for (const point of stroke) {
				if (!point || !Number.isFinite(point.x) || !Number.isFinite(point.y)) continue
				// Same degenerate-rect trick rotatePoint uses on the way out, so the rotation math
				// stays single-sourced in invertRotation rather than being re-derived per axis here.
				const inverted = this.invertRotation({ x0: point.x, y0: point.y, x1: point.x, y1: point.y }, transform)
				// Clamp to non-negative to match normalizeCoords' own treatment of backend space.
				const x = round(Math.max(0, inverted.x0))
				const y = round(Math.max(0, inverted.y0))
				points.push([x, y])
				if (x < minX) minX = x
				if (y < minY) minY = y
				if (x > maxX) maxX = x
				if (y > maxY) maxY = y
			}
			// A stroke that lost every point to the guards above contributes nothing — dropping it
			// keeps `path` free of empty arrays, which the endpoint's minItems:2 point shape rejects.
			if (points.length > 0) path.push(points)
		}

		if (path.length === 0) return null

		return {
			pageNumber: annot.PageNumber - 1,
			path,
			coordinates: { x0: round(minX), y0: round(minY), x1: round(maxX), y1: round(maxY) },
		}
	}

	// REQ-400 click-to-select: hit-test a mouse event against tracked highlights ONLY (never entity
	// annotations), without selecting anything — mirrors getEntityAtMouseEvent. Used for the pointer
	// cursor on hover and the delete flow's click-to-pick.
	getHighlightAtMouseEvent(event: MouseEvent, pane: TViewerPane = this.getActivePane()): THighlight | undefined {
		if (!this.instance) return undefined
		const annotationManager = this.paneManager(pane)
		if (!annotationManager) return undefined
		const hits =
			typeof annotationManager.getAnnotationsByMouseEvent === "function"
				? annotationManager.getAnnotationsByMouseEvent(event)
				: [annotationManager.getAnnotationByMouseEvent(event)]
		const hit = hits.find((candidate) => candidate && this.highlightIds.has(candidate.Id))
		return hit ? this.highlightById.get(hit.Id) : undefined
	}

	// Select a highlight under a mouse event for the delete flow (REQ-400): temporarily lifts
	// exactly THIS highlight's inert permissions (NoMove/NoResize/NoDelete/ReadOnly/Listable — see
	// applyHighlightPermissions) so Apryse will actually select it, mirroring how
	// beginObjectPicking/endObjectPicking bracket the connect-objects mode. Any previously-selected
	// highlight is deselected (and its permissions restored) first — only one at a time. Returns the
	// domain highlight (for the floating toolbar to position/act on), or undefined when the click
	// missed every highlight (which also clears any existing selection, matching "empty space
	// deselects").
	selectHighlightFromMouseEvent(event: MouseEvent, pane: TViewerPane = this.getActivePane()): THighlight | undefined {
		if (!this.instance) return undefined
		const annotationManager = this.paneManager(pane)
		if (!annotationManager) return undefined
		const hits =
			typeof annotationManager.getAnnotationsByMouseEvent === "function"
				? annotationManager.getAnnotationsByMouseEvent(event)
				: [annotationManager.getAnnotationByMouseEvent(event)]
		const hitAnnot = hits.find((candidate) => candidate && this.highlightIds.has(candidate.Id))
		if (!hitAnnot) {
			this.deselectHighlight()
			return undefined
		}
		const highlight = this.highlightById.get(hitAnnot.Id)
		if (!highlight) return undefined
		return this.applySelectedHighlight(hitAnnot, highlight, pane)
	}

	// REQ-400 decision 2: select a highlight PROGRAMMATICALLY right after it's created (not via a
	// mouse hit-test), so the trash toolbar appears immediately without the user re-clicking it —
	// same lift-permissions-then-select as selectHighlightFromMouseEvent, just resolved by the
	// backend id addHighlight was just handed instead of a mouse event.
	// Takes the whole record for the same reason removeHighlight does — the id alone is ambiguous
	// across the figure/freehand tables (see highlightKey).
	selectHighlightById(target: THighlight, pane: TViewerPane = this.getActivePane()): THighlight | undefined {
		if (!this.instance) return undefined
		const key = highlightKey(target)
		const entry = [...this.highlightById.entries()].find(([, h]) => highlightKey(h) === key)
		if (!entry) return undefined
		const [primaryAnnotId, highlight] = entry
		// pane-correct object: a mouse selection must act on THIS pane's own annotation instance —
		// selecting the primary annotation through the split manager (or vice versa) is a no-op/error
		// since Apryse annotations belong to exactly one manager.
		const mirror = this.mirroredHighlightByPrimaryId.get(primaryAnnotId)
		const annot = pane === "split" && mirror ? mirror : this.highlightAnnotations.find((a) => a.Id === primaryAnnotId)
		if (!annot) return undefined
		return this.applySelectedHighlight(annot, highlight, pane)
	}

	private applySelectedHighlight(
		annot: Core.Annotations.Annotation,
		highlight: THighlight,
		pane: TViewerPane,
	): THighlight | undefined {
		if (!this.instance) return undefined
		const annotationManager = this.paneManager(pane)
		if (!annotationManager) return undefined
		if (this.selectedHighlightAnnotId === annot.Id) return highlight

		this.deselectHighlight()
		annot.NoMove = false
		annot.NoResize = false
		annot.NoDelete = false
		;(annot as Core.Annotations.RectangleAnnotation).ReadOnly = false
		annot.Listable = true
		// selectAnnotation alone is ADDITIVE (mirrors the entity wrapper's own selectAnnotation()
		// above and syncSelectionToSplitPane()) — without clearing first, picking a highlight would
		// leave whatever furniture/eref/ecall was already selected live in Apryse's selection too,
		// so e.g. Delete/Backspace could act on the wrong thing (#1323 review).
		annotationManager.deselectAllAnnotations()
		annotationManager.selectAnnotation(annot)
		this.selectedHighlightAnnotId = annot.Id
		this.selectedHighlightPane = pane
		return highlight
	}

	// Deselects the currently-picked highlight (if any), restoring its inert permissions exactly as
	// applyHighlightPermissions set them on load — a highlight with nothing selected must be
	// byte-identical to #1378's original behavior. No-op when nothing is selected.
	deselectHighlight(): void {
		if (!this.instance || !this.selectedHighlightAnnotId) return
		const annotationManager = this.paneManager(this.selectedHighlightPane ?? "primary")
		const annot =
			this.highlightAnnotations.find((a) => a.Id === this.selectedHighlightAnnotId) ??
			[...this.mirroredHighlightByPrimaryId.values()].find((a) => a.Id === this.selectedHighlightAnnotId)
		if (annot) {
			this.applyHighlightPermissions(annot)
			annotationManager?.deselectAnnotation(annot)
		}
		this.selectedHighlightAnnotId = null
		this.selectedHighlightPane = null
	}

	// --- Notes (REQ-395/396) ----------------------------------------------------------------------

	// True for a note this service is already tracking (loaded, or one this service itself just
	// added) — used by the create-tool's `annotationChanged` listener to tell a fresh, user-drawn
	// draft apart from a note this service itself just added (`addNote` registers the id BEFORE
	// calling `addAnnotations`, so by the time that event reaches a listener this already returns
	// true for it). Mirrors the reasoning `isInertAnnotation` documents for labels/highlights.
	isOwnNoteAnnotation(id: string): boolean {
		return this.noteIdByAnnotId.has(id)
	}

	getNoteIdForAnnotation(id: string): number | undefined {
		return this.noteIdByAnnotId.get(id)
	}

	getNoteRecord(noteId: number): TNote | undefined {
		return this.noteRecords.get(noteId)
	}

	getNoteAnnotation(noteId: number): Core.Annotations.Annotation | undefined {
		return this.noteAnnotations.get(noteId)
	}

	// `font_size`/`text_color` -> the two Apryse FreeText style properties. `FontSize` is a CSS-like
	// size string ("12pt"), not a number — verified against `node_modules/@pdftron/webviewer`'s own
	// type defs for `Core.Annotations.FreeTextAnnotation`.
	//
	// It also neutralises the BORDER, which is not a note field at all (PR #1386 review: "add a
	// note — no frame; refresh — it has a frame"). Measured live on both paths, same document, same
	// note: a note Apryse's own FREETEXT create tool places comes out with `StrokeThickness: 0` (the
	// tool's own `defaults` carry that zero), while `new Annotations.FreeTextAnnotation()` —
	// `buildNoteAnnotation`'s path, i.e. every note rebuilt from the quotation GET — inherits the
	// CLASS default of `1` and draws a 1px black rectangle. Everything else about the two matched
	// exactly (`StrokeColor` #000000 and `FillColor` alpha 0 on both), so the thickness IS the frame:
	// setting it to 0 on the rebuilt annotation made the frame disappear, verified in-browser before
	// this line was written. Borderless is the intended look, so the zero is applied here rather than
	// a border being added to the fresh path — and here specifically because this is the one method
	// every note path (build / register-created / update) already funnels through.
	private applyNoteStyle(annot: Core.Annotations.Annotation, note: Pick<TNote, "font_size" | "text_color">): void {
		if (!this.instance) return
		const freeText = annot as unknown as {
			FontSize: string
			TextColor: Core.Annotations.Color
			StrokeThickness: number
		}
		freeText.StrokeThickness = 0
		freeText.FontSize = `${note.font_size}pt`
		const [r, g, b] = hexToRgb(note.text_color)
		freeText.TextColor = new this.instance.Core.Annotations.Color(r, g, b, 1)
		this.applyNoteStyleToOpenEditor(annot, note)
	}

	// Push a font-size / text-color change into the note's OPEN inline rich-text editor (PR #1386
	// review: "the note is still typed using a small font size and is re-rendered in its expected
	// size only after the user clicks Enter").
	//
	// Setting `FontSize` on the annotation is not enough while Apryse's inline editor is up: the
	// editor is Quill-backed and keeps its own per-run formatting, so it goes on rendering — and
	// accepting new keystrokes at — whatever size it opened with until the edit commits. Measured
	// live: with the editor open on a 12pt note, `getFormat()` reported
	// `{ size: "4.63998pt", originalSize: "12" }` (the `size` is the pt value scaled by the current
	// zoom) and the rendered span stayed at 6.19px no matter what the annotation said.
	//
	// The route in is an UNDOCUMENTED format name, `"applyCustomFontSize"`, read off the shipped
	// bundle's own `RichTextEditor.prototype.format` and then verified live: given a string|number it
	// resolves the zoom-scaled value itself and applies Quill's `size` + `originalSize` formats
	// (`format("size", "72pt")` alone would fight the zoom scaling). The declared `Format` union in
	// the SDK's typings lists only 'color' | 'bold' | 'italic' | 'underline' | 'strike', so this is
	// deliberately cast — and, per this project's twice-burnt rule about these typings (`tool.on()`,
	// `getEditBoxManager().on()`), EVERY method here is feature-detected and the whole thing is
	// wrapped: a note whose editor is closed, or an SDK build without this format, must lose the live
	// preview and nothing else. The annotation itself is already styled by the caller either way.
	private applyNoteStyleToOpenEditor(
		annot: Core.Annotations.Annotation,
		note: Pick<TNote, "font_size" | "text_color">,
	): void {
		const withEditor = annot as unknown as { getEditor?: () => TNoteRichTextEditor | undefined }
		if (typeof withEditor.getEditor !== "function") return
		try {
			const editor = withEditor.getEditor()
			if (!editor || typeof editor.format !== "function") return
			// `hasFocus()` is the only reliable "the editor is actually open" signal on this object —
			// `getEditor()` hands back a live editor for a note that is merely selected, and formatting
			// a closed one would silently pre-stain the next edit session.
			if (typeof editor.hasFocus === "function" && !editor.hasFocus()) return
			const selection = typeof editor.getSelection === "function" ? editor.getSelection() : null
			// Resize the text that is ALREADY there, not just the next keystroke: Quill's `format`
			// applies to the current selection and only falls back to "make it the active format" when
			// that selection is empty. `selectAll` exists at runtime but not in the typings, so its
			// absence just means the existing runs keep their size while new typing gets the new one.
			if (typeof editor.selectAll === "function") editor.selectAll()
			editor.format("applyCustomFontSize", note.font_size)
			editor.format("color", note.text_color)
			if (selection && typeof editor.setSelection === "function") {
				editor.setSelection(selection.index, selection.length)
			}
		} catch (error) {
			console.warn("Could not apply the note style to its open inline editor:", error)
		}
	}

	// Move + resize stay ON (the whole point of a REAL interactive annotation is the native
	// drag/resize handles); rotation and native delete stay OFF. Rotation: bounding boxes are
	// axis-aligned and the backend PATCH body has no angle field, same reasoning as
	// `applyEntityPermissions`. Delete: goes through the floating trash-icon toolbar's own
	// version-locked DELETE, never Apryse's own Delete-key/backspace path — mirroring how every
	// other deletable entity here has its own confirm flow instead of relying on the edit tool.
	private applyNotePermissions(annot: Core.Annotations.Annotation): void {
		annot.NoRotate = true
		annot.NoDelete = true
		annot.disableRotationControl?.()
	}

	// Explicit property assignment rather than relying on the constructor initializer object
	// `buildHighlightAnnotation` uses for Rectangle/Ellipse: FreeTextAnnotation is new API surface
	// for this codebase (no prior "real, interactive Apryse annotation" existed here), so this
	// avoids depending on unverified initializer support for a class it wasn't tested against.
	private buildNoteAnnotation(
		note: TNote,
		viewerPageNumber: number,
		transform: TPageRotationTransform | undefined,
		Annotations: typeof Core.Annotations,
	): Core.Annotations.Annotation {
		const coords = this.applyRotation(note.coordinates, transform)
		const annot = new Annotations.FreeTextAnnotation()
		annot.PageNumber = viewerPageNumber
		annot.X = coords.x0
		annot.Y = coords.y0
		annot.Width = coords.x1 - coords.x0
		annot.Height = coords.y1 - coords.y0
		// On a rotated page `applyRotation` only places the BOX, so the text needs its own matching
		// rotation or it renders sideways. Apryse's native create tool sets this itself, which is why
		// only reloaded notes were affected. Degrees = quarter-turns * 90.
		const completeRotation = this.instance?.Core.documentViewer.getCompleteRotation?.(viewerPageNumber)
		annot.Rotation = completeRotation != null ? completeRotation * 90 : (transform?.rotation ?? 0)
		annot.setContents(note.text)
		this.applyNoteStyle(annot, note)
		return annot
	}

	// Draw every persisted note on load (REQ-395) — called by the render effect (`useAnnotations`)
	// alongside `renderAnnotations`, kept as its OWN pass rather than folded into that method: a
	// note is a real interactive annotation with a completely different permission model than every
	// entity/highlight `renderAnnotations` draws, and keeping the two clear+redraw passes
	// independent is what makes them order-independent — the same reasoning `renderAnnotations`'s
	// own comment gives for keeping furniture-label sync separate.
	renderNotes(pages: TPageElevationData[]): void {
		if (!this.instance) return
		this.clearNotes()

		const { Annotations, annotationManager, documentViewer } = this.instance.Core
		const doc = documentViewer.getDocument()
		const pageTransforms = this.buildPageRotationMap(doc, pages)
		const allAnnotations: Core.Annotations.Annotation[] = []

		for (const page of pages) {
			const viewerPageNumber = page.page_number + 1
			const transform = pageTransforms.get(viewerPageNumber)
			for (const note of page.notes ?? []) {
				const annot = this.buildNoteAnnotation(note, viewerPageNumber, transform, Annotations)
				this.applyNotePermissions(annot)
				this.noteAnnotations.set(note.id, annot)
				this.noteIdByAnnotId.set(annot.Id, note.id)
				this.noteRecords.set(note.id, note)
				allAnnotations.push(annot)
			}
		}
		if (allAnnotations.length > 0) {
			annotationManager.addAnnotations(allAnnotations)
			annotationManager.drawAnnotationsFromList(allAnnotations)
		}
	}

	// Draw ONE just-created note without a full re-render (REQ-395), mirroring the shape
	// `addHighlight` (#1381) uses for the sibling REQ-400 feature — integrates the note into the
	// SAME tracking `renderNotes` builds so it behaves identically to one loaded from persistence.
	addNote(note: TNote): void {
		if (!this.instance) return
		if (this.noteAnnotations.has(note.id)) return
		const { Annotations, annotationManager, documentViewer } = this.instance.Core
		const doc = documentViewer.getDocument()
		const viewerPageNumber = note.page_number + 1
		const transform = this.buildPageRotationMap(doc, [{ page_number: note.page_number } as TPageElevationData]).get(
			viewerPageNumber,
		)
		const annot = this.buildNoteAnnotation(note, viewerPageNumber, transform, Annotations)
		this.applyNotePermissions(annot)
		this.noteAnnotations.set(note.id, annot)
		this.noteIdByAnnotId.set(annot.Id, note.id)
		this.noteRecords.set(note.id, note)
		annotationManager.addAnnotations([annot])
		annotationManager.drawAnnotationsFromList([annot])
	}

	// REQ-395 create: track a note the user just drew THROUGH APRYSE'S OWN NATIVE CREATE TOOL — the
	// annotation already exists on the canvas (Apryse added it the moment the box was placed) and is
	// likely already in its own built-in rich-text-EDIT state, so unlike `addHighlight`'s
	// delete-and-rebuild dance this KEEPS the exact same annotation object rather than swapping it
	// for a freshly-built one. Recreating it here would kill the user's in-progress typing session
	// the instant the box is drawn. Only applies the tracked-note permissions/style on top and
	// registers it in the same maps `addNote`/`renderNotes` populate.
	registerCreatedNoteAnnotation(annot: Core.Annotations.Annotation, note: TNote): void {
		if (!this.instance) return
		this.applyNotePermissions(annot)
		this.applyNoteStyle(annot, note)
		this.noteAnnotations.set(note.id, annot)
		this.noteIdByAnnotId.set(annot.Id, note.id)
		this.noteRecords.set(note.id, note)
		this.instance.Core.annotationManager.redrawAnnotation(annot)
	}

	// Re-places an already-tracked note's annotation at an AUTHORITATIVE backend record (REQ-396
	// edit commit — text, font size, text color and/or geometry, whichever the PATCH/creation
	// response says). Mirrors `applyEntityCoordinates`'s "snap to authoritative state" role, but
	// across the note's whole field set since a PATCH can change any subset of them. Also used to
	// REVERT an optimistic geometry change when a commit fails (same authoritative-state role).
	applyNoteUpdate(note: TNote): void {
		if (!this.instance) return
		const annot = this.noteAnnotations.get(note.id)
		if (!annot) return
		const transform = this.getPageTransform(note.page_number + 1)
		const coords = this.applyRotation(note.coordinates, transform)
		annot.PageNumber = note.page_number + 1
		annot.X = coords.x0
		annot.Y = coords.y0
		annot.Width = coords.x1 - coords.x0
		annot.Height = coords.y1 - coords.y0
		annot.setContents(note.text)
		this.applyNoteStyle(annot, note)
		this.noteRecords.set(note.id, note)
		this.instance.Core.annotationManager.redrawAnnotation(annot)
	}

	// Applies a font-size/text-color pick from the tool-options bar directly to the LIVE annotation
	// (immediate visual feedback), without touching the backend record — the caller (`useNoteEdit`)
	// picks this change up through the SAME generic diff-and-commit path a text/geometry edit uses,
	// by handing the returned annotation to its debounced commit. Returns the annotation so the
	// caller doesn't have to look it up again, or `undefined` if the note isn't tracked.
	applyLiveNoteStyle(
		noteId: number,
		style: { font_size: number; text_color: string },
	): Core.Annotations.Annotation | undefined {
		if (!this.instance) return undefined
		const annot = this.noteAnnotations.get(noteId)
		if (!annot) return undefined
		this.applyNoteStyle(annot, style)
		this.instance.Core.annotationManager.redrawAnnotation(annot)
		return annot
	}

	// Force-remove one note (REQ-396 delete) — used once the DELETE request has succeeded, without a
	// full re-render/refetch. Mirrors `removeHighlight` (#1381).
	removeNote(noteId: number): void {
		if (!this.instance) return
		const annot = this.noteAnnotations.get(noteId)
		if (!annot) return
		annot.NoDelete = false
		annot.NoRotate = false
		this.instance.Core.annotationManager.deleteAnnotations([annot], { force: true })
		this.noteAnnotations.delete(noteId)
		this.noteIdByAnnotId.delete(annot.Id)
		this.noteRecords.delete(noteId)
	}

	// Force-delete a native, not-yet-persisted draft (a note the user started drawing/typing but
	// abandoned with empty text, or one whose create request failed) — no backend id to track, so
	// this takes the raw annotation rather than a note id. Mirrors the `{ force: true }` unlock
	// dance every other force-delete here uses (NoDelete is normally what stops the edit tool's own
	// Delete key from removing a note directly).
	discardDraftNoteAnnotation(annot: Core.Annotations.Annotation): void {
		if (!this.instance) return
		annot.NoDelete = false
		annot.NoRotate = false
		this.instance.Core.annotationManager.deleteAnnotations([annot], { force: true })
	}

	// Force-delete every tracked note. Mirrors `clearHighlights`.
	private clearNotes(): void {
		if (this.instance && this.noteAnnotations.size > 0) {
			const annots = [...this.noteAnnotations.values()]
			for (const annot of annots) {
				annot.NoDelete = false
				annot.NoRotate = false
			}
			this.instance.Core.annotationManager.deleteAnnotations(annots, { force: true })
		}
		this.noteAnnotations.clear()
		this.noteIdByAnnotId.clear()
		this.noteRecords.clear()
	}

	private trackAnnotation(
		annot: Core.Annotations.Annotation,
		category: TAnnotationCategory,
		metadata: TAnnotationMetadata,
	): void {
		if (!this.annotationsByCategory.has(category)) {
			this.annotationsByCategory.set(category, new Set())
		}
		this.annotationsByCategory.get(category)!.add(annot)
		this.metadataMap.set(annot.Id, metadata)
	}

	private setupClickListener(): void {
		if (!this.instance) return

		const { annotationManager } = this.instance.Core
		// Remove any previously-registered handler first so repeated setInstance calls on the
		// same shared instance don't stack duplicate listeners (#1119).
		if (this.annotationSelectedHandler) {
			annotationManager.removeEventListener("annotationSelected", this.annotationSelectedHandler)
		}
		const handler = this.buildAnnotationSelectedHandler()
		this.annotationSelectedHandler = handler
		annotationManager.addEventListener("annotationSelected", handler)
	}

	// Attached to the PRIMARY manager only — it is the single selection source (a click in the split
	// pane is routed here too, see selectSplitAnnotationFromMouseEvent), which is what keeps the one
	// shared sidebar / batch toolbar correct (decision D3 — shared selection).
	private buildAnnotationSelectedHandler() {
		return (annotations: Core.Annotations.Annotation[], action: string) => {
			// No early return on a deselect: the split pane's outlines are synced below either way.
			if (action === "selected" && annotations.length > 0) {
				// Last line of defence for info labels (REQ-401) and persisted highlights (REQ-400):
				// both deny every permission and are absent from metadataMap, but Apryse's edit tool
				// can still put a no-permission selection outline on one. Drop it immediately so a
				// click on either looks like nothing.
				const inert = annotations.filter((annot) => this.isInertAnnotation(annot.Id))
				if (inert.length > 0) this.instance?.Core.annotationManager.deselectAnnotations(inert)
				// Take the first non-inert one: a marquee selection can put an inert annotation at
				// index 0, and reading annotations[0] blindly would find no metadata and silently skip
				// opening the sidebar for the real entity highlight that was also selected.
				const entity = annotations.find((annot) => !this.isInertAnnotation(annot.Id))
				const metadata = entity ? this.metadataMap.get(entity.Id) : undefined
				if (metadata) {
					this.onAnnotationSelected?.(metadata)
				}
			}
			// Keep the split pane's outlines in step with the primary selection (no-op when closed).
			this.syncSelectionToSplitPane()
		}
	}

	// Listen for user move/resize commits (REQ-349). Apryse fires "annotationChanged" with
	// action "modify" once the edit tool finishes dragging/resizing an annotation. For each of
	// OUR entity highlights whose geometry actually changed, recover the backend coords and hand
	// them to the orchestrator to persist. Skips `imported` (programmatic import) changes and
	// no-op moves (label/style updates fire "modify" too but don't move the box).
	private setupChangeListener(): void {
		if (!this.instance) return

		const { annotationManager } = this.instance.Core
		if (this.annotationChangedHandler) {
			annotationManager.removeEventListener("annotationChanged", this.annotationChangedHandler)
		}
		const handler = this.buildAnnotationChangedHandler()
		this.annotationChangedHandler = handler
		annotationManager.addEventListener("annotationChanged", handler)
	}

	// Shared by the primary manager and the split-screen mirror manager, so a drag/resize in the
	// SECOND pane commits through exactly the same path (cache version → shared mutation queue →
	// applyEntityCoordinates re-locks BOTH panes onto the authoritative coords).
	private buildAnnotationChangedHandler() {
		return (annotations: Core.Annotations.Annotation[], action: string, info?: { imported?: boolean }) => {
			if (action !== "modify" || info?.imported) return
			for (const annot of annotations) {
				const metadata = this.metadataMap.get(annot.Id)
				if (!metadata) continue
				// Snap the info label onto its box the moment a drag/resize ends, rather than
				// leaving it behind until the debounced PATCH and the cache write land ~150 ms
				// later. Note this is release-time, not per-frame: Apryse (v11.9) fires "modify"
				// once when the edit tool finishes, not on every mouse-move — verified live — so
				// the label holds still during the drag itself and catches up on mouse-up.
				if (metadata.entityType === "furniture") this.repositionFurnitureLabel(metadata.entityId, annot)
				if (!this.onAnnotationCoordinatesChanged) continue
				const coords = this.getBackendCoordsFromAnnotation(annot)
				if (metadata.renderedRect && this.coordsEqual(coords, metadata.renderedRect)) continue
				this.onAnnotationCoordinatesChanged(metadata, coords)
			}
		}
	}

	// --- Split-screen mirror layer (REQ-399) -----------------------------------------------------

	private entityKey(entityType: string, entityId: number): string {
		return `${entityType}-${entityId}`
	}

	// Register the SECOND viewer's annotation manager (Apryse MultiViewerMode). Mirrors the whole
	// detection layer into it and attaches the shared selection/change listeners so the pane is a
	// real editing surface, not a screenshot. Idempotent — re-registering the same manager just
	// re-projects.
	registerSplitViewer(viewer: Core.DocumentViewer): void {
		const manager = viewer.getAnnotationManager()
		if (this.splitManager && this.splitManager !== manager) this.unregisterSplitViewer()
		if (this.splitManager !== manager) {
			this.splitViewer = viewer
			this.splitManager = manager
			manager.promoteUserToAdmin()
			// Only "annotationChanged" is attached here — a drag/resize in the second pane must
			// commit. Selection deliberately is NOT listened to: it flows through the primary
			// manager (see selectSplitAnnotationFromMouseEvent), so the sidebar, the batch toolbar
			// and the context menu keep reading ONE selection source.
			this.splitChangedHandler = this.buildAnnotationChangedHandler()
			manager.addEventListener("annotationChanged", this.splitChangedHandler)
		}
		// Highlights first — they're the bottom-most layer (Pass -1), so mirroring them before the
		// entity layer below reproduces that z-order without extra bringToFront calls; entities added
		// after naturally land on top.
		this.syncSplitHighlights()
		this.syncSplitAnnotations()
	}

	unregisterSplitViewer(): void {
		const manager = this.splitManager
		if (!manager) return
		this.clearSplitAnnotations()
		this.clearSplitHighlights()
		if (this.splitChangedHandler) manager.removeEventListener("annotationChanged", this.splitChangedHandler)
		this.splitChangedHandler = null
		this.splitManager = null
		this.splitViewer = null
		this.activePane = "primary"
	}

	// Which pane the user last interacted with — set by the viewer's mousedown/contextmenu handlers.
	// Falls back to "primary" when split screen isn't open.
	setActivePane(pane: TViewerPane): void {
		this.activePane = pane === "split" && this.splitManager ? "split" : "primary"
	}

	getActivePane(): TViewerPane {
		return this.splitManager ? this.activePane : "primary"
	}

	private paneViewer(pane: TViewerPane): Core.DocumentViewer | null {
		if (pane === "split") return this.splitViewer
		return this.instance?.Core.documentViewer ?? null
	}

	private paneManager(pane: TViewerPane): Core.AnnotationManager | null {
		if (pane === "split") return this.splitManager
		return this.instance?.Core.annotationManager ?? null
	}

	// DOM subtree to search for a pane's `pageContainer{n}` elements. The per-viewer viewer element
	// is what makes the lookup unambiguous in split screen — the ids repeat in both panes, each
	// nested under its own `#DocumentContainer{key}`. Falls back to the whole shadow root (the
	// pre-split behavior) when the viewer element isn't exposed.
	private getPaneSearchRoot(documentViewer: Core.DocumentViewer): ParentNode | null {
		const scrollEl = documentViewer.getScrollViewElement?.() as HTMLElement | null
		const viewerEl = (documentViewer as unknown as { getViewerElement?: () => HTMLElement | null }).getViewerElement?.()
		const root = scrollEl?.getRootNode?.() as ShadowRoot | Document | undefined
		return viewerEl ?? root ?? null
	}

	// Hit-test + select in the SECOND pane. Same manual click-select bypass the primary pane needs
	// (#1119), but the hit is resolved to its entity and then selected on the PRIMARY manager — the
	// one selection source every consumer reads (sidebar, batch toolbar, context menu, hotkeys).
	// The primary's annotationSelected handler mirrors the selection back so the second pane shows
	// its own selection outline too.
	selectSplitAnnotationFromMouseEvent(event: MouseEvent, options?: { additive?: boolean }): boolean {
		const manager = this.splitManager
		if (!manager) return false
		// Resolved exactly as the primary pane resolves its own click (see
		// selectAnnotationFromMouseEvent): ALL hits under the cursor, filtered to the ones we own,
		// then `pickHitAnnotation` to prefer whichever is already selected. The singular
		// `getAnnotationByMouseEvent` this used to call returns only the TOPMOST annotation, which
		// cost the split pane both of the fixes the primary pane has:
		//
		//   - #1312: grabbing a selected highlight's resize handle where it overlaps a neighbour
		//     picked the NEIGHBOUR, because it is on top — so the drag moved the wrong object and the
		//     sidebar switched under the user. `pickHitAnnotation` is what prevents that, and it was
		//     never reached from here. The split manager's selection is kept mirrored by
		//     syncSelectionToSplitPane, so passing `manager` lets it match on the mirror.
		// (Info labels are NOT mirrored into the split manager — measured: 35 in the primary layer,
		// 0 here — so the label-swallowing half of the primary pane's reason for the plural hit-test
		// cannot arise in this pane. The plural call is kept anyway so both panes resolve a click
		// through one code path, and so it stays correct if labels are ever mirrored.)
		const hits =
			typeof manager.getAnnotationsByMouseEvent === "function"
				? manager.getAnnotationsByMouseEvent(event)
				: [manager.getAnnotationByMouseEvent(event)]
		const ownedHits = hits.filter((candidate): candidate is Core.Annotations.Annotation =>
			Boolean(candidate && this.metadataMap.has(candidate.Id)),
		)
		const hit = this.pickHitAnnotation(ownedHits)
		const metadata = hit ? this.metadataMap.get(hit.Id) : undefined
		if (!hit || !metadata) return false

		const primaryAnnot = this.findEntityAnnotation(metadata.entityType, metadata.entityId)
		if (!primaryAnnot) return false
		if (options?.additive) {
			if (metadata.entityType !== "furniture") return false
			this.toggleFurnitureInSelection(primaryAnnot)
			return true
		}
		this.selectAnnotation(primaryAnnot)
		return true
	}

	// Project the primary manager's selection onto the mirrored annotations, so a highlight selected
	// in either pane shows its selection outline in BOTH. Safe against loops: the split manager has
	// no annotationSelected listener.
	private syncSelectionToSplitPane(): void {
		const manager = this.splitManager
		if (!manager || !this.instance) return

		const wantedIds = new Set<string>()
		for (const annot of this.instance.Core.annotationManager.getSelectedAnnotations()) {
			const metadata = this.metadataMap.get(annot.Id)
			if (!metadata) continue
			const mirrored = this.mirroredByEntityKey.get(this.entityKey(metadata.entityType, metadata.entityId))
			if (mirrored) wantedIds.add(mirrored.Id)
		}

		const currentIds = new Set(manager.getSelectedAnnotations().map((annot) => annot.Id))
		if (currentIds.size === wantedIds.size && [...wantedIds].every((id) => currentIds.has(id))) return

		manager.deselectAllAnnotations()
		const toSelect = [...this.mirroredByEntityKey.values()].filter((annot) => wantedIds.has(annot.Id))
		for (const annot of toSelect) manager.selectAnnotation(annot)
	}

	// Microtask-debounced re-projection (incremental — see syncSplitAnnotations). Called from every
	// method that mutates the primary layer, so the second pane can never drift from the
	// cache-backed source of truth.
	private scheduleSplitSync(): void {
		if (!this.splitManager || this.splitSyncScheduled) return
		this.splitSyncScheduled = true
		queueMicrotask(() => {
			this.splitSyncScheduled = false
			this.syncSplitAnnotations()
		})
	}

	private clearSplitAnnotations(): void {
		const manager = this.splitManager
		if (!manager) return
		const mirrored = [...this.mirroredByEntityKey.values()]
		for (const annot of mirrored) this.metadataMap.delete(annot.Id)
		this.mirroredByEntityKey.clear()
		if (mirrored.length > 0) manager.deleteAnnotations(mirrored, { force: true })
	}

	// Re-project the second pane's highlights from the primary (cache-projected) ones: geometry,
	// style, contents and custom data, with each copy's metadata registered under its own Id so the
	// shared handlers treat it as the same entity.
	//
	// INCREMENTAL by entity key. An entity that already has a mirror is updated IN PLACE; only
	// genuinely new entities are constructed and only vanished ones are deleted. The first version
	// tore the whole layer down and rebuilt it on every pass, which — because `applyStyle` is the
	// choke point for every status/fill change — meant one status update re-created every mirror on
	// the page, dropped the second pane's selection outline, and forced a full redraw. REQ-399.
	private syncSplitAnnotations(): void {
		const manager = this.splitManager
		if (!manager || !this.instance) return
		// The second pane's own document must be loaded before annotations can be drawn into it.
		if (!this.splitViewer?.getDocument()) return

		const { Annotations } = this.instance.Core
		const created: Core.Annotations.RectangleAnnotation[] = []
		const updated: Core.Annotations.RectangleAnnotation[] = []
		// Visibility is applied after the draw pass, but read here — categories can be toggled
		// between passes and the mirror must follow its source.
		const visibility: Array<{ annot: Core.Annotations.RectangleAnnotation; hidden: boolean }> = []
		const liveKeys = new Set<string>()

		for (const annotations of this.annotationsByCategory.values()) {
			for (const source of annotations) {
				const metadata = this.metadataMap.get(source.Id)
				if (!metadata) continue
				const src = source as Core.Annotations.RectangleAnnotation
				const key = this.entityKey(metadata.entityType, metadata.entityId)
				liveKeys.add(key)

				const existing = this.mirroredByEntityKey.get(key)
				const copy =
					existing ??
					new Annotations.RectangleAnnotation({
						PageNumber: src.PageNumber,
						X: src.X,
						Y: src.Y,
						Width: src.Width,
						Height: src.Height,
					})
				if (!existing) {
					copy.StrokeThickness = 0
					this.applyEntityPermissions(copy)
					this.mirroredByEntityKey.set(key, copy)
				}

				copy.PageNumber = src.PageNumber
				copy.X = src.X
				copy.Y = src.Y
				copy.Width = src.Width
				copy.Height = src.Height
				// Reuse the source Color instance's channels — XFDF export/import would drop the
				// fill ALPHA (interior-color is opaque hex), so we copy the object directly.
				const fill = src.FillColor
				copy.FillColor = new Annotations.Color(fill.R, fill.G, fill.B, fill.A)
				const contents = src.getContents()
				if (contents) copy.setContents(contents)
				for (const dataKey of MIRRORED_CUSTOM_DATA_KEYS) {
					const value = src.getCustomData(dataKey)
					if (value) copy.setCustomData(dataKey, value)
				}

				// Metadata objects are REPLACED (not mutated) on every coordinate commit, so the
				// mirror's entry is re-pointed at the current one on each pass.
				this.metadataMap.set(copy.Id, metadata)
				;(existing ? updated : created).push(copy)
				visibility.push({ annot: copy, hidden: Boolean(src.Hidden) })
			}
		}

		// Entities that are no longer in the primary layer (deleted, or filtered out of a re-render).
		for (const [key, mirrored] of [...this.mirroredByEntityKey]) {
			if (liveKeys.has(key)) continue
			this.mirroredByEntityKey.delete(key)
			this.metadataMap.delete(mirrored.Id)
			manager.deleteAnnotations([mirrored], { force: true })
		}

		if (created.length > 0) manager.addAnnotations(created)
		const drawn = [...created, ...updated]
		if (drawn.length > 0) manager.drawAnnotationsFromList(drawn)
		for (const { annot, hidden } of visibility) {
			if (hidden) manager.hideAnnotation(annot)
			else manager.showAnnotation(annot)
		}

		// An updated layer must still show what the primary manager has selected — without this the
		// second pane silently loses its selection outline after any status/style change.
		this.syncSelectionToSplitPane()
	}
}

export default new AnnotationService()
